// ============================================================================
// MÓDULO 5: CONTRACT ENGINE (VERSÃO 34.2 - FULL REFACTOR FIX)
// ARQUIVO: src/modules/contracts/ContractEngine.jsx
// OBJETIVO: Editor Completo com Caminhos de Importação Corrigidos (V2.0).
// ============================================================================
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import { browserEnv } from "../../utils/browserEnv";
import { escapeHtml } from "../../utils/escapeHtml";
import { createPortal } from "react-dom";
import { LAW_FIRM_INFO, FIREBASE_CONFIG } from "../../config/Config";
import { POSICOES_POR_RAMO as POSICOES_POR_RAMO_SHARED } from "../../config/PosicoesProcessuais";
import { serverTimestamp } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import { db, auth, storage } from "../../shared/api/firebase/config";
import {
  fetchContractMasterV8,
  saveContractMasterV8,
  fetchContractAutosave,
  subscribeContractMasterV8,
  saveContractAutosave,
  subscribeClientData,
  fetchClientData,
  addFinancialLedgerEntries,
  publishContractDocument,
  addContractProntuarioNote,
  getNextContractDocumentRef,
  getFreshContractMasterV8,
  MASTER_PATH,
} from "../../services/ContractEngineRepository";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

// IMPORTS CORRIGIDOS (PATH RELATIVO DA NOVA ESTRUTURA)
import ContractEditor from "../../components/ContractEditor";
import { SaveCancelBar } from "../../components/ui/SaveCancelBar";
// REMOVIDO: ReferencePicker duplicado (mantido apenas RulesPanel)
import { CrmClientPickerModal } from "../../components/CrmClientPickerModal";
import { PreFlightCheckModal } from "../../components/common/PreFlightCheckModal";
import { CadernoSelector } from "../../components/ui/CadernoSelector";
import * as ContractData from "./ContractData";
import {
  CLIENT_TYPES,
  generateQualificationText,
  hydrateClientData,
  buildCanonicalClientQualification,
  hydrateAndBuildCanonicalClientQualification,
  buildRepresentativeFromClient,
  buildSignatureLine,
} from "../../config/ClientSchema";
import { WhatsAppService } from "../../services/WhatsAppService";
import { useOfficeData } from "../settings/useOfficeData";
import { useCloudSync } from "../../hooks/useCloudSync";
import { useContractMode } from "../../hooks/useContractMode";
import { useContractSaving } from "../../hooks/useContractSaving";
import { useContractEditing } from "../../hooks/useContractEditing";
import { useContractModals } from "../../hooks/useContractModals";
import { useContractForm } from "../../hooks/useContractForm";
import { useContractPartner } from "../../hooks/useContractPartner";
import { useContractImport } from "../../hooks/useContractImport";
import { useContractOffice } from "../../hooks/useContractOffice";
import { useContractAdvanced } from "../../hooks/useContractAdvanced";
import {
  getContractMaster,
  setContractMaster,
  subscribeContractMaster,
  getContractAutosave,
  setContractAutosave,
} from "../../services/TemplateRepository";
import {
  DocumentHeader,
  DocumentFooter,
} from "../../components/layout/DocumentLetterhead";

import { buildQuickNavMeta } from "../../utils/quickNavMeta";
import { toTitleCasePt } from "../../utils/formatters";
import { buildContextoProcessual } from "../../utils/contextoProcessualHelper";
import {
  generateDocumentFileName,
  generatePublishedArtifactFileStem,
} from "../../utils/documentUtils";
import { readLatestPublishedDocumentInSeries } from "../../utils/documentConsent";
import {
  clearQueuedPublishedDocumentEdit,
  publishedDocumentEditEventName,
  readQueuedPublishedDocumentEdit,
} from "../../utils/publishedDocumentEditor";

import BillingGenerator from "../finance/components/BillingGenerator";
import { generateInstallments } from "../../utils/FinanceEngine";

// Ícones
import {
  Save,
  Edit3,
  Printer,
  Trash2,
  Plus,
  FileText,
  User,
  Briefcase,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Search,
  XCircle,
  Filter,
  Link as LinkIcon,
  DollarSign,
  X,
  GripVertical,
  Map as MapIcon,
  EyeOff,
  Lock,
  Tag,
  Gavel,
  Users,
  Download,
  Coins,
  Send,
} from "lucide-react";

// CSS CORRIGIDO (VOLTA 2 NÍVEIS)
import "../../styles/legal-print.css";
import { fetchWithWarmup } from "../../utils/backendWarmup";

// DADOS MESTRES DO ESCRITÓRIO (Fonte da Verdade para o Contratado)
// Extraído da validação visual do usuário em 16/02/2026
const PUBLISHING_API_BASE_URL = String(
  import.meta?.env?.VITE_BACKEND_URL || "https://rda-backend-thuq.onrender.com",
)
  .trim()
  .replace(/\/+$/, "");

const OFFICE_LAWYER_DATA = {
  name: "Rodrigo Demeterco",
  nationality: "brasileiro",
  naturalness: "Campo Grande-MS",
  civilStatus: "divorciado",
  profession: "advogado",
  // Dados de Registro
  hasProfessionalRegister: true,
  registerType: "OAB",
  registerUf: "PR",
  professionalRegister: "113.938",
  // Documentos Pessoais
  rg: "6.478.961-9",
  orgaoEmissor: "SSP/PR",
  cpf: "029.824.709-77",
  // Endereço
  address: {
    street: "Rua Bom Jesus de Iguape",
    number: "2035",
    neighborhood: "Hauer",
    city: "Curitiba",
    state: "PR",
    zipCode: "81.610-040",
  },
  // Contato
  contact: {
    phone: "(41) 99848-1711",
    email: "rodrigodemeterco@hotmail.com",
  },
  // Meta
  gender: "M",
  pronoun: "ELE",
};

const appId = FIREBASE_CONFIG.projectId;
const DRAFT_KEY = "rda_contract_draft_v23_enc";
const CLOUD_AUTOSAVE_DOC_ID = "autosave_v1";
const CLOUD_AUTOSAVE_INTERVAL_MS = 10000;
const MOJIBAKE_PATTERN = /(?:Ã.|Â.|â€|—|–|�)/;
const readText = (value) => String(value || "").trim();

const TAXONOMY = ContractData.TAXONOMY_TREE || {};

// =============================================================================
// V17.0 (VISUAL): FICHA TÉCNICA DO DONO DO ESCRITÓRIO (VOCÊ)
// =============================================================================
const DEFAULT_OWNER_DATA = {
  name: "RODRIGO DEMETERCO",
  nationality: "Brasileiro(a)",
  civilStatus: "Casado(a)",
  oab: "113.938",
  oabState: "PR",
  cpf: "029.824.709-77",
  rg: "6.478.961-9",
  rgIssuer: "SESP/PR",
  address: "Rua Bom Jesus de Iguape, 2035 - Hauer, Curitiba - PR",
  fullAddress:
    "Rua Bom Jesus de Iguape, 2035 - Hauer, Curitiba - PR, CEP 81.610-040",
  email: "contato@rodrigodemeterco.adv.br",
};

// =============================================================================
// V17.0 (VISUAL): CONFIGURAÇÕES VISUAIS (PADRÃO OURO)
// =============================================================================
const STYLES = {
  headerTitle: {
    color: "#20243B",
    fontWeight: "800",
    fontSize: "2rem",
    textAlign: "center",
    marginBottom: "1.5rem",
    borderBottom: "2px solid #20243B",
    paddingBottom: "0.75rem",
    textTransform: "uppercase",
    fontFamily: '"Times New Roman", serif',
    lineHeight: "1.2",
  },
  headerMeta: {
    fontSize: "1rem",
    color: "#20243B",
    textAlign: "center",
    marginTop: "-1rem",
    marginBottom: "2rem",
    fontWeight: "600",
    textTransform: "uppercase",
    backgroundColor: "#fff",
    display: "inline-block",
    padding: "0 1rem",
    position: "relative",
    zIndex: 10,
    fontFamily: '"Times New Roman", serif',
  },
  preambleBox: {
    textAlign: "justify",
    marginBottom: "1rem",
    lineHeight: "1.6",
    fontSize: "11pt",
    fontFamily: "inherit",
    color: "#000",
    textIndent: "0",
    marginTop: "0",
  },
  clauseTitle: {
    fontWeight: "bold",
    textTransform: "uppercase",
    marginTop: "1.5rem",
    marginBottom: "0.5rem",
    color: "#000",
    fontSize: "12pt",
    fontFamily: '"Times New Roman", serif',
  },
  signatureLine: {
    borderTop: "1px solid #000",
    width: "70%",
    margin: "0 auto",
    paddingTop: "0.5rem",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontFamily: '"Times New Roman", serif',
  },
};

const LISTS = {
  civilStatus: [
    "Solteiro(a)",
    "Casado(a)",
    "Divorciado(a)",
    "Viúvo(a)",
    "União Estável",
  ],
  nationality: ["Brasileiro(a)", "Português(a)", "Estrangeiro(a)"],
  ufs: [
    "PR",
    "SP",
    "SC",
    "RS",
    "RJ",
    "MG",
    "DF",
    "GO",
    "MT",
    "MS",
    "ES",
    "BA",
    "PE",
    "CE",
    "AM",
  ],
};

// --- COMPONENTE: MODAL DE IMPORTAÇÃO DE PARCEIRO (ADVOGADO) ---
const PartnerImportModal = ({ onClose, onSelect }) => {
  return (
    <CrmClientPickerModal
      isOpen={true}
      onClose={onClose}
      onSelect={onSelect}
      db={db}
      appId={appId}
      title="Base de Clientes (CRM) — Parceiros"
      requiredRole="PARTNER"
    />
  );
};
const HONORARIOS_OPTIONS = ContractData.HONORARIOS_LIST || [];

// --- HOOK: ESCAPE KEY ---
const useEscapeKey = (callback) => {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") callback();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [callback]);
};

// --- UTILITÁRIOS ---
const CryptoShield = {
  async encrypt(data) {
    try {
      const text = JSON.stringify(data);
      const enc = new TextEncoder();
      const key = await window.crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"],
      );
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encrypted = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv: iv },
        key,
        enc.encode(text),
      );
      const exportedKey = await window.crypto.subtle.exportKey("jwk", key);
      return JSON.stringify({
        k: exportedKey,
        iv: Array.from(iv),
        d: Array.from(new Uint8Array(encrypted)),
      });
    } catch (e) {
      return null;
    }
  },
  async decrypt(cipherStr) {
    try {
      const pack = JSON.parse(cipherStr);
      const key = await window.crypto.subtle.importKey(
        "jwk",
        pack.k,
        { name: "AES-GCM" },
        true,
        ["encrypt", "decrypt"],
      );
      const decrypted = await window.crypto.subtle.decrypt(
        { name: "AES-GCM", iv: new Uint8Array(pack.iv) },
        key,
        new Uint8Array(pack.d),
      );
      return JSON.parse(new TextDecoder().decode(decrypted));
    } catch (e) {
      return null;
    }
  },
};

const repairMojibakeText = (value) => {
  let text = String(value ?? "");
  if (!text || !MOJIBAKE_PATTERN.test(text)) return text;

  const replacements = [
    ["À", "À"],
    ["Á", "Á"],
    ["Â", "Â"],
    ["Ã", "Ã"],
    ["Ä", "Ä"],
    ["Ç", "Ç"],
    ["È", "È"],
    ["É", "É"],
    ["Ê", "Ê"],
    ["Ë", "Ë"],
    ["Ì", "Ì"],
    ["Í", "Í"],
    ["Î", "Î"],
    ["Ï", "Ï"],
    ["Ñ", "Ñ"],
    ["Ò", "Ò"],
    ["Ó", "Ó"],
    ["Ô", "Ô"],
    ["Õ", "Õ"],
    ["Ö", "Ö"],
    ["Ù", "Ù"],
    ["Ú", "Ú"],
    ["Û", "Û"],
    ["Ü", "Ü"],
    ["Ã ", "à"],
    ["á", "á"],
    ["â", "â"],
    ["ã", "ã"],
    ["ä", "ä"],
    ["ç", "ç"],
    ["è", "è"],
    ["Ãé", "é"],
    ["ê", "ê"],
    ["ë", "ë"],
    ["ì", "ì"],
    ["í", "í"],
    ["î", "î"],
    ["ï", "ï"],
    ["ñ", "ñ"],
    ["ò", "ò"],
    ["ó", "ó"],
    ["ô", "ô"],
    ["õ", "õ"],
    ["ö", "ö"],
    ["ù", "ù"],
    ["ú", "ú"],
    ["û", "û"],
    ["ü", "ü"],
    ["–", "–"],
    ["—", "—"],
    ["‘", "‘"],
    ["’", "’"],
    ["“", "“"],
    ["”", "”"],
    ["•", "•"],
    ["…", "…"],
    ["â€", "†"],
    ["ª", "ª"],
    ["º", "º"],
    ["°", "°"],
    ["§", "§"],
    ["«", "«"],
    ["»", "»"],
    ["·", "·"],
    ["Â", ""],
  ];

  replacements.forEach(([from, to]) => {
    if (text.includes(from)) text = text.split(from).join(to);
  });

  return text;
};

const repairMojibakeDeep = (value) => {
  if (typeof value === "string") return repairMojibakeText(value);
  if (Array.isArray(value))
    return value.map((item) => repairMojibakeDeep(item));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [key, repairMojibakeDeep(val)]),
    );
  }
  return value;
};

const containsIrrecoverableCorruption = (value) => {
  try {
    return JSON.stringify(value || "").includes("�");
  } catch {
    return String(value || "").includes("�");
  }
};

// --- MODAL: ENVIO WHATSAPP ---
const DeliveryModal = ({ onClose, clientName, clientPhone, contractName }) => {
  useEscapeKey(onClose);
  const [phone] = useState(clientPhone || "");

  const handleSendWhatsApp = () => {
    if (!phone || phone.length < 8) return alert("Telefone inválido.");

    const message = `Olá, *${clientName}*.

Segue a minuta do contrato de honorários para análise e assinatura: *${contractName}*.

Fico à disposição para dúvidas.

*Rodrigo Demeterco Adv.*`;

    const link = WhatsAppService.generateLink(phone, message);
    window.open(link, "_blank");
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in-up">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden border-t-4 border-brand-blue">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
          <h3 className="font-bold text-gray-800 text-sm flex items-center gap-2">
            <Send className="w-4 h-4 text-brand-blue" /> Enviar / Assinar
          </h3>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-gray-500 mb-2">
            Selecione a ação para o contrato: <strong>{contractName}</strong>
          </p>

          <button
            onClick={handleSendWhatsApp}
            className="w-full py-3 bg-green-50 border border-green-200 hover:bg-green-100 rounded-lg flex items-center px-4 gap-3 transition-colors group"
          >
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white">
              <i className="fab fa-whatsapp"></i>
            </div>
            <div className="text-left">
              <h4 className="text-xs font-bold text-green-900 group-hover:text-green-900">
                Enviar Minuta
              </h4>
              <p className="text-[9px] text-green-700">
                Via WhatsApp (Link Direto)
              </p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// --- COMPONENTE: MODAL DE PROVISIONAMENTO FINANCEIRO (Fase 41 — Faturamento Atômico) ---
const brl = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    v,
  );

const ProvisionModal = ({ onClose, onConfirm, initialValue, clientName }) => {
  useEscapeKey(onClose);
  const [installments, setInstallments] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [totalValue, setTotalValue] = useState(initialValue || 0);
  const [loading, setLoading] = useState(false);

  const preview = useMemo(() => {
    const v = Number(totalValue) || 0;
    const n = Math.max(1, Number(installments) || 1);
    if (v <= 0 || !firstDueDate) return [];
    return generateInstallments(v, n, firstDueDate, {
      description: "Honorários",
    });
  }, [totalValue, installments, firstDueDate]);

  const handleConfirm = async () => {
    if (Number(totalValue) <= 0)
      return alert("O valor deve ser maior que zero.");
    setLoading(true);
    await onConfirm({
      installments: Number(installments),
      firstDueDate,
      totalValue: Number(totalValue),
    });
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in-up">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden border-t-4 border-green-600">
        <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-green-50">
          <h3 className="font-bold text-green-900 text-sm flex items-center gap-2">
            <Coins className="w-4 h-4" /> Faturar Contrato no ERP
          </h3>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-blue-50 p-3 rounded border border-blue-100 text-xs text-blue-800">
            <strong>Cliente:</strong> {clientName || "Não identificado"}
            <br />
            Os lançamentos serão criados em <strong>
              financial_ledger
            </strong>{" "}
            (Contas a Receber).
          </div>
          <div>
            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
              Valor Total (R$)
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded font-bold text-gray-800 text-lg"
              value={totalValue}
              onChange={(e) => setTotalValue(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                Parcelas
              </label>
              <input
                type="number"
                min="1"
                max="60"
                className="w-full p-2 border rounded"
                value={installments}
                onChange={(e) => setInstallments(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">
                1º Vencimento
              </label>
              <input
                type="date"
                className="w-full p-2 border rounded"
                value={firstDueDate}
                onChange={(e) => setFirstDueDate(e.target.value)}
              />
            </div>
          </div>
          {preview.length > 0 && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="max-h-44 overflow-y-auto custom-scrollbar">
                <table className="w-full text-[11px]">
                  <thead className="bg-[#20243B] text-white sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-bold">#</th>
                      <th className="px-2 py-1.5 text-left font-bold">
                        Vencimento
                      </th>
                      <th className="px-2 py-1.5 text-right font-bold">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                      >
                        <td className="px-2 py-1 text-gray-400 font-bold">
                          {idx + 1}
                        </td>
                        <td className="px-2 py-1 text-gray-700">{p.dueDate}</td>
                        <td className="px-2 py-1 text-right font-bold text-green-700">
                          {brl(p.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-3 py-1.5 bg-gray-50 border-t border-gray-200 text-[10px] text-gray-500 flex justify-between">
                <span>{preview.length} parcela(s)</span>
                <span className="font-bold text-green-700">
                  Total: {brl(Number(totalValue) || 0)}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-100">
          <SaveCancelBar
            onCancel={onClose}
            onSave={handleConfirm}
            cancelLabel="Cancelar"
            saveLabel="Lançar no ERP"
            saving={loading}
          />
        </div>
      </div>
    </div>
  );
};

// --- COMPONENTE: MODAL DE IMPORTAÇÃO DE CLIENTE ---
const ClientImportModal = ({ onClose, onSelect }) => {
  return (
    <CrmClientPickerModal
      isOpen={true}
      onClose={onClose}
      onSelect={onSelect}
      db={db}
      appId={appId}
      title="Base de Clientes (CRM)"
    />
  );
};

// --- COMPONENTE: SELETOR DE TAGS (MULTI-SELECT) ---
const TagSelector = ({ selectedTags, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTag = (tag) => {
    let newTags;
    if (tag === "Universal") {
      newTags = selectedTags.includes("Universal") ? [] : ["Universal"];
    } else {
      const withoutUniversal = selectedTags.filter((t) => t !== "Universal");
      if (withoutUniversal.includes(tag)) {
        newTags = withoutUniversal.filter((t) => t !== tag);
      } else {
        newTags = [...withoutUniversal, tag];
      }
    }
    onChange(newTags);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-[10px] font-bold text-gray-600 hover:border-brand-blue transition-colors max-w-[240px]"
      >
        <Tag className="w-3 h-3 text-brand-blue" />
        <span className="truncate">
          {selectedTags.length > 0 ? selectedTags.join(", ") : "Vincular Áreas"}
        </span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-[70] p-2 animate-fade-in-up max-h-60 overflow-y-auto custom-scrollbar">
          <label className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded cursor-pointer border-b border-gray-100 mb-1">
            <input
              type="checkbox"
              checked={selectedTags.includes("Universal")}
              onChange={() => toggleTag("Universal")}
              className="rounded text-brand-blue focus:ring-0 w-3 h-3"
            />
            <span className="text-xs font-bold text-gray-800">
              UNIVERSAL (Todas as Áreas)
            </span>
          </label>
          <p className="text-[9px] font-bold text-gray-400 uppercase mb-1 px-2 mt-2">
            Específicas
          </p>
          {Object.entries(TAXONOMY).map(([key, val]) => (
            <label
              key={key}
              className="flex items-center gap-2 p-2 hover:bg-blue-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedTags.includes(key)}
                onChange={() => toggleTag(key)}
                className="rounded text-blue-600 focus:ring-0 w-3 h-3"
              />
              <span className="text-xs text-gray-600 leading-tight">
                {val.label}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// --- COMPONENTE: PAINEL DE REGRAS ---
const RulesPanel = ({ blockData, onChange, taxonomy }) => {
  const MultiSelectGeneric = ({
    label,
    options,
    values,
    onUpdate,
    icon: Icon,
  }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
      const clickOut = (e) => {
        if (ref.current && !ref.current.contains(e.target)) setOpen(false);
      };
      document.addEventListener("mousedown", clickOut);
      return () => document.removeEventListener("mousedown", clickOut);
    }, []);
    return (
      <div className="relative" ref={ref}>
        <button
          onClick={() => setOpen(!open)}
          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-[10px] font-bold text-gray-600 hover:border-brand-blue max-w-full"
        >
          {Icon && <Icon className="w-3 h-3 text-brand-blue" />}{" "}
          <span>
            {label} ({values.length})
          </span>{" "}
          <ChevronDown className="w-3 h-3" />
        </button>
        {open && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded shadow-xl z-[70] p-2 max-h-60 overflow-auto custom-scrollbar">
            {options.map((opt) => (
              <label
                key={opt.value}
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={values.includes(opt.value)}
                  onChange={() => {
                    onUpdate(
                      values.includes(opt.value)
                        ? values.filter((v) => v !== opt.value)
                        : [...values, opt.value],
                    );
                  }}
                  className="rounded text-blue-600 w-3 h-3"
                />
                <span className="text-xs text-gray-700">{opt.label}</span>
              </label>
            ))}
            {options.length === 0 && (
              <p className="text-[10px] text-gray-400 p-2">Sem opções.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  const allObjects = Object.values(taxonomy).flatMap((t) =>
    t.objects.map((o) => ({ value: o.id, label: o.label })),
  );

  return (
    <div className="bg-gray-50 p-3 rounded border border-gray-200 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-500 uppercase flex items-center gap-1">
          <Filter className="w-3 h-3" /> Regras de Vinculação
        </span>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <TagSelector
          selectedTags={blockData.tags || ["Universal"]}
          onChange={(newTags) => onChange({ ...blockData, tags: newTags })}
        />

        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 max-w-full">
          <User className="w-3 h-3 text-brand-blue" />
          <select
            className="text-[10px] font-bold text-gray-700 bg-transparent outline-none cursor-pointer w-28"
            value={blockData.condition || "ALWAYS"}
            onChange={(e) =>
              onChange({ ...blockData, condition: e.target.value })
            }
          >
            <option value="ALWAYS">Qualquer Posição</option>
            <option value="AUTOR_ONLY">Apenas Autor</option>
            <option value="REU_ONLY">Apenas Réu</option>
          </select>
        </div>

        <MultiSelectGeneric
          label="Objetos"
          icon={LinkIcon}
          options={allObjects}
          values={blockData.requiredObjects || []}
          onUpdate={(v) => onChange({ ...blockData, requiredObjects: v })}
        />

        <MultiSelectGeneric
          label="Honorários"
          icon={DollarSign}
          options={HONORARIOS_OPTIONS.map((h) => ({
            value: h.id,
            label: h.label,
          }))}
          values={blockData.requiredHonorarios || []}
          onUpdate={(v) => onChange({ ...blockData, requiredHonorarios: v })}
        />

        <label
          className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50"
          title="Se marcado, exige TODOS os itens (AND). Se desmarcado, exige APENAS UM (OR)."
        >
          <input
            type="checkbox"
            className="rounded text-brand-blue w-3 h-3"
            checked={blockData.matchRule === "ALL"}
            onChange={(e) =>
              onChange({
                ...blockData,
                matchRule: e.target.checked ? "ALL" : "ANY",
              })
            }
          />
          <span className="text-[10px] font-bold text-gray-600">
            Regra "E" (ALL)
          </span>
        </label>

        <div
          className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5"
          title="Valor Mínimo da Causa"
        >
          <DollarSign className="w-3 h-3 text-green-600" />
          <input
            type="number"
            placeholder="Min R$"
            className="text-[10px] font-bold text-gray-700 bg-transparent outline-none w-24"
            value={blockData.minValue || ""}
            onChange={(e) =>
              onChange({ ...blockData, minValue: e.target.value })
            }
          />
        </div>

        <label className="flex items-center gap-2 bg-white border border-gray-300 rounded px-2 py-1.5 cursor-pointer hover:bg-gray-50">
          <input
            type="checkbox"
            checked={Boolean(blockData?.isPlural)}
            onChange={(e) =>
              onChange({ ...blockData, isPlural: e.target.checked })
            }
            className="rounded border-gray-300 text-brand-blue focus:ring-brand-blue"
          />
          <span className="text-[10px] font-bold text-gray-600">
            Plural (2+ Partes)
          </span>
        </label>

        {/* CONTROLE DE NÍVEL HIERÁRQUICO (PATCH 4.3 CORRIGIDO) */}
        <div className="w-px h-6 bg-gray-300 mx-1 hidden md:block"></div>

        <div
          className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded px-2 py-1.5"
          title="Nível Hierárquico (Recuo Progressivo)"
        >
          <button
            onClick={() =>
              onChange({
                ...blockData,
                level: Math.max(0, (blockData.level || 0) - 1),
              })
            }
            className="text-orange-700 hover:bg-orange-100 p-1 rounded"
            title="Diminuir Nível"
            type="button"
          >
            <ChevronLeft className="w-3 h-3" />
          </button>
          <span className="text-[10px] font-black text-orange-800 min-w-[50px] text-center">
            Nível {blockData.level || 0}
          </span>
          <button
            onClick={() =>
              onChange({
                ...blockData,
                level: Math.min(10, (blockData.level || 0) + 1),
              })
            }
            className="text-orange-700 hover:bg-orange-100 p-1 rounded"
            title="Aumentar Nível"
            type="button"
          >
            <ChevronRight className="w-3 h-3" />
          </button>

          <button
            type="button"
            onClick={() =>
              onChange({ ...blockData, level: (blockData.level || 0) + 1 })
            }
            className="p-0.5 rounded hover:bg-orange-200 transition-colors"
            title="Aumentar nível (recuo para direita)"
          >
            <ChevronRight className="w-3 h-3 text-orange-700" />
          </button>
        </div>
      </div>

      <div className="flex gap-1 flex-wrap">
        {blockData.tags?.map((t) => (
          <span
            key={t}
            className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200 uppercase"
          >
            {t}
          </span>
        ))}
        {blockData.condition !== "ALWAYS" && (
          <span className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded border border-purple-200 uppercase">
            {blockData.condition}
          </span>
        )}
      </div>
    </div>
  );
};

const SidebarSection = ({
  title,
  icon: Icon,
  children,
  isOpenDefault = true,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenDefault);
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 border-b border-gray-100 hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-2 text-brand-blue">
          <Icon className="w-4 h-4" />
          <span className="text-xs font-bold uppercase tracking-wider">
            {title}
          </span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 space-y-4 animate-fade-in-down">{children}</div>
      )}
    </div>
  );
};

const DocumentMap = ({
  structure,
  activeId,
  onScrollTo,
  onReorder,
  onDelete,
  mode,
  isVisible,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);

  const cleanClauseTitle = (title) => {
    const t = String(title || "").trim();
    return t.replace(/^\d+(?:\.\d+)*\s*[-–—]\s*/g, "");
  };

  const isPreambuloBlock = (block) => {
    return (
      block?.id === "preambulo" ||
      String(block?.title || "")
        .toUpperCase()
        .includes("PREÂMBULO")
    );
  };

  const isOptionLikeBlock = (block) => {
    const id = String(block?.id || "");
    const t = String(block?.title || "").toLowerCase();
    if (/_opt_/.test(id)) return true;
    if (t.includes("opção")) return true;
    if (
      t.includes("pessoa física") ||
      t.includes("pessoa jurid") ||
      t.includes("incapaz") ||
      t.includes("menor") ||
      t.includes("espólio") ||
      t.includes("condom") ||
      t.includes("animal") ||
      t.includes("privada de liberdade")
    )
      return true;
    return false;
  };

  const isNavigableClause = (block) => {
    if (!block) return false;
    if (isPreambuloBlock(block)) return false;
    if (block.isSubClause) return false;
    if (isOptionLikeBlock(block)) return false;
    return true;
  };

  const baseBlocksRaw = Array.isArray(structure)
    ? structure.filter((b) => isNavigableClause(b))
    : [];
  const mapBlocks =
    mode === "producao" && typeof isVisible === "function"
      ? baseBlocksRaw.filter((b) => isVisible(b))
      : baseBlocksRaw;
  const reorderOffset = 0;
  const mapOriginalIndexes = Array.isArray(structure)
    ? structure
        .map((b, i) => ({ b, i }))
        .filter(({ b }) => isNavigableClause(b))
        .map(({ i }) => i)
    : [];

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    if (draggedIndex !== dropIndex) {
      const fromOriginal = mapOriginalIndexes[draggedIndex - reorderOffset];
      const toOriginal = mapOriginalIndexes[dropIndex - reorderOffset];
      if (typeof fromOriginal === "number" && typeof toOriginal === "number")
        onReorder(fromOriginal, toOriginal);
    }
    setDraggedIndex(null);
  };

  return (
    <div className="hidden lg:flex w-72 2xl:w-96 bg-white border-l border-gray-200 h-full flex-col shadow-xl z-20 flex-shrink-0">
      <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
          <MapIcon className="w-3 h-3" /> Mapa do Contrato
        </h3>
        <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">
          {mapBlocks.length} Cláusulas
        </span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-2 custom-scrollbar space-y-1">
        {mapBlocks.map((block, idx) => {
          const isSubClause = block.isSubClause;
          return (
            <div
              key={block.id}
              draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, idx)}
              onClick={() => onScrollTo(block.id)}
              className={`relative pl-2 py-2 pr-2 cursor-pointer text-xs transition-all group rounded border border-transparent flex items-center gap-2 ${activeId === block.id ? "bg-blue-50 border-blue-200 text-brand-blue font-bold shadow-sm" : "hover:bg-gray-50 text-gray-600 hover:border-gray-200"} ${draggedIndex === idx ? "opacity-50 border-dashed border-blue-400 bg-blue-50" : ""} ${isSubClause ? "ml-4" : ""}`}
            >
              <div className="text-gray-300 cursor-grab hover:text-gray-500">
                <GripVertical className="w-3 h-3" />
              </div>
              <div
                className={`w-6 text-[10px] font-bold text-gray-300 text-right select-none ${isSubClause ? "hidden" : ""}`}
              >
                {idx + 1}
              </div>
              <div className="flex-1 truncate">
                {isSubClause && (
                  <i className="fas fa-level-up-alt rotate-90 mr-2 text-gray-300"></i>
                )}
                {cleanClauseTitle(block.title)}
              </div>
              <button
                type="button"
                draggable={false}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onDelete(block.id);
                }}
                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-1 rounded hover:bg-white transition-all"
                title="Excluir"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="p-2 bg-gray-50 text-[9px] text-center text-gray-400 border-t border-gray-200">
        Arraste para reordenar
      </div>
    </div>
  );
};

const inputBaseStyle =
  "w-full px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-[12px] font-medium text-gray-800 outline-none transition-all focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]";
const labelBaseStyle =
  "text-[10px] font-bold text-gray-500 uppercase tracking-widest";
const CONTRACT_ROLE_OPTIONS = [
  "CONTRATANTE",
  "CONTRATADO",
  "FIADOR",
  "INTERVENIENTE",
  "TESTEMUNHA",
];
const formatContractRoleLabel = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (raw === "CONTRATANTE") return "Contratante";
  if (raw === "CONTRATADO") return "Contratado";
  if (raw === "FIADOR") return "Fiador";
  if (raw === "INTERVENIENTE") return "Interveniente";
  if (raw === "TESTEMUNHA") return "Testemunha";
  return "Parte";
};

const ConfigSection = ({
  id,
  title,
  icon,
  children,
  openSection,
  setOpenSection,
}) => {
  const isOpen = openSection === id;
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpenSection(isOpen ? "" : id)}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left transition-colors border-l-2 ${isOpen ? "bg-slate-50 border-l-[#C5A059]" : "bg-white border-l-transparent hover:bg-slate-50"}`}
        type="button"
      >
        <span
          className={`text-[9px] font-medium uppercase tracking-[0.03em] flex items-center gap-1.5 ${isOpen ? "text-[#20243B]" : "text-slate-500"}`}
        >
          <i
            className={`fas ${icon} ${isOpen ? "text-[#C5A059]" : "text-slate-300"}`}
          ></i>
          {title}
        </span>
        <i
          className={`fas fa-chevron-down text-[9px] text-slate-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
        ></i>
      </button>
      {isOpen && (
        <div className="px-2.5 py-1.5 bg-slate-50/80 space-y-2 animate-fade-in">
          {children}
        </div>
      )}
    </div>
  );
};

const DEFAULT_FORM = {
  ramoDireito: "Civil",
  isCustomRamo: false,
  customRamo: "",
  posicaoCliente: "Autor",
  isCustomPosicao: false,
  customPosicao: "",
  parties: [
    {
      id: "contratante",
      name: "",
      cpf: "",
      email: "",
      phone: "",
      address: "",
      nationality: "",
      civilStatus: "",
      profession: "",
      uf: "PR",
      role: "Contratante",
      clientId: null,
      fullQualification: "",
      gender: "M",
      isPlural: false,
    },
  ],
  selectedObjetos: [],
  customObjetos: [],
  selectedHonorarios: [],
  customHonorarios: [],
  objetoPrincipal: "Descreva o objeto...",
  parteContrariaName: "",
  valorCausa: 0,
  valorHonorarios: 0,
  percentualExito: 20,
};

const DEFAULT_PARTNER = {
  active: false,
  name: "",
  oab: "",
  cpf: "",
  address: "",
  clientId: null,
  qualificationData: null,
};

// --- CONTRACT FACTORY (MAIN) ---
export const ContractFactory = () => {
  const { user, email, getToken } = useCurrentUser();
  const {
    mode,
    setMode,
    templateData,
    setTemplateData,
    dataSource,
    setDataSource,
  } = useContractMode();
  const { isSaving, setIsSaving, isPublishing, setIsPublishing } =
    useContractSaving();
  const { showAdvancedActions, setShowAdvancedActions } = useContractAdvanced();
  const {
    editingBlockId,
    setEditingBlockId,
    activeBlockData,
    setActiveBlockData,
    tempTitle,
    setTempTitle,
    isModified,
    setIsModified,
    expandedInactiveId,
    setExpandedInactiveId,
  } = useContractEditing();
  const {
    dialogState,
    setDialogState,
    preFlightState,
    setPreFlightState,
    showImportModal,
    setShowImportModal,
    showPartnerImportModal,
    setShowPartnerImportModal,
    showProvisionModal,
    setShowProvisionModal,
    showDeliveryModal,
    setShowDeliveryModal,
    hydratingDeepClients,
    setHydratingDeepClients,
    openDialog,
    closeDialog,
    openPreFlight,
    closePreFlight,
    setPreFlightCaderno,
  } = useContractModals();
  const { form, setForm } = useContractForm();
  const {
    officeQualificationText,
    setOfficeQualificationText,
    officeQualificationClient,
    setOfficeQualificationClient,
  } = useContractOffice();
  const {
    cloudSyncState,
    cloudSyncMeta,
    markSaving,
    markSaved,
    markError,
    isCloudAutosaveInFlightRef,
    lastCloudAutosavePayloadRef,
  } = useCloudSync();
  const [openSection, setOpenSection] = useState("client");
  // REMOVIDO: showRefPicker duplicado (mantido apenas RulesPanel)
  const [crmClientsById, setCrmClientsById] = useState({});

  // Dicionário Taxonômico de Posições (CNJ / OAB Padrão Ouro)
  // MIGRAÇÃO PARA FONTE ÚNICA (22/08/2026): esta lista divergia da
  // equivalente em ProcurationEngine.jsx (aqui faltava "Fiel Depositário"
  // em Cível/Penal, entre outras diferenças) — unificada em
  // src/config/PosicoesProcessuais.js (união das duas, nada removido).
  const POSICOES_POR_RAMO = POSICOES_POR_RAMO_SHARED;

  // DEV DEBUG HELPER
  useEffect(() => {
    if (import.meta.env.DEV) {
      browserEnv.setDebug = browserEnv.setDebug || {};
      browserEnv.setDebug.crmClientsById = crmClientsById;
      browserEnv.setDebug.lastAnimalProcessed = null;
    }
  }, [crmClientsById]);

  const { data: officeData } = useOfficeData();
  const [editingPublicationMeta, setEditingPublicationMeta] = useState(null);

  const publishedEditLockRef = useRef(false);
  const activeDocumentLockRef = useRef(false);
  const draftPromptedRef = useRef(false);

  useEffect(() => {
    publishedEditLockRef.current = Boolean(editingPublicationMeta?.documentId);
  }, [editingPublicationMeta]);

  useEffect(() => {
    const source = String(dataSource || "").trim();
    activeDocumentLockRef.current =
      Boolean(editingPublicationMeta?.documentId) ||
      [
        "published_document",
        "cloud_autosave",
        "draft_local",
        "localStorage",
        "migrated_from_cloud_autosave",
        "migrated_from_localStorage",
        "backup_json",
        "migrated_from_backup_json",
      ].includes(source);
  }, [dataSource, editingPublicationMeta]);

  const openPreFlightForContract = (
    publishType = "simple",
    format = "html",
  ) => {
    const safeCadernoId = String(preFlightState.cadernoId || "").trim();
    if (!safeCadernoId) {
      setDialogState({
        open: true,
        title: "Caderno de Autos obrigatório",
        message:
          "Selecione o Caderno de Autos (Vincular a Processo) antes de salvar. Todo contrato deve estar vinculado a um processo (caderno).",
        mode: "info",
        onConfirm: null,
      });
      return;
    }
    setPreFlightState((prev) => ({
      ...prev,
      open: true,
      publishType:
        String(publishType || "simple")
          .trim()
          .toLowerCase() === "consent"
          ? "consent"
          : "simple",
      format:
        String(format || "html")
          .trim()
          .toLowerCase() === "pdf"
          ? "pdf"
          : "html",
    }));
  };

  const closePreFlightForContract = () => {
    setPreFlightState({
      open: false,
      publishType: "simple",
      format: "html",
      cadernoId: "",
      cadernoTitle: "",
    });
  };

  const confirmPreFlightForContract = () => {
    const nextPublishType = preFlightState.publishType;
    const nextFormat = preFlightState.format;
    const nextCadernoId = String(preFlightState.cadernoId || "").trim();
    const nextCadernoTitle = String(preFlightState.cadernoTitle || "").trim();
    if (!nextCadernoId) {
      setDialogState({
        open: true,
        title: "Caderno de Autos obrigatório",
        message:
          "Selecione o Caderno de Autos (Vincular a Processo) antes de publicar. Todo contrato deve estar vinculado a um processo (caderno) para que o documento apareça corretamente no portal do cliente.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }
    closePreFlightForContract();
    handlePublishContract(
      nextPublishType,
      nextFormat,
      nextCadernoId,
      nextCadernoTitle,
    );
  };

  useEffect(() => {
    let alive = true;

    if (!officeData) {
      setOfficeQualificationText("");
      setOfficeQualificationClient(null);
      return () => {
        alive = false;
      };
    }

    const hydrateOffice = async () => {
      try {
        const canonical = await hydrateAndBuildCanonicalClientQualification(
          officeData,
          crmClientsById,
          { isAttorneyOfRecord: true },
        );
        if (!alive) return;
        setOfficeQualificationText(String(canonical?.text || "").trim());
        setOfficeQualificationClient(canonical?.client || officeData);
      } catch {
        if (!alive) return;
        setOfficeQualificationText(
          String(
            buildCanonicalClientQualification(officeData, crmClientsById, {
              isAttorneyOfRecord: true,
            })?.text || "",
          ).trim(),
        );
        setOfficeQualificationClient(officeData);
      }
    };

    hydrateOffice();

    return () => {
      alive = false;
    };
  }, [officeData, crmClientsById]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    try {
      browserEnv.setDebug = browserEnv.setDebug || {};
      const parties = Array.isArray(form?.parties) ? form.parties : [];
      browserEnv.setDebug.partiesDebug = parties.map((p) => ({
        id: p?.id,
        name: p?.name,
        role: p?.role,
        clientId: p?.clientId,
        hasFullQualification: Boolean(
          String(p?.fullQualification || "").trim(),
        ),
      }));
    } catch {
      /* ignore */
    }
  }, [form?.parties]);

  useEffect(() => {
    if (!showAdvancedActions) return undefined;

    const updateMenuPosition = () => {
      const anchor = advancedMenuAnchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const menuWidth = 256;
      const margin = 12;
      const left = Math.min(
        Math.max(margin, rect.right - menuWidth),
        Math.max(margin, window.innerWidth - menuWidth - margin),
      );
      const top = Math.max(margin, rect.top - 8);
      setAdvancedMenuPosition({ top, left });
    };

    const handlePointerDown = (event) => {
      const menuNode = advancedMenuRef.current;
      const anchorNode = advancedMenuAnchorRef.current;
      const target = event.target;
      if (menuNode && menuNode.contains(target)) return;
      if (anchorNode && anchorNode.contains(target)) return;
      setShowAdvancedActions(false);
    };

    updateMenuPosition();
    browserEnv.addEventListener("resize", updateMenuPosition);
    browserEnv.addEventListener("scroll", updateMenuPosition, true);
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [showAdvancedActions]);

  const { partnerData, setPartnerData, partnerExtras, setPartnerExtras } =
    useContractPartner();
  const {
    importTargetPartyId,
    setImportTargetPartyId,
    importTargetPartnerIndex,
    setImportTargetPartnerIndex,
  } = useContractImport();
  const advancedMenuAnchorRef = useRef(null);
  const advancedMenuRef = useRef(null);
  const { advancedMenuPosition, setAdvancedMenuPosition } =
    useContractAdvanced();

  const DAS_PARTES_DEFAULT_CONTENT = useMemo(() => {
    const extras = Array.isArray(partnerExtras) ? partnerExtras : [];
    const firstPartner = {
      name: partnerData?.name || "",
      oab: partnerData?.oab || "",
      cpf: partnerData?.cpf || "",
      address: partnerData?.address || "",
    };
    const activePartners = partnerData?.active
      ? [firstPartner, ...extras].filter(
          (p) =>
            String(p?.name || p?.oab || p?.cpf || p?.address || "").trim() !==
            "",
        )
      : [];

    const contratanteNum = `1.${activePartners.length + 2}`;
    return String(
      `<p><strong>1.1.</strong> [[ROTULO_CONTRATADO]] [[QUALIFICACAO_CONTRATADO]]</p>` +
        `<p><strong>${contratanteNum}.</strong> [[ROTULO_CONTRATANTE]] [[QUALIFICACAO_COMPLETA]]</p>`,
    );
  }, [
    partnerData.active,
    partnerData.name,
    partnerData.oab,
    partnerData.cpf,
    partnerData.address,
    partnerExtras,
  ]);

  const G = useMemo(() => {
    const genderRaw = String(form?.parties?.[0]?.gender || "M").toUpperCase();
    const isFem = genderRaw === "F" || genderRaw === "PJ";
    const isPl = Boolean(form?.parties?.[0]?.isPlural);

    return {
      STATUS: `${isFem ? "FEMININO" : "MASCULINO"} | ${isPl ? "PLURAL" : "SINGULAR"}`,

      O: "A",
      o: "a",
      UM: "UMA",
      um: "uma",
      AO: "À",
      ao: "à",
      DO: "DA",
      do: "da",
      NO: "NA",
      no: "na",
      PELO: "PELA",
      pelo: "pela",

      CONTRATANTE: "a parte CONTRATANTE",
      contratante: "a parte CONTRATANTE",
      SUJEITO: "a parte CONTRATANTE",
      sujeito: "a parte CONTRATANTE",

      PORTADOR: isPl
        ? isFem
          ? "portadoras"
          : "portadores"
        : isFem
          ? "portadora"
          : "portador",
      INSCRITO: isPl
        ? isFem
          ? "inscritas"
          : "inscritos"
        : isFem
          ? "inscrita"
          : "inscrito",
      DOMICILIADO: isPl
        ? isFem
          ? "domiciliadas"
          : "domiciliados"
        : isFem
          ? "domiciliada"
          : "domiciliado",
      REPRESENTADO: isPl
        ? isFem
          ? "representadas"
          : "representados"
        : isFem
          ? "representada"
          : "representado",
      INFORMADO: isPl
        ? isFem
          ? "informadas"
          : "informados"
        : isFem
          ? "informada"
          : "informado",
      INTERESSADO: isPl
        ? isFem
          ? "interessadas"
          : "interessados"
        : isFem
          ? "interessada"
          : "interessado",
      CIENTE: isPl ? "cientes" : "ciente",
    };
  }, [form?.parties]);

  const S = (text, genderOverride) => {
    if (!text) return "";
    const g = String(genderOverride || form?.parties?.[0]?.gender || "M")
      .trim()
      .toUpperCase();
    const isFem = g === "F" || g === "PJ";

    let clean = String(text);

    if (isFem) {
      clean = clean
        .replace(/\(a\)/gi, "a")
        .replace(/\/(a)\b/gi, "a")
        .replace(/\(o\)/gi, "a")
        .replace(/\/(o)\b/gi, "a");
    } else {
      clean = clean
        .replace(/\(a\)/gi, "")
        .replace(/\/(a)\b/gi, "")
        .replace(/\(o\)/gi, "o")
        .replace(/\/(o)\b/gi, "o");
    }

    return clean.replace(/\s+/g, " ").trim();
  };

  const PREAMBULO_DEFAULT_CONTENT = useMemo(() => {
    const partnersCount = partnerData?.active
      ? 1 + (Array.isArray(partnerExtras) ? partnerExtras.length : 0)
      : 0;
    const hasPartners = partnersCount > 0;
    const PL = {
      CONTRATADA_LC: hasPartners
        ? "da parte <strong>CONTRATADA</strong>"
        : "da parte <strong>CONTRATADA</strong>",
      CONTRATADA_UC: hasPartners ? "a parte CONTRATADA" : "a parte CONTRATADA",
      INDISPENSAVEL: hasPartners ? "indispensáveis" : "indispensável",
      PROFISSIONAL: hasPartners
        ? "profissionais habilitados"
        : "profissional habilitado",
      VERBO_SER: hasPartners ? "são" : "é",
    };

    return String(
      `<p>Considerando que <strong>a parte CONTRATANTE</strong> busca a expertise jurídica ${PL.CONTRATADA_LC} para a defesa de seus interesses; e considerando que ${PL.CONTRATADA_UC} ${PL.VERBO_SER} ${PL.PROFISSIONAL} e <strong>${PL.INDISPENSAVEL} à administração da justiça (CRFB/1988, art. 133)</strong>, as partes firmam o presente contrato com o propósito de formalizar os termos da prestação de serviços, assegurando total transparência, clareza nas obrigações e a busca pela excelência profissional.</p>` +
        `<p>Para os fins deste instrumento, utilizaremos a forma <strong>"a parte CONTRATANTE"</strong> e <strong>"a parte CONTRATADA"</strong>.</p>`,
    );
  }, [partnerData.active, partnerExtras]);

  const T = useMemo(() => {
    const partnersCount = partnerData?.active
      ? 1 + (Array.isArray(partnerExtras) ? partnerExtras.length : 0)
      : 0;
    const hasPartners = partnersCount > 0;
    return {
      contratado_lc: hasPartners ? "os CONTRATADOS" : "o CONTRATADO",
      CONTRATADO: hasPartners ? "OS CONTRATADOS" : "O CONTRATADO",
      VERBO_SER: hasPartners ? "são" : "é",
      PROFISSIONAL: hasPartners
        ? "profissionais habilitados"
        : "profissional habilitado",
      INDISPENSAVEL: hasPartners ? "indispensáveis" : "indispensável",
    };
  }, [partnerData.active, partnerExtras]);

  const ClauseWrapper = ({ title, id, onEdit, children, isPreamble }) => {
    return (
      <div
        className={`clause-block group relative${isPreamble ? " is-preamble" : ""}`}
        id={id ? `block-${id}` : undefined}
      >
        {mode === "auditoria" && typeof onEdit === "function" ? (
          <div className="absolute top-0 right-0 flex gap-2 no-print z-50">
            <button
              className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-semibold hover:bg-blue-200"
              style={{ pointerEvents: "auto" }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onEdit();
              }}
              type="button"
              title="Editar"
            >
              <Edit3 className="w-3 h-3" />
            </button>
          </div>
        ) : null}

        <h2
          className={`clause-heading${isPreamble ? " preamble-heading" : ""}`}
        >
          {title}
        </h2>
        {children}
      </div>
    );
  };

  const renderBlock = (block, clauseNumber) => {
    if (!block) return null;
    const isMatch = validateBlock(block);

    const sanitizeScreenHtml = (rawHtml) => {
      let h = String(rawHtml || "");
      // Remove classes que induzem colunas/grade no conteúdo salvo.
      h = h.replace(
        /\sclass\s*=\s*(["'])([\s\S]*?)\1/gi,
        (full, quote, classText) => {
          const tokens = String(classText || "")
            .split(/\s+/g)
            .filter(Boolean);
          const kept = tokens.filter((t) => {
            if (!t) return false;
            if (
              t === "flex" ||
              t === "grid" ||
              t === "inline-flex" ||
              t === "inline-grid"
            )
              return false;
            if (t.startsWith("grid-cols-")) return false;
            if (t.startsWith("col-") || t.startsWith("col-span-")) return false;
            if (t.startsWith("w-") || t.startsWith("basis-")) return false;
            if (t.startsWith("columns-") || t.startsWith("column-"))
              return false;
            return true;
          });
          if (kept.length === 0) return "";
          return ` class=${quote}${kept.join(" ")}${quote}`;
        },
      );
      return h;
    };

    // visibleMask (opcional): array paralelo a `children` indicando quais
    // itens estão visíveis. Itens ocultos (condição/honorários não
    // atendidos) não consomem número — sem isso, um item condicional
    // escondido no meio de itens sempre-visíveis fazia a numeração
    // renderizada pular (ex.: "3.2." direto para "3.6."), parecendo
    // cláusula faltando para quem lê o documento gerado (Art. 354/§3.2).
    const computeHierNumbers = (children, macroNum, visibleMask) => {
      const out = [];
      const stack = [];
      const arr = Array.isArray(children) ? children : [];
      const macro = typeof macroNum === "number" ? macroNum : null;

      for (let i = 0; i < arr.length; i += 1) {
        if (visibleMask && visibleMask[i] === false) {
          out.push("");
          continue;
        }
        const child = arr[i] || {};
        let lvl = Number(child.level || 0);
        if (!Number.isFinite(lvl) || lvl < 0) lvl = 0;
        lvl = Math.floor(lvl);

        while (stack.length <= lvl) stack.push(0);
        stack.length = lvl + 1;
        stack[lvl] = (stack[lvl] || 0) + 1;
        for (let k = 0; k < lvl; k += 1) {
          if (!stack[k]) stack[k] = 1;
        }

        const parts = [];
        if (macro !== null) parts.push(String(macro));
        for (let k = 0; k <= lvl; k += 1) parts.push(String(stack[k] || 1));
        out.push(parts.join(".") + ".");
      }

      return out;
    };

    if (editingBlockId === block.id) {
      const hasChildren =
        Array.isArray(activeBlockData?.children) &&
        activeBlockData.children.length > 0;
      const childNumbers = hasChildren
        ? computeHierNumbers(activeBlockData.children, clauseNumber)
        : [];

      return (
        <div
          id={`block-${block.id}`}
          className="my-6 bg-white border-2 border-highlight-action shadow-2xl rounded-lg overflow-hidden z-50 relative animate-fade-in-up"
        >
          <div className="bg-gradient-to-r from-orange-50 to-white p-3 border-b border-highlight-action/30 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Edit3 className="w-4 h-4 text-highlight-action" />
              <input
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                className="font-bold text-brand-blue text-sm bg-transparent border-b border-gray-300 w-full focus:outline-none focus:border-highlight-action"
              />
            </div>
            <RulesPanel
              blockData={activeBlockData}
              onChange={setActiveBlockData}
              taxonomy={TAXONOMY}
            />
          </div>
          <div className="relative">
            {hasChildren ? (
              <div className="p-4 bg-gray-50 flex flex-col gap-4">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2 border-b border-gray-200 pb-2 flex justify-between items-center">
                  <span>Sub-cláusulas (Itens Dinâmicos)</span>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[9px] font-extrabold shadow-sm">
                    {activeBlockData.children.length} itens
                  </span>
                </div>
                {activeBlockData.children.map((child, cIdx) => (
                  <div
                    key={child.id || cIdx}
                    className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-all hover:border-blue-300 hover:shadow-md"
                    style={{
                      marginLeft: `${(Number(child?.level || 0) || 0) * 24}px`,
                      borderLeft: "none",
                    }}
                  >
                    <div className="bg-gray-100 p-2 flex justify-between items-center border-b border-gray-200">
                      <span className="text-[10px] font-bold text-gray-600 tracking-wider">
                        {childNumbers[cIdx]
                          ? childNumbers[cIdx]
                          : `ITEM ${cIdx + 1}`}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addSubClauseAfter(child.id, cIdx);
                          }}
                          className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1.5 rounded transition-colors"
                          title="Inserir sub-cláusula abaixo"
                          type="button"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => {
                            const newChildren = [...activeBlockData.children];
                            newChildren.splice(cIdx, 1);
                            setActiveBlockData({
                              ...activeBlockData,
                              children: newChildren,
                            });
                          }}
                          className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-colors"
                          title="Remover Item"
                          type="button"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3 flex flex-col gap-3">
                      <RulesPanel
                        blockData={child}
                        onChange={(newChildData) => {
                          const newChildren = [...activeBlockData.children];
                          newChildren[cIdx] = newChildData;
                          setActiveBlockData({
                            ...activeBlockData,
                            children: newChildren,
                          });
                        }}
                        taxonomy={TAXONOMY}
                      />

                      {/* BARRA DE VARIÁVEIS RÁPIDAS (COMPLETA E CATEGORIZADA V2) */}
                      <div className="flex flex-col gap-2 pb-3 border-b border-gray-100 select-none">
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-bold text-gray-400 uppercase w-14 text-right">
                            Finanças:
                          </span>
                          {[
                            ["R$ Causa", "[[VALOR_CAUSA]]"],
                            ["R$ Fixo", "[[VALOR_HONORARIOS]]"],
                            ["% Êxito", "[[PERCENTUAL_EXITO]]"],
                          ].map(([label, val]) => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => {
                                const newChildren = [
                                  ...activeBlockData.children,
                                ];
                                newChildren[cIdx] = {
                                  ...child,
                                  content: (child.content || "") + val,
                                };
                                setActiveBlockData({
                                  ...activeBlockData,
                                  children: newChildren,
                                });
                              }}
                              className="text-[9px] font-bold bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded hover:bg-green-100 transition-colors"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-bold text-gray-400 uppercase w-14 text-right">
                            Cliente:
                          </span>
                          {[
                            ["Nome", "[[NOME_CLIENTE]]"],
                            ["CPF", "[[CPF_CLIENTE]]"],
                            ["Rótulo", "[[ROTULO_CONTRATANTE]]"],
                            ["Qualificação", "[[QUALIFICACAO_COMPLETA]]"],
                            [
                              "Qualif. Todos",
                              "[[QUALIFICACAO_COMPLETA_DE_TODOS]]",
                            ],
                          ].map(([label, val]) => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => {
                                const newChildren = [
                                  ...activeBlockData.children,
                                ];
                                newChildren[cIdx] = {
                                  ...child,
                                  content: (child.content || "") + val,
                                };
                                setActiveBlockData({
                                  ...activeBlockData,
                                  children: newChildren,
                                });
                              }}
                              className="text-[9px] font-bold bg-blue-50 text-brand-blue border border-blue-200 px-2 py-0.5 rounded hover:bg-blue-100 transition-colors"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-bold text-gray-400 uppercase w-14 text-right">
                            Contrato:
                          </span>
                          {[
                            ["Ramo", "[[RAMO_DIREITO]]"],
                            ["Posição", "[[POSICAO_CLIENTE]]"],
                            ["Serviços", "[[SERVICOS_CONTRATADOS]]"],
                            ["Objeto (Desc.)", "[[OBJETO_PRINCIPAL]]"],
                            ["Nome Escritório", "[[NOME_CONTRATADO]]"],
                            ["Rótulo Contratado", "[[ROTULO_CONTRATADO]]"],
                            [
                              "Qualif. Contratado",
                              "[[QUALIFICACAO_CONTRATADO]]",
                            ],
                          ].map(([label, val]) => (
                            <button
                              key={val}
                              type="button"
                              onClick={() => {
                                const newChildren = [
                                  ...activeBlockData.children,
                                ];
                                newChildren[cIdx] = {
                                  ...child,
                                  content: (child.content || "") + val,
                                };
                                setActiveBlockData({
                                  ...activeBlockData,
                                  children: newChildren,
                                });
                              }}
                              className="text-[9px] font-bold bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded hover:bg-orange-100 transition-colors"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-1.5 items-center">
                          <span className="text-[9px] font-bold text-gray-400 uppercase w-14 text-right">
                            Expressão Fixa:
                          </span>
                          {[
                            [
                              "a parte CONTRATANTE",
                              "a parte <strong>CONTRATANTE</strong>",
                            ],
                            [
                              "da parte CONTRATANTE",
                              "da parte <strong>CONTRATANTE</strong>",
                            ],
                            [
                              "à parte CONTRATANTE",
                              "à parte <strong>CONTRATANTE</strong>",
                            ],
                            [
                              "a parte CONTRATADA",
                              "a parte <strong>CONTRATADA</strong>",
                            ],
                            [
                              "da parte CONTRATADA",
                              "da parte <strong>CONTRATADA</strong>",
                            ],
                          ].map(([label, val]) => (
                            <button
                              key={label}
                              type="button"
                              onClick={() => {
                                const newChildren = [
                                  ...activeBlockData.children,
                                ];
                                newChildren[cIdx] = {
                                  ...child,
                                  content: (child.content || "") + val,
                                };
                                setActiveBlockData({
                                  ...activeBlockData,
                                  children: newChildren,
                                });
                              }}
                              className="text-[9px] font-bold bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded hover:bg-purple-100 transition-colors"
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <ContractEditor
                        key={child.id || cIdx}
                        compact
                        hideActions
                        initialData={{
                          ...(child || {}),
                          type: "text",
                          content: String(child?.content || ""),
                        }}
                        onSave={() => {}}
                        onDraftChange={(draft) => {
                          const nextContent = String(draft?.content || "");
                          const newChildren = [...activeBlockData.children];
                          newChildren[cIdx] = {
                            ...child,
                            content: nextContent,
                          };
                          setActiveBlockData({
                            ...activeBlockData,
                            children: newChildren,
                          });
                        }}
                        isSaving={false}
                        onCancel={null}
                        structure={templateData?.structure || []}
                      />
                    </div>
                  </div>
                ))}
                <button
                  onClick={() => {
                    const newChild = {
                      id: `sub_${Date.now()}`,
                      type: "sub_clause",
                      content: "",
                      condition: "ALWAYS",
                      requiredHonorarios: [],
                    };
                    setActiveBlockData({
                      ...activeBlockData,
                      children: [...activeBlockData.children, newChild],
                    });
                  }}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 font-bold text-xs uppercase tracking-wider hover:bg-blue-50 hover:border-brand-blue hover:text-brand-blue transition-all flex items-center justify-center gap-2 mt-2"
                  type="button"
                >
                  <Plus className="w-4 h-4" /> Adicionar Novo Item
                </button>

                <div className="mt-2 pt-4 border-t border-gray-200">
                  <SaveCancelBar
                    onCancel={() => {
                      setEditingBlockId(null);
                      setActiveBlockData(null);
                    }}
                    onSave={() => {
                      saveEditorContent(activeBlockData);
                      setActiveBlockData(null);
                    }}
                    saving={isSaving}
                    saveLabel="Salvar Cláusula"
                    cancelLabel="Cancelar"
                  />
                </div>
              </div>
            ) : (
              <ContractEditor
                key={editingBlockId}
                initialData={activeBlockData}
                onSave={saveEditorContent}
                isSaving={isSaving}
                onCancel={() => {
                  setEditingBlockId(null);
                  setActiveBlockData(null);
                }}
                structure={templateData?.structure || []}
              />
            )}
          </div>
        </div>
      );
    }

    if (mode === "auditoria" && !isMatch) {
      const isExpanded = expandedInactiveId === block.id;
      return (
        <div id={`block-${block.id}`} className="mb-2 no-print">
          <div
            onClick={() => setExpandedInactiveId(isExpanded ? null : block.id)}
            className="flex items-center justify-between p-2 bg-gray-100 border border-gray-300 border-dashed rounded cursor-pointer hover:bg-gray-200 transition-colors"
          >
            <div className="flex items-center gap-3">
              <EyeOff className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-bold text-gray-500 line-through">
                {block.title}
              </span>
            </div>
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
            />
          </div>
          {isExpanded && (
            <div className="p-4 border-l-2 border-gray-300 ml-4 opacity-50 bg-gray-50 text-sm">
              <div className="flex justify-end gap-2 mb-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(block);
                  }}
                  className="text-blue-500 text-xs font-bold hover:underline"
                >
                  EDITAR REGRAS
                </button>
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: parseText(block.content || ""),
                }}
              />
            </div>
          )}
        </div>
      );
    }

    return (
      <div
        id={`block-${block.id}`}
        className="clause-block group relative transition-all duration-200 hover:bg-blue-50/30 rounded-lg"
        style={{
          ...(typeof clauseNumber === "number"
            ? { "--macro-num": clauseNumber }
            : {}),
          "--indent-level": `${(block.level || 0) * 24}px`,
          ...(typeof clauseNumber === "number"
            ? { "--macro-num": clauseNumber }
            : {}),
        }}
      >
        <h2 className="clause-heading relative">
          {(() => {
            const safeId = String(block?.id || "").replace(
              /[^a-zA-Z0-9_\-]/g,
              "_",
            );
            return (
              <span
                className="ghost-marker"
                style={{ top: "-20px", left: 0 }}
                aria-hidden="true"
              >{`RDA_TGT_${safeId}`}</span>
            );
          })()}
          <span className="clause-heading-row w-full flex items-start flex-wrap gap-1">
            {typeof clauseNumber === "number" ? (
              <span className="clause-number">Cláusula {clauseNumber}ª –</span>
            ) : null}
            <span className="clause-title">
              {cleanClauseTitle(block.title)}
            </span>
            <span className="ml-auto flex items-center gap-1 relative z-10">
              {mode === "auditoria" && (
                <>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addClauseAfter(block.id);
                    }}
                    className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print"
                    type="button"
                    title="Inserir cláusula abaixo"
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      startEditing(block);
                    }}
                    className="text-brand-blue hover:bg-blue-50 p-1 rounded no-print"
                    type="button"
                    title="Editar"
                  >
                    <Edit3 className="w-3 h-3" />
                  </button>
                  <button
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      deleteClause(block.id);
                    }}
                    className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded no-print"
                    type="button"
                    title="Excluir"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </>
              )}
              <a
                href="#guia-rapido"
                className="text-[9px] font-bold uppercase bg-gray-100 text-gray-500 px-2 py-1 rounded hover:bg-gray-200 transition-colors no-underline relative inline-block overflow-hidden"
                title="Voltar ao topo"
                onClick={(e) => {
                  // PORTAL/TELA: comportamento de navegação interna.
                  // PRINT/PDF: não deve navegar/scrollar (mantemos sem efeito).
                  const isPrint = (() => {
                    try {
                      return Boolean(
                        window.matchMedia && window.matchMedia("print").matches,
                      );
                    } catch {
                      return false;
                    }
                  })();

                  if (isPrint) return;

                  e.preventDefault();
                  scrollToId("guia-rapido");
                  try {
                    const nextState =
                      window.history &&
                      typeof window.history.state === "object" &&
                      window.history.state
                        ? window.history.state
                        : {};
                    window.history.replaceState(nextState, "", "#guia-rapido");
                  } catch {
                    // ignore
                  }
                }}
              >
                {(() => {
                  const safeId = String(block?.id || "").replace(
                    /[^a-zA-Z0-9_\-]/g,
                    "_",
                  );
                  return (
                    <>
                      <span
                        className="ghost-marker"
                        style={{ top: 0, left: 0 }}
                        aria-hidden="true"
                      >{`RDA_TL_TOP_${safeId}`}</span>
                      <span
                        className="ghost-marker"
                        style={{ bottom: 0, right: 0 }}
                        aria-hidden="true"
                      >{`RDA_BR_TOP_${safeId}`}</span>
                    </>
                  );
                })()}
                TOPO<span aria-hidden="true"> &uarr;</span>
              </a>
            </span>
          </span>
        </h2>

        {block.type === "list" && Array.isArray(block.items) ? (
          renderHierarchicalList(block.items, block.listStyle, clauseNumber)
        ) : Array.isArray(block.children) && block.children.length > 0 ? (
          <div className="clause-body mt-2">
            {(() => {
              const visibleMask = block.children.map((_, i) =>
                isChildVisibleInHierarchy(block.children, i),
              );
              const childNumbers = computeHierNumbers(
                block.children,
                clauseNumber,
                visibleMask,
              );
              return (Array.isArray(block.children) ? block.children : []).map(
                (child, cIdx) => {
                  const isChildVisible = visibleMask[cIdx];

                  if (!isChildVisible) {
                    if (mode === "producao") return null;

                    return (
                      <div
                        key={child.id || cIdx}
                        className="mb-2 p-3 border border-dashed border-gray-300 bg-gray-50 opacity-60 rounded-lg no-print relative"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <EyeOff className="w-3 h-3 text-red-400" />
                          <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest">
                            Item Oculto pela Lógica (Regra não atendida)
                          </span>
                        </div>
                        <span
                          className="text-sm"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeScreenHtml(
                              parseText(child.content),
                            ),
                          }}
                        />
                      </div>
                    );
                  }

                  return (
                    <div
                      key={child.id || cIdx}
                      className="sub-clause-item has-manual-number"
                      style={{
                        "--subclause-indent": (() => {
                          let lvl = Number(child?.level || 0);
                          if (!Number.isFinite(lvl) || lvl < 0) lvl = 0;
                          lvl = Math.floor(lvl);
                          return lvl > 0
                            ? `calc(${lvl} * (var(--clause-num-col) + var(--clause-col-gap)))`
                            : "0";
                        })(),
                        marginLeft: 0,
                      }}
                    >
                      <div className="clause-li-row">
                        <span className="shrink-0 font-bold text-[11pt]">
                          {childNumbers[cIdx] || ""}
                        </span>
                        <span
                          className="sub-clause-content"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeScreenHtml(
                              parseText(child.content),
                            ),
                          }}
                        />
                        {mode === "auditoria" && (
                          <button
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              addSubClauseAfter(child.id, cIdx);
                            }}
                            className="text-gray-400 hover:text-green-600 hover:bg-green-50 p-1 rounded no-print ml-2"
                            type="button"
                            title="Inserir sub-cláusula abaixo"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                },
              );
            })()}
          </div>
        ) : (
          <div
            className="contract-p clause-body"
            dangerouslySetInnerHTML={{
              __html: sanitizeScreenHtml(parseText(block.content)),
            }}
          />
        )}
      </div>
    );
  };

  // Achado do deep-check de 09/08/2026: o bloco de assinatura antigo só
  // renderizava form.parties[0] (Contratante) + escritório (Contratado),
  // ignorando qualquer CONTRATANTE adicional (co-contratante) ou sócio
  // adicional em partnerData/partnerExtras — mesmo o documento já rotulando
  // "CONTRATANTES"/"CONTRATADOS" no plural em outros pontos quando há mais
  // de uma parte. Extraído para função única (mesmo padrão de
  // renderOutorganteSignatures do ProcurationEngine.jsx) para não duplicar
  // a correção nos dois pontos onde a assinatura é renderizada.
  // Achado do titular em 09/08/2026: o fechamento do Contrato não tinha a
  // "Nota de Validade Híbrida" (fundamento legal + campo de Local/Data para
  // assinatura física) que a Procuração já tem — mesmo o titular tendo
  // pedido explicitamente que o Contrato ficasse padronizado igual à
  // Procuração. Replicado aqui fielmente (mesmo texto legal, mesma
  // estrutura visual), só adaptando OUTORGANTE -> CONTRATANTE/CONTRATADO.
  //
  // Espaçamento (mb-4/py-3/mb-2/mb-1) e vão de assinatura (1,6cm, via CSS
  // `.signature-spacer-top` em legal-print.css) são canônicos e idênticos
  // nos 5 documentos de fechamento — não são valores arbitrários. Ver
  // CONSTITUICAO_REGRAS.SH Art. 384-386 antes de alterar.
  const renderContractExecutionClosing = () => (
    <>
      <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-[10.5pt] leading-6 text-amber-950 text-justify mb-4">
        <strong>
          Nota de Validade Híbrida (art. 654, § 1º, da Lei n. 10.406/2002 —
          Código Civil Brasileiro):
        </strong>{" "}
        Na hipótese de assinatura eletrônica remota, o local de emissão, a
        data e a hora oficiais do ato coincidem de forma inalterável com os
        dados de geolocalização, IP e Carimbo do Tempo registrados no
        relatório de auditoria da plataforma certificadora anexa, suprindo
        plenamente o preenchimento manual dos campos. Em caso de assinatura
        física presencial, inclusive no interior de estabelecimentos
        prisionais ou hospitalares, o local exato e a data deverão ser
        preenchidos de próprio punho pela parte <strong>CONTRATANTE</strong>{" "}
        ou pela parte <strong>CONTRATADA</strong> nos campos abaixo.
      </div>

      <div className="mb-4 rounded-xl border border-dashed border-stone-300 bg-white px-4 py-3">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500 mb-2">
          Local e Data de Emissão
        </p>
        <p className="text-[10.5pt] italic leading-6 text-stone-400 mb-1">
          (apenas para assinatura física presencial)
        </p>
        <p className="text-[11.5pt] leading-7 text-gray-800 break-words">
          <span className="inline-block min-w-[220px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>
          ,{" "}
          <span className="inline-block min-w-[42px] text-center border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-block min-w-[170px] border-b border-stone-400 align-baseline">
            &nbsp;
          </span>{" "}
          de{" "}
          <span className="inline-flex min-w-[76px] border-b border-stone-400 align-baseline justify-start">
            <span className="pl-1">20</span>
          </span>
          .
        </p>
      </div>
    </>
  );

  const renderContractSignatures = () => {
    const namedParties = (
      Array.isArray(form?.parties) ? form.parties : []
    ).filter((p) => String(p?.name || "").trim());
    const signerContratantes = (
      namedParties.length > 0 ? namedParties : [{}]
    ).map((p) => p?.qualificationData || p || {});

    const signerContratados = [
      officeQualificationClient || officeData || DEFAULT_OWNER_DATA,
      ...(partnerData?.active && String(partnerData?.name || "").trim()
        ? [partnerData?.qualificationData || partnerData]
        : []),
      ...(Array.isArray(partnerExtras) ? partnerExtras : [])
        .filter((p) => p?.active && String(p?.name || "").trim())
        .map((p) => p?.qualificationData || p || {}),
    ];

    const renderSignatureColumn = (clientData, roleLabel, key) => {
      const signature = buildSignatureLine(clientData);
      const subtitle = String(signature?.subtitle || "").trim();
      return (
        <div key={key} className="text-center border-t border-black pt-2">
          <span className="font-bold">{signature?.nameLine}</span>
          <br />
          <span className="text-[11px]">
            {signature?.cpfLine} — {String(roleLabel || "").toUpperCase()}
          </span>
          {subtitle ? (
            <>
              <br />
              <span className="text-[11px]">{subtitle}</span>
            </>
          ) : null}
        </div>
      );
    };

    return (
      <div className="grid grid-cols-2 gap-10">
        <div className="flex flex-col gap-10">
          {signerContratantes.map((clientData, idx) =>
            renderSignatureColumn(
              clientData,
              signerContratantes.length > 1
                ? `Contratante ${idx + 1}`
                : "Contratante",
              `contratante-${idx}`,
            ),
          )}
        </div>
        <div className="flex flex-col gap-10">
          {signerContratados.map((clientData, idx) =>
            renderSignatureColumn(
              clientData,
              idx === 0 ? "Contratado" : "Sócio / Parceiro",
              `contratado-${idx}`,
            ),
          )}
        </div>
      </div>
    );
  };

  const renderPreamble = () =>
    (() => {
      const preambuloBlock =
        (templateData?.structure || []).find(
          (b) => String(b?.id || "").trim() === "preambulo",
        ) || null;
      const html =
        String(preambuloBlock?.content || "").trim() ||
        PREAMBULO_DEFAULT_CONTENT;

      return (
        <ClauseWrapper
          title="PREÂMBULO"
          id="PREAMBULO"
          isPreamble
          onEdit={() =>
            startEditingSpecial(
              "preambulo",
              "PREÂMBULO",
              PREAMBULO_DEFAULT_CONTENT,
            )
          }
        >
          {editingBlockId === "preambulo" ? (
            <div className="my-4 bg-white border-2 border-highlight-action shadow-2xl rounded-lg overflow-hidden z-50 relative animate-fade-in-up">
              <div className="bg-gradient-to-r from-orange-50 to-white p-3 border-b border-highlight-action/30 flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-highlight-action" />
                  <div className="font-bold text-brand-blue text-sm">
                    PREÂMBULO
                  </div>
                </div>
                <RulesPanel
                  blockData={activeBlockData}
                  onChange={setActiveBlockData}
                  taxonomy={TAXONOMY}
                />
              </div>

              <ContractEditor
                key={editingBlockId}
                initialData={activeBlockData}
                onSave={saveEditorContent}
                isSaving={isSaving}
                onCancel={() => {
                  setEditingBlockId(null);
                  setActiveBlockData(null);
                }}
                structure={templateData?.structure || []}
              />
            </div>
          ) : (
            <div
              className="contract-p clause-body"
              dangerouslySetInnerHTML={{ __html: parseText(html) }}
            />
          )}
        </ClauseWrapper>
      );
    })();

  const [tempCustomObj, setTempCustomObj] = useState("");
  const [tempCustomHon, setTempCustomHon] = useState("");

  const masterStructureRef = useRef(null);

  const buildCloudAutosavePayload = () => ({
    templateData,
    form,
    partnerData,
    partnerExtras,
  });

  const applyPublishedDocumentForEdit = useCallback((publishedData) => {
    publishedEditLockRef.current = true;
    activeDocumentLockRef.current = true;
    const contentData =
      publishedData?.contentData &&
      typeof publishedData.contentData === "object"
        ? publishedData.contentData
        : {};
    const repairedTemplateData = repairMojibakeDeep(contentData?.templateData);
    const repairedForm = repairMojibakeDeep(contentData?.form);
    const repairedPartnerData = repairMojibakeDeep(contentData?.partnerData);
    const repairedPartnerExtras = repairMojibakeDeep(
      Array.isArray(contentData?.partnerExtras)
        ? contentData.partnerExtras
        : [],
    );
    const normalizedPartnerData = repairedPartnerData
      ? { ...structuredClone(DEFAULT_PARTNER), ...repairedPartnerData }
      : structuredClone(DEFAULT_PARTNER);
    const normalizedPartnerExtras = Array.isArray(repairedPartnerExtras)
      ? repairedPartnerExtras
      : [];

    if (!repairedTemplateData?.structure || !repairedForm) {
      throw new Error(
        "O documento publicado não possui snapshot editável completo para o Contract Engine.",
      );
    }

    if (
      containsIrrecoverableCorruption(repairedTemplateData) ||
      containsIrrecoverableCorruption(repairedForm)
    ) {
      throw new Error(
        "O snapshot editável do contrato publicado está corrompido e não pode ser reaberto com segurança.",
      );
    }

    setTemplateData(repairedTemplateData);
    setForm(repairedForm);
    setPartnerData(normalizedPartnerData);
    setPartnerExtras(normalizedPartnerExtras);
    setEditingBlockId(null);
    setActiveBlockData(null);
    setExpandedInactiveId(null);
    setDataSource("published_document");
    setIsModified(false);
    markSaved({
      updatedAt:
        publishedData?.updatedAt ||
        publishedData?.publication?.publishedAtIso ||
        null,
      updatedBy:
        publishedData?.updatedBy ||
        publishedData?.publication?.publishedBy ||
        publishedData?.createdBy ||
        null,
      source: "published_document",
      error: null,
    });
    lastCloudAutosavePayloadRef.current = JSON.stringify({
      templateData: repairedTemplateData,
      form: repairedForm,
      partnerData: normalizedPartnerData,
      partnerExtras: normalizedPartnerExtras,
    });
    setEditingPublicationMeta({
      documentId: String(publishedData?.id || "").trim(),
      publicationSeriesId: String(
        publishedData?.publicationSeriesId || publishedData?.id || "",
      ).trim(),
      publicationVersion: Math.max(
        1,
        Number(publishedData?.publicationVersion || 1) || 1,
      ),
    });
  }, []);

  const loadPublishedDocumentForEdit = useCallback(
    async (requestLike) => {
      const documentId = String(requestLike?.documentId || "").trim();
      if (!documentId) return false;

      const loaded = await readLatestPublishedDocumentInSeries({
        documentId,
        publicationSeriesId: String(
          requestLike?.publicationSeriesId || "",
        ).trim(),
      });
      const publishedData = loaded?.data || null;
      if (!publishedData) {
        throw new Error("Documento publicado não localizado para reedição.");
      }

      const moduleName = String(
        publishedData?.module || publishedData?.context?.module || "",
      )
        .trim()
        .toLowerCase();
      const typeName = String(
        publishedData?.type || publishedData?.contentData?.documentType || "",
      )
        .trim()
        .toUpperCase();
      if (moduleName && moduleName !== "contracts" && typeName !== "CONTRACT") {
        throw new Error(
          "O documento selecionado não pertence ao módulo de contratos.",
        );
      }

      applyPublishedDocumentForEdit({
        id: loaded?.id || documentId,
        ...publishedData,
      });
      clearQueuedPublishedDocumentEdit("contracts");
      return true;
    },
    [applyPublishedDocumentForEdit],
  );

  const handleDownloadCloudMaster = async () => {
    try {
      if (!appId) throw new Error("ProjectId do Firebase não encontrado.");
      const data = await getContractMaster(appId);
      if (!data) throw new Error("Mestre da nuvem não encontrado.");
      // getContractMaster já retorna { id, ...docData } — data.id existe,
      // não precisa (e não tem como, "snap" nunca existiu neste escopo)
      // reconstruir o id separadamente.
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = "contract_master_cloud.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
    } catch (e) {
      setDialogState({
        open: true,
        title: "Falha ao baixar mestre",
        message: String(
          e?.message || e || "Não foi possível baixar o mestre da nuvem.",
        ),
        mode: "alert",
        onConfirm: null,
      });
    }
  };

  const handleForceSyncCloud = async () => {
    try {
      if (!appId) throw new Error("ProjectId do Firebase não encontrado.");
      const data = await fetchContractMasterV8();
      if (!data) throw new Error("Mestre da nuvem não encontrado.");
      const nextStructure = Array.isArray(data?.structure)
        ? data.structure
        : null;
      if (!nextStructure || nextStructure.length === 0)
        throw new Error("Estrutura vazia na nuvem.");

      localStorage.removeItem(DRAFT_KEY);

      masterStructureRef.current = nextStructure;
      setTemplateData({ structure: nextStructure });
      setIsModified(false);
      setDataSource("cloud_master");
      markSaved();
      setEditingBlockId(null);
      setActiveBlockData(null);

      setDialogState({
        open: true,
        title: "Sincronização concluída",
        message: `✅ Template sincronizado com a nuvem!\n\n${nextStructure.length} blocos carregados do master_v8.\nCache local limpo com sucesso.`,
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      setDialogState({
        open: true,
        title: "Falha na sincronização",
        message: String(
          e?.message || e || "Não foi possível sincronizar com a nuvem.",
        ),
        mode: "alert",
        onConfirm: null,
      });
    }
  };

  const cleanClauseTitle = (title) => {
    const t = String(title || "").trim();
    return t.replace(/^\d+(?:\.\d+)*\s*[-–—]\s*/g, "");
  };

  const handleResetDraft = () => {
    const message =
      "VOCÊ TEM CERTEZA QUE QUER FAZER ISSO?\n\nEssa ação apagará o rascunho local (draft) e restaurará o contrato para o estado de fábrica.\n\nEsta alteração não poderá ser desfeita.";
    setDialogState({
      open: true,
      title: "Confirmar Reset",
      message,
      mode: "confirm",
      onConfirm: () => {
        localStorage.removeItem("rda_contract_draft_v23_enc");
        const masterStructure = Array.isArray(masterStructureRef.current)
          ? masterStructureRef.current
          : null;
        const fallback =
          ContractData.CANONICAL_CLAUSE_LIBRARY ||
          ContractData.CLAUSE_LIBRARY ||
          [];
        setTemplateData({ structure: masterStructure || fallback });
        setForm(structuredClone(DEFAULT_FORM));
        setPartnerData(structuredClone(DEFAULT_PARTNER));
        setPartnerExtras([]);
        setIsModified(false);
        setEditingBlockId(null);
        setActiveBlockData(null);
        setExpandedInactiveId(null);
        setMode("producao");
        setDataSource(masterStructure ? "cloud_master" : "local_canonical");
      },
    });
  };

  // --- FUNÇÃO DE BACKUP LOCAL (COLD STORAGE) ---
  const handleDownloadBackup = () => {
    try {
      const structureToSave = templateData?.structure || [];
      if (structureToSave.length === 0) {
        alert(
          "O contrato está vazio ou ainda carregando. Nada para fazer backup.",
        );
        return;
      }

      const payload = JSON.stringify(structureToSave, null, 2);
      const blob = new Blob([payload], { type: "application/json" });
      const url = URL.createObjectURL(blob);

      const dateStr = new Date().toISOString().split("T")[0];
      const a = document.createElement("a");
      a.href = url;
      a.download = `RDA_Contract_Backup_${dateStr}.json`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setDialogState({
        open: true,
        title: "Backup Concluído",
        message:
          "✅ SUCESSO!\n\nUma cópia de segurança do contrato foi baixada para o seu computador (pasta Downloads).\n\nVocê está seguro para realizar qualquer operação de sistema.",
        mode: "info",
        onConfirm: null,
      });
    } catch (e) {
      console.error("Erro ao gerar backup:", e);
      alert("Erro ao tentar gerar o arquivo de backup.");
    }
  };

  // --- LÓGICA DE EXPORTAÇÃO MARKDOWN (V3.0 - SIMBIOSE PROCURAÇÃO ↔ CONTRATO) ---
  const handleExportMD = () => {
    if (!templateData?.structure) return alert("Nenhuma estrutura carregada.");

    const htmlToMarkdown = (html) => {
      let text = String(html || "");
      // Strip variable-highlight wrapper spans injected by parseText
      text = text.replace(
        /<span[^>]*class="[^"]*var-highlight[^"]*"[^>]*>([\s\S]*?)<\/span>/gi,
        "`$1`",
      );
      text = text.replace(/<strong>(.*?)<\/strong>/gi, "**$1**");
      text = text.replace(/<b>(.*?)<\/b>/gi, "**$1**");
      text = text.replace(/<em>(.*?)<\/em>/gi, "*$1*");
      text = text.replace(/<i>(.*?)<\/i>/gi, "*$1*");
      text = text.replace(/<br\s*[\/]?>/gi, "\n");
      text = text.replace(/<\/p>\s*<p>/gi, "\n\n");
      text = text.replace(/<\/?p>/gi, "");
      text = text.replace(/<ul>/gi, "\n");
      text = text.replace(/<\/ul>/gi, "\n");
      text = text.replace(/<li>/gi, "- ");
      text = text.replace(/<\/li>/gi, "\n");
      text = text.replace(/<[^>]*>?/gm, "");
      text = text.replace(/&nbsp;/g, " ");
      return text.trim();
    };

    // --- FICHA RESUMO (espelhando Procuração) ---
    const parties = Array.isArray(form?.parties) ? form.parties : [];
    const primaryName = String(parties[0]?.name || "CONTRATANTE").toUpperCase();
    const ramoDisplay =
      String(
        form?.isCustomRamo ? form?.customRamo : form?.ramoDireito || "",
      ).trim() || "—";
    const posicaoDisplay =
      String(
        form?.isCustomPosicao
          ? form?.customPosicao
          : form?.posicaoCliente || "",
      ).trim() || "—";
    const objetoDisplay =
      String(form?.objetoPrincipal || "").trim() &&
      String(form?.objetoPrincipal || "").trim() !== "Descreva o objeto..."
        ? String(form.objetoPrincipal).trim()
        : "—";
    const valorHonDisplay = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(form?.valorHonorarios || 0));
    const valorCausaDisplay = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(Number(form?.valorCausa || 0));
    const parteContrariaDisplay =
      String(form?.parteContrariaName || "").trim() || "—";
    const honorariosDisplay =
      Array.isArray(form?.selectedHonorarios) &&
      form.selectedHonorarios.length > 0
        ? form.selectedHonorarios.join(", ")
        : "—";

    let md = `# CONTRATO DE HONORÁRIOS — REVISÃO COMPLETA (RDA SYSTEM)\n\n`;
    md += `> **Data da exportação:** ${new Date().toLocaleDateString("pt-BR", { day: "numeric", month: "long", year: "numeric" })} às ${new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}\n\n`;
    md += `## FICHA RESUMO\n`;
    md += `| Campo | Valor |\n|---|---|\n`;
    md += `| **Contratante(s)** | ${primaryName} |\n`;
    md += `| **Ramo do Direito** | ${ramoDisplay} |\n`;
    md += `| **Posição do Cliente** | ${posicaoDisplay} |\n`;
    md += `| **Objeto Principal** | ${objetoDisplay} |\n`;
    md += `| **Parte Contrária** | ${parteContrariaDisplay} |\n`;
    md += `| **Valor da Causa** | ${valorCausaDisplay} |\n`;
    md += `| **Honorários** | ${valorHonDisplay} |\n`;
    md += `| **Modalidade** | ${honorariosDisplay} |\n\n`;
    md += `---\n\n`;

    // --- CORPO DO DOCUMENTO (com parseText + visibilidade) ---
    const structure = templateData.structure || [];
    let clauseCounter = 0;

    structure.forEach((block) => {
      if (!block) return;
      const isPreambulo =
        block?.id === "preambulo" ||
        String(block?.title || "")
          .toUpperCase()
          .includes("PREÂMBULO");

      // Filtrar blocos invisíveis (mesma lógica do modo produção)
      if (!isPreambulo && !validateBlock(block)) return;

      // Numeração de cláusula
      const isNavigable = isNavigableClause(block);
      if (isNavigable) clauseCounter += 1;
      const clauseLabel = isNavigable ? `CLÁUSULA ${clauseCounter}ª - ` : "";

      md += `## ${clauseLabel}${cleanClauseTitle(block.title) || block.title}\n`;

      if (Array.isArray(block.children) && block.children.length > 0) {
        block.children.forEach((child, cIdx) => {
          // Filtrar children invisíveis
          if (!isChildVisibleInHierarchy(block.children, cIdx)) return;

          const resolvedContent = parseText(child.content);
          const mdContent = htmlToMarkdown(resolvedContent);
          if (mdContent) {
            md += `- ${mdContent}\n\n`;
          }
        });
      } else {
        const resolvedContent = parseText(block.content);
        const mdContent = htmlToMarkdown(resolvedContent);
        if (mdContent) {
          md += `${mdContent}\n\n`;
        }
      }
      md += `---\n\n`;
    });

    const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Revisao_Contrato_RDA_${new Date().toISOString().split("T")[0]}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const buildFriendlyContractName = () => {
    const sourceParties = Array.isArray(form?.parties) ? form.parties : [];
    const primaryParty = sourceParties[0] || {};
    return generateDocumentFileName("Contrato", primaryParty, {
      mainPartyName: String(primaryParty?.name || "").trim(),
      ramoDireito: String(form?.ramoDireito || "").trim(),
    });
  };

  const handleImportMD = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".md,text/markdown";

    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const md = String(event.target.result || "");
          const structure = Array.isArray(templateData?.structure)
            ? templateData.structure
            : [];
          if (structure.length === 0)
            throw new Error(
              "Estrutura vazia. Carregue um contrato antes de importar.",
            );

          const markdownToHtml = (raw) => {
            const src = String(raw || "")
              .replace(/\r\n/g, "\n")
              .trim();
            if (!src) return "";

            const lines = src.split("\n");
            const htmlParts = [];
            let i = 0;
            while (i < lines.length) {
              const line = lines[i];
              if (/^\s*-\s+/.test(line)) {
                const items = [];
                while (i < lines.length && /^\s*-\s+/.test(lines[i])) {
                  items.push(lines[i].replace(/^\s*-\s+/, "").trim());
                  i += 1;
                }
                const li = items
                  .map((t) => {
                    let x = t;
                    x = x.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
                    x = x.replace(/\*([^*]+)\*/g, "<em>$1</em>");
                    return `<li>${x}</li>`;
                  })
                  .join("");
                htmlParts.push(`<ul>${li}</ul>`);
                continue;
              }

              // parágrafo (agrupar até linha em branco)
              const paraLines = [];
              while (i < lines.length && String(lines[i] || "").trim() !== "") {
                paraLines.push(lines[i]);
                i += 1;
              }
              // pular linhas em branco
              while (i < lines.length && String(lines[i] || "").trim() === "")
                i += 1;

              if (paraLines.length > 0) {
                let text = paraLines.join("\n");
                text = text.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
                text = text.replace(/\*([^*]+)\*/g, "<em>$1</em>");
                text = text.replace(/\n/g, "<br>");
                htmlParts.push(`<p>${text}</p>`);
              } else {
                i += 1;
              }
            }

            return htmlParts.join("");
          };

          const stripMetaLines = (raw) => {
            return String(raw || "")
              .split(/\r?\n/g)
              .filter((l) => {
                const s = String(l || "").trim();
                if (!s) return true;
                if (s.startsWith(">")) return false;
                if (s === "---") return false;
                if (s.startsWith("#")) return false;
                return true;
              })
              .join("\n")
              .trim();
          };

          const blocksById = new Map();
          const childById = new Map();
          structure.forEach((b) => {
            if (!b) return;
            blocksById.set(String(b.id), b);
            (Array.isArray(b.children) ? b.children : []).forEach((c) => {
              if (!c) return;
              childById.set(String(c.id), c);
            });
          });

          // Parse por headers ## [ID: X] e ### Sub-Item ... [ID: Y]
          const lines = md.replace(/\r\n/g, "\n").split("\n");
          let currentBlockId = null;
          let currentChildId = null;
          const buffer = [];

          const flush = () => {
            const raw = buffer.join("\n").trim();
            buffer.length = 0;
            if (!raw) return;
            const cleaned = stripMetaLines(raw);
            const html = markdownToHtml(cleaned);
            if (currentChildId && childById.has(currentChildId)) {
              childById.get(currentChildId).content = html;
              return;
            }
            if (currentBlockId && blocksById.has(currentBlockId)) {
              const b = blocksById.get(currentBlockId);
              if (!Array.isArray(b.children) || b.children.length === 0)
                b.content = html;
            }
          };

          for (const line of lines) {
            const blockMatch = line.match(/^##\s+\[ID:\s*([^\]]+)\]\s*-/);
            const childMatch = line.match(
              /^###\s+Sub-Item\s+\d+\s+\[ID:\s*([^\]]+)\]/,
            );
            if (blockMatch) {
              flush();
              currentBlockId = String(blockMatch[1] || "").trim();
              currentChildId = null;
              continue;
            }
            if (childMatch) {
              flush();
              currentChildId = String(childMatch[1] || "").trim();
              continue;
            }
            buffer.push(line);
          }
          flush();

          setTemplateData({ structure: [...structure] });
          setIsModified(true);
          setDataSource("localStorage");

          setDialogState({
            open: true,
            title: "Importação Concluída",
            message:
              "✅ SUCESSO!\n\nA revisão (.MD) foi importada e aplicada ao contrato que está na tela.\n\nRevise visualmente e, se desejar, publique o Mestre.",
            mode: "info",
            onConfirm: null,
          });
        } catch (err) {
          console.error("Erro ao importar revisão (.MD):", err);
          alert(
            "Erro ao importar revisão (.MD): " + (err?.message || String(err)),
          );
        }
      };
      reader.readAsText(file);
    };

    input.click();
  };

  const suggestedOABTotal = useMemo(() => {
    const allObjects = Object.values(TAXONOMY).flatMap((t) =>
      Array.isArray(t?.objects) ? t.objects : [],
    );
    return (
      Array.isArray(form.selectedObjetos) ? form.selectedObjetos : []
    ).reduce((acc, objId) => {
      const found = allObjects.find((o) => o.id === objId);
      return acc + (Number(found?.suggestedFee || 0) || 0);
    }, 0);
  }, [form.selectedObjetos]);

  // --- FUNÇÃO DE RESTAURAÇÃO AUTOMÁTICA (UPLOAD JSON) ---
  const handleRestoreBackup = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json";

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const restoredStructure = JSON.parse(event.target.result);

          if (!Array.isArray(restoredStructure)) {
            throw new Error(
              "O arquivo não contém uma estrutura válida de contrato.",
            );
          }

          setTemplateData({ structure: restoredStructure });
          setIsModified(true);
          setDataSource("localStorage"); // Indica que estamos com dados modificados

          setDialogState({
            open: true,
            title: "Restauração Concluída",
            message:
              ' SUCESSO!\n\nO contrato foi restaurado a partir do backup.\n\nO sistema está em modo de edição com os dados recuperados.\n\nUse "Publicar Mestre" para salvar na nuvem se desejar.',
            mode: "info",
            onConfirm: null,
          });
        } catch (error) {
          console.error("Erro ao restaurar backup:", error);
          alert(
            "Erro ao ler o arquivo de backup. Verifique se é um JSON válido.",
          );
        }
      };

      reader.readAsText(file);
    };

    input.click();
  };

  // --- FUNÇÃO ADMIN NUCLEAR: GRAVAR MESTRE + HARD RESET ---
  const handleSaveAsMaster = async () => {
    const message =
      " OPERAÇÃO LÁZARO (ADMIN) \n\n" +
      "ATENÇÃO: Esta ação vai sobrescrever o Mestre de Contratos na nuvem com o que está na tela.\n\n" +
      "Isso vai recuperar as cláusulas originais.\n\n" +
      "Depois, o sistema fará um Hard Reset e recarregará.\n" +
      "Continuar?";

    setDialogState({
      open: true,
      title: "Operação Lázaro (Restauração)",
      message,
      mode: "confirm",
      onConfirm: async () => {
        setIsSaving(true);
        try {
          // 1. UPLOAD PARA A NUVEM (A Nova Verdade)
          const latestData = await import(
            /* @vite-ignore */ "./ContractData.js?v=" + Date.now()
          );
          const cleanStructure =
            latestData.CANONICAL_CLAUSE_LIBRARY ||
            latestData.CLAUSE_LIBRARY ||
            [];

          const payload = {
            structure: cleanStructure,
            updatedAt: serverTimestamp(),
            updatedBy: "Admin (Lázaro Restore)",
            version: `v${Date.now()}-LAZARO`,
          };

          await saveContractMasterV8(payload);

          // 2. EXTERMÍNIO LOCAL (Kill Ghosts)

          // A) Matar Rascunho Local
          try {
            localStorage.removeItem("rda_contract_draft_v23_enc");
          } catch {
            /* ignore */
          }

          // B) Matar Storage
          try {
            localStorage.clear();
          } catch {
            /* ignore */
          }
          try {
            sessionStorage.clear();
          } catch {
            /* ignore */
          }

          // C) Matar Service Workers (Cache Zumbi)
          if ("serviceWorker" in navigator) {
            const registrations =
              await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
              await registration.unregister();
            }
          }

          // D) Matar Cache Storage (Arquivos Velhos)
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }

          setDialogState({
            open: true,
            title: "Sucesso",
            message:
              " SUCESSO! O sistema foi atualizado e limpo.\n\nA página será recarregada agora.",
            mode: "info",
            onConfirm: () => {
              window.location.reload();
            },
          });
        } catch (e) {
          console.error(e);
          setDialogState({
            open: true,
            title: "Erro na Operação Nuclear",
            message: "Erro na Operação Nuclear: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
          setIsSaving(false);
        }
      },
    });
  };

  // --- CARREGAMENTO ---
  useEffect(() => {
    const load = async () => {
      const queuedEditRequest = readQueuedPublishedDocumentEdit("contracts");
      if (queuedEditRequest?.documentId) {
        setDialogState({
          open: false,
          title: "",
          message: "",
          mode: "confirm",
          onConfirm: null,
          onCancel: null,
        });
        try {
          const loadedFromPublication =
            await loadPublishedDocumentForEdit(queuedEditRequest);
          if (loadedFromPublication) return;
        } catch (e) {
          clearQueuedPublishedDocumentEdit("contracts");
          publishedEditLockRef.current = false;
          activeDocumentLockRef.current = false;
          setEditingPublicationMeta(null);
          setDialogState({
            open: true,
            title: "Erro ao reabrir contrato publicado",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
            onCancel: null,
          });
        }
      }

      try {
        if (publishedEditLockRef.current) return;

        let pendingLocalEnc = null;
        try {
          pendingLocalEnc = localStorage.getItem(DRAFT_KEY);
          if (pendingLocalEnc) localStorage.removeItem(DRAFT_KEY);
        } catch {
          pendingLocalEnc = null;
        }

        let cloudDraft = null;
        try {
          if (appId) {
            cloudDraft = await fetchContractAutosave();
            if (cloudDraft) {
              const data = cloudDraft || {};
              if (data?.templateData?.structure && data?.form)
                cloudDraft = data;
            }
          }
        } catch {
          cloudDraft = null;
        }

        if (!draftPromptedRef.current && (cloudDraft || pendingLocalEnc)) {
          draftPromptedRef.current = true;
          setDialogState({
            open: true,
            title: "Rascunho Encontrado",
            message:
              'Detectamos um contrato que estava sendo editado anteriormente.\n\nDeseja restaurar este rascunho para continuar o trabalho?\n\nSe clicar em "Cancelar", você iniciará um documento em branco limpo.',
            mode: "confirm",
            onConfirm: async () => {
              try {
                if (cloudDraft) {
                  const repairedTemplateData = repairMojibakeDeep(
                    cloudDraft.templateData,
                  );
                  const repairedForm = repairMojibakeDeep(cloudDraft.form);
                  const repairedPartnerData = repairMojibakeDeep(
                    cloudDraft.partnerData,
                  );
                  const repairedPartnerExtras = repairMojibakeDeep(
                    Array.isArray(cloudDraft.partnerExtras)
                      ? cloudDraft.partnerExtras
                      : [],
                  );

                  if (
                    !containsIrrecoverableCorruption(repairedTemplateData) &&
                    !containsIrrecoverableCorruption(repairedForm)
                  ) {
                    activeDocumentLockRef.current = true;
                    setTemplateData(repairedTemplateData);
                    setForm(repairedForm);
                    setPartnerData(
                      repairedPartnerData
                        ? {
                            ...structuredClone(DEFAULT_PARTNER),
                            ...repairedPartnerData,
                          }
                        : structuredClone(DEFAULT_PARTNER),
                    );
                    setPartnerExtras(
                      Array.isArray(repairedPartnerExtras)
                        ? repairedPartnerExtras
                        : [],
                    );
                    setDataSource("cloud_autosave");
                    setIsModified(false);
                    lastCloudAutosavePayloadRef.current = JSON.stringify({
                      templateData: repairedTemplateData,
                      form: repairedForm,
                      partnerData: repairedPartnerData
                        ? {
                            ...structuredClone(DEFAULT_PARTNER),
                            ...repairedPartnerData,
                          }
                        : structuredClone(DEFAULT_PARTNER),
                      partnerExtras: Array.isArray(repairedPartnerExtras)
                        ? repairedPartnerExtras
                        : [],
                    });
                    markSaved({
                      updatedAt: cloudDraft?.updatedAt || null,
                      updatedBy: cloudDraft?.updatedBy || null,
                      source: "cloud_autosave",
                      error: null,
                    });
                    return;
                  }
                }

                if (pendingLocalEnc) {
                  const dec = await CryptoShield.decrypt(pendingLocalEnc);
                  if (dec) {
                    const repairedTemplate = repairMojibakeDeep(dec.template);
                    const repairedForm = repairMojibakeDeep(dec.form);
                    const repairedPartnerData = repairMojibakeDeep(
                      dec.partnerData,
                    );
                    const repairedPartnerExtras = repairMojibakeDeep(
                      Array.isArray(dec.partnerExtras) ? dec.partnerExtras : [],
                    );

                    if (
                      containsIrrecoverableCorruption(repairedTemplate) ||
                      containsIrrecoverableCorruption(repairedForm)
                    ) {
                      return;
                    }

                    activeDocumentLockRef.current = true;
                    setTemplateData(repairedTemplate);
                    setForm(repairedForm);
                    setPartnerData(
                      repairedPartnerData
                        ? {
                            ...structuredClone(DEFAULT_PARTNER),
                            ...repairedPartnerData,
                          }
                        : structuredClone(DEFAULT_PARTNER),
                    );
                    setPartnerExtras(
                      Array.isArray(repairedPartnerExtras)
                        ? repairedPartnerExtras
                        : [],
                    );
                    setDataSource("draft_local");
                    setIsModified(true);
                  }
                }
              } catch (e) {
                console.error("Erro ao restaurar draft:", e);
              }
            },
            onCancel: () => {
              try {
                localStorage.removeItem(DRAFT_KEY);
              } catch {
                /* ignore */
              }
            },
          });
        }
      } catch {}

      try {
        if (!appId) throw new Error("No ID");
        subscribeContractMasterV8((_freshMasterData) => {
          if (activeDocumentLockRef.current) return;
          if (!localStorage.getItem(DRAFT_KEY) && _freshMasterData) {
            const data = _freshMasterData || {};
            const nextStructure = Array.isArray(data?.structure)
              ? repairMojibakeDeep(data.structure)
              : ContractData.CANONICAL_CLAUSE_LIBRARY ||
                ContractData.CLAUSE_LIBRARY ||
                [];

            masterStructureRef.current = Array.isArray(nextStructure)
              ? nextStructure
              : null;

            if (import.meta.env.DEV) {
              console.warn("[Master Template] Loaded from Firestore", {
                id: data?.id || MASTER_PATH.join("/"),
                blocks: Array.isArray(nextStructure) ? nextStructure.length : 0,
                version: data?.version,
                updatedBy: data?.updatedBy,
              });
            }

            setTemplateData({ structure: nextStructure });
            setDataSource("cloud_master");
            markSaved({
              updatedAt: new Date().toISOString(),
              updatedBy: data?.updatedBy || email || null,
              source: "cloud_master",
              error: null,
            });
            lastCloudAutosavePayloadRef.current = JSON.stringify(
              buildCloudAutosavePayload(),
            );
          } else if (_freshMasterData) {
            const data = _freshMasterData || {};
            const nextStructure = Array.isArray(data?.structure)
              ? repairMojibakeDeep(data.structure)
              : null;
            masterStructureRef.current = nextStructure;
          } else {
            setTemplateData(
              repairMojibakeDeep({
                structure:
                  ContractData.CANONICAL_CLAUSE_LIBRARY ||
                  ContractData.CLAUSE_LIBRARY ||
                  [],
              }),
            );
            setDataSource("local");
          }
        });
      } catch (e) {
        setTemplateData(
          repairMojibakeDeep({
            structure:
              ContractData.CANONICAL_CLAUSE_LIBRARY ||
              ContractData.CLAUSE_LIBRARY ||
              [],
          }),
        );
        setDataSource("local");
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const detail =
        event?.detail && typeof event.detail === "object" ? event.detail : {};
      if (String(detail?.tab || "").trim() !== "contracts") return;
      if (detail?.documentId) {
        setDialogState({
          open: false,
          title: "",
          message: "",
          mode: "confirm",
          onConfirm: null,
          onCancel: null,
        });
      }
      void loadPublishedDocumentForEdit(detail).catch((e) => {
        clearQueuedPublishedDocumentEdit("contracts");
        publishedEditLockRef.current = false;
        activeDocumentLockRef.current = false;
        setEditingPublicationMeta(null);
        setDialogState({
          open: true,
          title: "Erro ao reabrir contrato publicado",
          message: "Erro: " + (e?.message || String(e)),
          mode: "info",
          onConfirm: null,
        });
      });
    };

    browserEnv.addEventListener(publishedDocumentEditEventName, handler);
    return () =>
      window.removeEventListener(publishedDocumentEditEventName, handler);
  }, [loadPublishedDocumentForEdit]);

  useEffect(() => {
    const save = async () => {
      if (templateData && isModified) {
        const enc = await CryptoShield.encrypt({
          template: templateData,
          form: form,
          partnerData: partnerData,
          partnerExtras: partnerExtras,
        });
        if (enc) localStorage.setItem(DRAFT_KEY, enc);
      }
    };
    const t = setTimeout(save, 1000);
    return () => clearTimeout(t);
  }, [templateData, form, partnerData, partnerExtras, isModified]);

  useEffect(() => {
    if (!appId) return undefined;

    const autoSave = async () => {
      if (!isModified) return;
      if (isCloudAutosaveInFlightRef.current) return;

      const payloadObject = buildCloudAutosavePayload();
      const payloadString = JSON.stringify(payloadObject);
      if (
        !payloadString ||
        payloadString === lastCloudAutosavePayloadRef.current
      )
        return;

      isCloudAutosaveInFlightRef.current = true;
      markSaving();

      try {
        await saveContractAutosave({
          ...payloadObject,
          updatedBy: email || null,
        });
        lastCloudAutosavePayloadRef.current = payloadString;
        markSaved({
          updatedAt: new Date().toISOString(),
          updatedBy: email || null,
          source: "cloud_autosave",
          error: null,
        });
      } catch (e) {
        markError(e, "cloud_autosave");
      } finally {
        isCloudAutosaveInFlightRef.current = false;
      }
    };

    const timer = setInterval(() => {
      autoSave();
    }, CLOUD_AUTOSAVE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [templateData, form, partnerData, partnerExtras, isModified]);

  useEscapeKey(() => {
    if (editingBlockId) {
      setEditingBlockId(null);
      setActiveBlockData(null);
    }
  });

  const updateParty = (id, f, v) =>
    setForm((p) => ({
      ...p,
      parties: p.parties.map((x) => (x.id === id ? { ...x, [f]: v } : x)),
    }));
  const addParty = () =>
    setForm((p) => ({
      ...p,
      parties: [
        ...p.parties,
        {
          id: Date.now(),
          name: "",
          cpf: "",
          email: "",
          phone: "",
          address: "",
          nationality: "",
          civilStatus: "",
          profession: "",
          // Dados de Registro
          uf: "PR",
          role: "Contratante",
          clientId: null,
          fullQualification: "",
          gender: "M",
          isPlural: false,
        },
      ],
    }));
  const removeParty = (id) =>
    form.parties.length > 1 &&
    setForm((p) => ({ ...p, parties: p.parties.filter((x) => x.id !== id) }));

  const crmWatchRef = useRef({});

  useEffect(() => {
    const parties = Array.isArray(form?.parties) ? form.parties : [];
    const partyIds = parties
      .map((p) => String(p?.clientId || "").trim())
      .filter(Boolean);

    const linkedIds = (() => {
      const out = [];
      partyIds.forEach((id) => {
        const live = crmClientsById?.[id];
        if (!live) return;
        const anchorId = String(live?.anchor?.id || "").trim();
        if (anchorId) out.push(anchorId);

        const type = String(live?.type || "").trim();
        const q = live?.qualification || {};

        if (type === "Animal Não-Humano") {
          const ref = q?.animal?.responsible || q?.animal?.guardian || null;
          const refId = String(ref?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Menor/Incapaz") {
          const refId = String(q?.minor?.representative?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Pessoa Jurídica") {
          const refId = String(q?.legal?.representative?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Espólio (Falecido)") {
          const refId = String(q?.estate?.inventorier?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Condomínio") {
          const refId = String(q?.condominium?.syndic?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Nascituro (Gestação)") {
          const refId = String(q?.unborn?.representative?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Pessoa Privada de Liberdade") {
          const refId = String(q?.imprisoned?.proxy?.id || "").trim();
          if (refId) out.push(refId);
        }
        if (type === "Massa Falida") {
          const refId = String(q?.bankruptcy?.administrator?.id || "").trim();
          if (refId) out.push(refId);
        }
      });

      return out;
    })();

    const idsToWatch = Array.from(
      new Set([...partyIds, ...linkedIds].filter(Boolean)),
    );
    const current = crmWatchRef.current || {};

    idsToWatch.forEach((id) => {
      if (current[id]) return;
      const unsub = subscribeClientData(id, (clientData) => {
        setCrmClientsById((prev) => ({ ...prev, [id]: clientData }));
      });
      current[id] = unsub;
    });

    Object.keys(current).forEach((id) => {
      if (idsToWatch.includes(id)) return;
      try {
        current[id]?.();
      } catch {
        /* ignore */
      }
      delete current[id];
    });

    crmWatchRef.current = current;

    return () => {};
  }, [form?.parties, crmClientsById]);

  // 🆕 SUBSCRIBE: Cliente atualizado no CRM (rda:client-updated)
  useEffect(() => {
    const handler = async (e) => {
      const { clientId } = e.detail || {};
      if (!clientId) return;

      // Verificar se o cliente atualizado está nas partes do contrato
      const parties = Array.isArray(form?.parties) ? form.parties : [];
      const partyIds = parties
        .map((p) => String(p?.clientId || "").trim())
        .filter(Boolean);
      if (!partyIds.includes(clientId)) return;

      console.log("[ContractEngine] Cliente atualizado pelo CRM:", clientId);

      try {
        // Re-fetch do cliente
        const freshClient = await fetchClientById(clientId);
        if (!freshClient) return;

        // Atualizar cache local crmClientsById
        setCrmClientsById((prev) => ({
          ...(prev || {}),
          [clientId]: freshClient,
        }));

        // Atualizar a parte correspondente no form
        setForm((prev) => {
          if (!prev?.parties) return prev;
          const nextParties = prev.parties.map((p) => {
            if (String(p?.clientId || "").trim() !== clientId) return p;
            return {
              ...p,
              fullQualification: String(
                freshClient?.qualificationText || p?.fullQualification || "",
              ).trim(),
              qualificationData: freshClient,
            };
          });
          return { ...prev, parties: nextParties };
        });
      } catch (err) {
        console.error(
          "[ContractEngine] Erro ao processar atualizacao do CRM:",
          err,
        );
      }
    };

    window.addEventListener("rda:client-updated", handler);
    return () => window.removeEventListener("rda:client-updated", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.parties]);

  const updatePartner = (field, value) =>
    setPartnerData((p) => ({ ...p, [field]: value }));

  const docTitle = String(templateData?.title || "");
  const isProcuration = docTitle.toLowerCase().includes("procuração");

  const fetchClientById = async (clientId) => {
    const id = String(clientId || "").trim();
    if (!id) return null;
    if (!appId) return null;
    try {
      return await fetchClientData(id);
    } catch {
      return null;
    }
  };

  const hydrateDeepClient = async (clientRaw) => {
    if (!clientRaw) return null;
    let hydrated = clientRaw;
    try {
      const canonical = await hydrateAndBuildCanonicalClientQualification(
        clientRaw,
        crmClientsById,
      );
      hydrated = canonical?.client || clientRaw;
    } catch {
      hydrated = clientRaw;
    }

    // Blindagem: Animal precisa ter responsible (Tutor) preenchido para qualificação sair completa.
    try {
      const type = String(clientRaw?.type || "").trim();
      if (type === "Animal Não-Humano") {
        const a = clientRaw?.qualification?.animal || {};
        const hasTutor = Boolean(
          String(a?.responsible?.name || a?.guardian?.name || "").trim(),
        );
        const rep = clientRaw?.representative || null;
        const repName = String(
          rep?.qualification?.physical?.socialName ||
            rep?.qualification?.physical?.name ||
            rep?.name ||
            "",
        ).trim();
        if (!hasTutor && repName) {
          if (!clientRaw.qualification) clientRaw.qualification = {};
          if (!clientRaw.qualification.animal)
            clientRaw.qualification.animal = {};
          clientRaw.qualification.animal.responsible =
            buildRepresentativeFromClient(
              rep,
              clientRaw.qualification.animal.responsible || {},
            );
        }
      }
    } catch {
      // ignore
    }

    return hydrated;
  };

  const ensureDeepHydratedParties = async () => {
    const parties = Array.isArray(form?.parties) ? form.parties : [];
    const ids = parties
      .map((p) => String(p?.clientId || "").trim())
      .filter(Boolean);
    if (ids.length === 0) return;

    setHydratingDeepClients(true);
    try {
      const resolved = await Promise.all(
        ids.map(async (id) => {
          const fromCache = crmClientsById?.[id];
          const base = fromCache || (await fetchClientById(id));
          if (!base) return { id, hydrated: null };
          const hydrated = await hydrateDeepClient(base);
          return { id, hydrated };
        }),
      );

      setCrmClientsById((prev) => {
        const next = { ...(prev || {}) };
        resolved.forEach(({ id, hydrated }) => {
          if (!id || !hydrated) return;
          next[id] = hydrated;
        });
        return next;
      });
    } finally {
      setHydratingDeepClients(false);
    }
  };

  const handleRelinkFromCrm = () => {
    setDialogState({
      open: true,
      title: "Revincular do CRM",
      message:
        "Esta ação não apaga dados do cadastro. O sistema irá recarregar do CRM os clientes vinculados por clientId e substituir snapshots antigos do contrato pelos dados canônicos atuais. Deseja continuar?",
      mode: "confirm",
      onConfirm: async () => {
        await ensureDeepHydratedParties();
        setForm((prev) => {
          const parties = Array.isArray(prev?.parties) ? prev.parties : [];
          const nextParties = parties.map((party) => {
            const linkedId = String(party?.clientId || "").trim();
            const live = linkedId ? crmClientsById?.[linkedId] : null;
            if (!live) return party;
            const addr = live?.location?.address || {};
            const address = [
              addr.street,
              addr.number ? `n. ${addr.number}` : "",
              addr.complement || "",
              addr.neighborhood ? `bairro ${addr.neighborhood}` : "",
              addr.city && addr.state
                ? `${addr.city}/${addr.state}`
                : addr.city || addr.state || "",
              addr.zipCode ? `CEP: ${addr.zipCode}` : "",
            ]
              .filter(Boolean)
              .join(", ");

            return {
              ...party,
              name: String(
                live?.qualification?.physical?.socialName ||
                  live?.qualification?.physical?.name ||
                  live?.qualification?.legal?.companyName ||
                  live?.name ||
                  party?.name ||
                  "",
              ).trim(),
              cpf: String(
                live?.qualification?.physical?.cpf ||
                  live?.qualification?.legal?.cnpj ||
                  live?.cpf ||
                  party?.cpf ||
                  "",
              ).trim(),
              phone: String(
                live?.contact?.phonePrimary ||
                  live?.contact?.phone ||
                  party?.phone ||
                  "",
              ).trim(),
              email: String(live?.contact?.email || party?.email || "").trim(),
              address: address || party?.address || "",
              fullQualification: String(
                buildCanonicalClientQualification(live, crmClientsById)?.text ||
                  party?.fullQualification ||
                  "",
              ).trim(),
              gender:
                live?.qualification?.physical?.grammaticalGender ||
                party?.gender ||
                "M",
            };
          });
          return { ...prev, parties: nextParties };
        });
        setIsModified(true);
      },
    });
  };

  const handleClientImport = async (clientData, targetPartyId) => {
    const name = String(clientData?.name || "").trim();
    const cpf = String(
      clientData?.qualification?.physical?.cpf || clientData?.cpf || "",
    ).trim();
    const phone = String(clientData?.contact?.phonePrimary || "").trim();
    const email = String(clientData?.contact?.email || "").trim();
    const addr = clientData?.location?.address || null;
    const address = addr
      ? [
          addr.street,
          addr.number ? `n. ${addr.number}` : "",
          addr.complement || "",
          addr.neighborhood ? `bairro ${addr.neighborhood}` : "",
          addr.city && addr.state
            ? `${addr.city}/${addr.state}`
            : addr.city || addr.state || "",
          addr.zipCode ? `CEP: ${addr.zipCode}` : "",
        ]
          .filter(Boolean)
          .join(", ")
      : "";
    const importedClientId = String(
      clientData?.id || clientData?.clientId || "",
    ).trim();

    let hydratedClient = clientData;
    let qualText = "";
    try {
      const canonical = await hydrateAndBuildCanonicalClientQualification(
        clientData,
        crmClientsById,
      );
      hydratedClient = canonical?.client || clientData;
      qualText = String(canonical?.text || "").trim();
    } catch {
      hydratedClient = clientData;
      qualText = String(
        buildCanonicalClientQualification(hydratedClient, crmClientsById)
          ?.text || "",
      ).trim();
    }

    setForm((prev) => {
      const parties = Array.isArray(prev?.parties) ? prev.parties : [];
      const newParties = [...parties];
      const idToUse = targetPartyId || parties?.[0]?.id;
      const idx = newParties.findIndex((p) => p.id === idToUse);
      const safeIdx = idx >= 0 ? idx : 0;
      newParties[safeIdx] = {
        ...newParties[safeIdx],
        name,
        cpf,
        phone,
        email,
        address,
        clientId: importedClientId,
        fullQualification: qualText,
        gender:
          hydratedClient?.qualification?.physical?.grammaticalGender ||
          clientData?.qualification?.physical?.grammaticalGender ||
          "M",
      };
      return { ...prev, parties: newParties };
    });

    setIsModified(true);

    if (importedClientId) {
      setCrmClientsById((prev) => {
        const key = String(importedClientId);
        const existing = prev?.[key] || null;
        const next = existing
          ? { ...hydratedClient, ...existing }
          : hydratedClient;
        return { ...prev, [key]: next };
      });
    }

    setShowImportModal(false);
  };

  const handlePartnerImport = (picked, targetIndex = 0) => {
    const raw = picked?.raw || picked || {};
    const name =
      picked?.name ||
      raw.qualification?.physical?.name ||
      raw.qualification?.legal?.companyName ||
      raw.qualification?.minor?.name ||
      "";
    const cpf =
      picked?.cpf ||
      raw.qualification?.physical?.cpf ||
      raw.qualification?.legal?.cnpj ||
      "";
    const address =
      picked?.address || raw.contact?.addressPrimary || raw.address || "";
    const oab =
      picked?.oab ||
      raw.professional?.oab ||
      raw.qualification?.professional?.oab ||
      raw.oab ||
      raw.contact?.oab ||
      "";

    const importedClientId =
      String(
        raw?.id || raw?.clientId || picked?.id || picked?.clientId || "",
      ).trim() || null;

    const applyPartnerImport = async () => {
      let hydratedPartner = raw;
      try {
        const canonical = await hydrateAndBuildCanonicalClientQualification(
          raw,
          crmClientsById,
        );
        hydratedPartner = canonical?.client || raw;
      } catch {
        hydratedPartner = raw;
      }

      if (targetIndex === 0) {
        setPartnerData((prev) => ({
          ...prev,
          active: true,
          name: name || prev.name,
          cpf: cpf || prev.cpf,
          address: address || prev.address,
          oab: oab || prev.oab,
          clientId: importedClientId,
          qualificationData: hydratedPartner,
        }));
      } else {
        setPartnerExtras((prev) => {
          const arr = Array.isArray(prev) ? [...prev] : [];
          const idx = targetIndex - 1;
          while (arr.length <= idx) arr.push({ ...DEFAULT_PARTNER });
          arr[idx] = {
            ...arr[idx],
            active: true,
            name: name || arr[idx].name,
            cpf: cpf || arr[idx].cpf,
            address: address || arr[idx].address,
            oab: oab || arr[idx].oab,
            clientId: importedClientId,
            qualificationData: hydratedPartner,
          };
          return arr;
        });
        setPartnerData((prev) => ({ ...prev, active: true }));
      }

      if (importedClientId) {
        setCrmClientsById((prev) => ({
          ...(prev || {}),
          [importedClientId]: hydratedPartner,
        }));
      }

      setIsModified(true);
      setShowPartnerImportModal(false);
      setOpenSection("partner");
    };

    applyPartnerImport();
  };

  const handleProvisioning = async (data) => {
    try {
      // batch and ledgerRef replaced by addFinancialLedgerEntries

      const clientId = String(form.parties?.[0]?.clientId || "").trim() || null;
      const contractId =
        templateData?.id || editingPublicationMeta?.documentId || null;
      const clientName = form.parties?.[0]?.name || "Cliente";

      const parcelas = generateInstallments(
        data.totalValue,
        data.installments,
        data.firstDueDate,
        {
          description: `Honorários — ${clientName}`,
          category: "Honorários Advocatícios",
          clientId,
          contractId,
        },
      );

      for (const parcela of parcelas) {
        batch.set(doc(ledgerRef), { ...parcela, createdAt: serverTimestamp() });
      }

      await batch.commit();
      setShowProvisionModal(false);
      alert(`✅ ${parcelas.length} parcela(s) lançada(s) no ERP com sucesso!`);
    } catch (e) {
      alert("Erro ao lançar no ERP: " + e.message);
    }
  };

  const toggleList = (listKey, item) =>
    setForm((prev) => {
      const list = prev[listKey] || [];
      return {
        ...prev,
        [listKey]: list.includes(item)
          ? list.filter((i) => i !== item)
          : [...list, item],
      };
    });

  const addCustomItem = (type) => {
    if (type === "OBJ" && tempCustomObj) {
      setForm((prev) => ({
        ...prev,
        customObjetos: [...prev.customObjetos, tempCustomObj],
      }));
      setTempCustomObj("");
    }
    if (type === "HON" && tempCustomHon) {
      setForm((prev) => ({
        ...prev,
        customHonorarios: [...prev.customHonorarios, tempCustomHon],
      }));
      setTempCustomHon("");
    }
  };

  const removeCustomItem = (type, index) => {
    if (type === "OBJ")
      setForm((prev) => ({
        ...prev,
        customObjetos: prev.customObjetos.filter((_, i) => i !== index),
      }));
    if (type === "HON")
      setForm((prev) => ({
        ...prev,
        customHonorarios: prev.customHonorarios.filter((_, i) => i !== index),
      }));
  };

  const getClauseNumberById = (blockId) => {
    const getBlockNumberById = (blockId) => {
      let n = 0;
      for (const b of templateData?.structure || []) {
        const t = String(b?.title || "").toUpperCase();
        if (t.startsWith("CLÁUSULA")) n += 1;
        if (b?.id === blockId) return t.startsWith("CLÁUSULA") ? n : null;
      }
      return null;
    };

    const blockNumber = getBlockNumberById(blockId);
    if (blockNumber) return `Cláusula ${blockNumber}ª`;
    return null;
  };

  const addClause = () => {
    const newBlock = {
      id: `cl_new_${Date.now()}`,
      title: "NOVA CLÁUSULA ADICIONAL",
      type: "text",
      content: "Digite o texto...",
      tags: ["Universal"],
      condition: "ALWAYS",
    };
    setTemplateData((prev) => ({
      ...prev,
      structure: [...prev.structure, newBlock],
    }));
    setIsModified(true);
    setTimeout(
      () =>
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        }),
      100,
    );
  };

  const addClauseAfter = (afterId) => {
    const safeAfterId = String(afterId || "");
    const newBlock = {
      id: `cl_new_${Date.now()}`,
      title: "NOVA CLÁUSULA ADICIONAL",
      type: "text",
      content: "Digite o texto...",
      tags: ["Universal"],
      condition: "ALWAYS",
    };

    setTemplateData((prev) => {
      const structure = Array.isArray(prev?.structure) ? prev.structure : [];
      const idx = structure.findIndex(
        (b) => String(b?.id || "") === safeAfterId,
      );
      if (idx < 0) {
        return { ...prev, structure: [...structure, newBlock] };
      }
      const next = [...structure];
      next.splice(idx + 1, 0, newBlock);
      return { ...prev, structure: next };
    });

    setIsModified(true);
    setTimeout(() => {
      scrollToBlock(newBlock.id);
      startEditing(newBlock);
    }, 100);
  };

  const addSubClauseAfter = (childId, childIndex) => {
    const newChild = {
      id: `sub_${Date.now()}`,
      type: "sub_clause",
      content: "",
      condition: "ALWAYS",
      requiredHonorarios: [],
    };
    setActiveBlockData((prev) => {
      if (!prev || !Array.isArray(prev.children)) return prev;
      const updated = [...prev.children];
      updated.splice(childIndex + 1, 0, newChild);
      return { ...prev, children: updated };
    });
    setIsModified(true);
  };

  const deleteClause = (id) => {
    const targetId = String(id || "");
    if (targetId === "preambulo" || targetId === "__das_partes") {
      setDialogState({
        open: true,
        title: "Atenção",
        message:
          "Este bloco é estrutural e não pode ser excluído. Use Editar para alterar o conteúdo.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setDialogState({
      open: true,
      title: "Confirmar Exclusão",
      message:
        "VOCÊ TEM CERTEZA?\n\nEsta cláusula será removida do contrato.\n\nPara desfazer, será necessário resetar o draft.",
      mode: "confirm",
      onConfirm: () => {
        setTemplateData((prev) => ({
          ...(prev || {}),
          structure: (prev?.structure || []).filter(
            (b) => String(b?.id || "") !== targetId,
          ),
        }));
        setIsModified(true);
      },
    });
  };

  const ensureSpecialBlock = (id, title, defaultContent) => {
    const safeId = String(id);
    const blocks = templateData?.structure || [];
    const existing = blocks.find((b) => String(b?.id || "") === safeId);
    if (existing) {
      try {
        if (safeId === "__das_partes") {
          const oldContent = String(existing?.content || "");
          const looksLegacy =
            oldContent.includes("[[OFFICE_NAME]]") ||
            oldContent.includes("[[CONTRATANTE_NOME]]") ||
            oldContent.includes("[[CONTRATANTE_NATIONALITY]]") ||
            oldContent.includes("[[CONTRATANTE_CIVIL_STATUS]]") ||
            oldContent.includes("[[CONTRATANTE_PROFESSION]]");
          const newPattern = String(defaultContent || "").includes(
            "[[QUALIFICACAO_CONTRATADO]]",
          );

          if (looksLegacy && newPattern) {
            const patched = {
              ...existing,
              content: String(defaultContent || ""),
            };
            setTemplateData((prev) => ({
              ...(prev || {}),
              structure: (prev?.structure || []).map((b) =>
                String(b?.id || "") === safeId ? patched : b,
              ),
            }));
            setIsModified(true);
            return patched;
          }
        }
      } catch {
        /* ignore */
      }
      return existing;
    }

    const newBlock = {
      id: safeId,
      title: String(title || "").trim(),
      type: "text",
      content: String(defaultContent || ""),
      tags: ["Universal"],
      condition: "ALWAYS",
    };

    setTemplateData((prev) => ({
      ...(prev || {}),
      structure: [...(prev && prev.structure ? prev.structure : []), newBlock],
    }));
    setIsModified(true);
    return newBlock;
  };

  const startEditingSpecial = (id, title, defaultContent) => {
    const block = ensureSpecialBlock(id, title, defaultContent);
    const safeContent =
      String(block?.content || "") ||
      (Array.isArray(block?.items) ? String(block.items.join("<br>")) : "") ||
      String(defaultContent || "");

    const normalized = {
      ...block,
      title: String(title || block?.title || "").trim(),
      type: "text",
      content: safeContent,
      items: null,
    };

    setTemplateData((prev) => ({
      ...(prev || {}),
      structure: (prev?.structure || []).map((b) =>
        String(b?.id || "") === String(normalized.id)
          ? { ...b, ...normalized }
          : b,
      ),
    }));
    setIsModified(true);
    startEditing(normalized);
  };

  const buildServiceNarrative = (selectedIds, customObjs) => {
    const ids = Array.isArray(selectedIds) ? selectedIds : [];
    const customs = Array.isArray(customObjs) ? customObjs : [];

    if (ids.length === 0 && customs.length === 0) {
      return "serviços advocatícios de consultoria geral";
    }

    const allObjects = Object.values(TAXONOMY).flatMap((t) => t.objects);

    const labels = ids.map((id) => {
      const found = allObjects.find((o) => o.id === id);
      return found ? found.label : id;
    });

    const combined = [...labels, ...customs.map((c) => escapeHtml(c))].filter(
      Boolean,
    );

    return new Intl.ListFormat("pt-BR", {
      style: "long",
      type: "conjunction",
    }).format(combined);
  };

  const parseText = (text) => {
    if (!text) return "";
    let out = repairMojibakeText(String(text));

    const normalizeTableLikeHtml = (html) => {
      let h = String(html || "");
      h = h.replace(
        /<\s*\/\s*(table|thead|tbody|tfoot|tr|td|th|colgroup|col)\s*>/gi,
        "</div>",
      );
      h = h.replace(
        /<\s*(table|thead|tbody|tfoot|tr|td|th|colgroup|col)(\s[^>]*)?>/gi,
        "<div$2>",
      );
      return h;
    };

    const normalizeInlineLayoutStyles = (html) => {
      let h = String(html || "");

      // O conteúdo salvo pode vir com estilos inline que forçam colunas.
      // Para a visualização do contrato, removemos o atributo inteiro para garantir fluxo em 1 coluna.
      h = h.replace(/\sstyle\s*=\s*(["'])([\s\S]*?)\1/gi, "");

      h = h.replace(
        /\sclass\s*=\s*("|')([\s\S]*?)\1/gi,
        (full, quote, classText) => {
          const shouldDrop = (token) => {
            const t = String(token || "").trim();
            if (!t) return false;
            if (
              t === "flex" ||
              t === "inline-flex" ||
              t === "grid" ||
              t === "inline-grid"
            )
              return true;
            if (t.startsWith("grid-cols-")) return true;
            if (t.startsWith("col-span-")) return true;
            if (t.startsWith("basis-")) return true;
            if (t.startsWith("columns-") || t.startsWith("column-"))
              return true;
            if (t.startsWith("table") || t.startsWith("table-")) return true;
            if (t.startsWith("w-")) {
              // remove classes de largura que forçam colunas (w-1/3, w-2/3, w-[33%], etc)
              if (/^w-\d+\/\d+$/.test(t)) return true;
              if (/^w-\[.*\]$/.test(t)) return true;
              if (/^w-\d+%$/.test(t)) return true;
            }
            return false;
          };

          const rawTokens = String(classText || "").split(/\s+/g);
          const kept = rawTokens.filter((tok) => tok && !shouldDrop(tok));
          if (kept.length === 0) return "";
          return ` class=${quote}${kept.join(" ")}${quote}`;
        },
      );

      return h;
    };

    const normalizeInlineLayoutClasses = (html) => {
      let h = String(html || "");

      // O conteúdo salvo pode vir com classes que forçam colunas.
      // Para a visualização do contrato, removemos as classes que forçam colunas.
      h = h.replace(
        /\sclass\s*=\s*(["'])([\s\S]*?)\1/gi,
        (full, quote, classText) => {
          const shouldDrop = (token) => {
            const t = String(token || "").trim();
            if (!t) return false;
            if (
              t === "flex" ||
              t === "inline-flex" ||
              t === "grid" ||
              t === "inline-grid"
            )
              return true;
            if (t.startsWith("grid-cols-")) return true;
            if (t.startsWith("col-span-")) return true;
            if (t.startsWith("basis-")) return true;
            if (t.startsWith("columns-") || t.startsWith("column-"))
              return true;
            if (t.startsWith("table") || t.startsWith("table-")) return true;
            if (t.startsWith("w-")) {
              // remove classes de largura que forçam colunas (w-1/3, w-2/3, w-[33%], etc)
              if (/^w-\d+\/\d+$/.test(t)) return true;
              if (/^w-\[.*\]$/.test(t)) return true;
              if (/^w-\d+%$/.test(t)) return true;
            }
            return false;
          };

          const rawTokens = String(classText || "").split(/\s+/g);
          const kept = rawTokens.filter((tok) => tok && !shouldDrop(tok));
          if (kept.length === 0) return "";
          return ` class=${quote}${kept.join(" ")}${quote}`;
        },
      );

      return h;
    };

    const normalizeLegacyAttributes = (html) => {
      let h = String(html || "");
      // Atributos típicos de tabelas/colunas que quebram o fluxo (mesmo após trocar tags por div)
      h = h.replace(
        /\s(width|height|align|valign|colspan|rowspan|nowrap)\s*=\s*[^\s>]+/gi,
        "",
      );
      h = h.replace(
        /\s(width|height|align|valign|colspan|rowspan|nowrap)\s*=\s*[^\s>]+/gi,
        "",
      );
      return h;
    };

    const resolveRefs = (raw) => {
      if (!raw) return "";
      const clauses = getQuickNavBlocks();
      const refMap = new Map(
        clauses.map((b, idx) => [String(b?.id || ""), `Cláusula ${idx + 1}ª`]),
      );
      return String(raw).replace(
        /\[\[(REF|REF_CLAUSE):([^\]]+)\]\]/g,
        (_, kind, id) => {
          const key = String(id || "").trim();
          const label = refMap.get(key);
          if (!label) {
            const prefix =
              String(kind || "").toUpperCase() === "REF_CLAUSE"
                ? "REF_CLAUSE"
                : "REF";
            return `[${prefix}. QUEBRADA: ${key}]`;
          }
          return `<strong>${label}</strong>`;
        },
      );
    };

    out = resolveRefs(out);
    out = normalizeTableLikeHtml(out);
    out = normalizeInlineLayoutStyles(out);
    out = normalizeInlineLayoutClasses(out);
    out = normalizeLegacyAttributes(out);

    const joinPartiesPt = (parts) => {
      const arr = (Array.isArray(parts) ? parts : []).filter(
        (x) => String(x || "").trim() !== "",
      );
      if (arr.length <= 1) return arr[0] || "";
      if (arr.length === 2) return `${arr[0]} e ${arr[1]}`;
      return `${arr.slice(0, -1).join("; ")}; e ${arr[arr.length - 1]}`;
    };

    const parties = Array.isArray(form?.parties) ? form.parties : [];
    const partiesNamesText = joinPartiesPt(
      parties.map((p) => {
        const id = String(p?.clientId || "").trim();
        const live = id ? crmClientsById?.[id] : null;
        const liveName =
          live?.qualification?.physical?.socialName ||
          live?.qualification?.physical?.name ||
          live?.qualification?.legal?.companyName ||
          live?.name;
        return String(liveName || p?.name || "").trim();
      }),
    );

    const partiesLabel =
      parties.length > 1 ? "PARTES CONTRATANTES" : "PARTE CONTRATANTE";

    const joinPt = (items) => {
      const arr = (Array.isArray(items) ? items : []).filter(
        (x) => String(x || "").trim() !== "",
      );
      if (arr.length <= 1) return arr[0] || "";
      if (arr.length === 2) return `${arr[0]} e ${arr[1]}`;
      return `${arr.slice(0, -1).join("; ")}; e ${arr[arr.length - 1]}`;
    };

    const activePartners = (() => {
      if (!partnerData?.active) return [];
      const firstPartner = {
        name: partnerData?.name || "",
        oab: partnerData?.oab || "",
        cpf: partnerData?.cpf || "",
        address: partnerData?.address || "",
        clientId: partnerData?.clientId || null,
        qualificationData: partnerData?.qualificationData || null,
      };
      const extras = Array.isArray(partnerExtras) ? partnerExtras : [];
      const all = [firstPartner, ...extras];
      return all.filter(
        (p) =>
          String(p?.name || p?.oab || p?.cpf || p?.address || "").trim() !== "",
      );
    })();

    const contractedQualificationText = (() => {
      const parts = [];

      if (officeQualificationText) parts.push(officeQualificationText);

      (Array.isArray(activePartners) ? activePartners : []).forEach((p) => {
        const structured = p?.qualificationData;
        const partnerQualification =
          structured && structured?.qualification && structured?.type
            ? String(
                buildCanonicalClientQualification(structured, crmClientsById)
                  ?.text || "",
              ).trim()
            : "";
        if (partnerQualification) parts.push(partnerQualification);
        else if (String(p?.name || "").trim())
          parts.push(escapeHtml(String(p.name).trim()));
      });

      if (parts.length <= 1)
        return (
          String(parts[0] || "")
            .trim()
            .replace(/[\s\.,;]+$/g, "") + "."
        );
      const stripFinalPunct = (s) =>
        String(s || "")
          .trim()
          .replace(/[\s\.,;]+$/g, "");
      const normalized = parts.map(stripFinalPunct);

      const joined = joinPt(normalized);
      return joined ? `${joined}.` : "";
    })();

    const partiesQualificationText = (() => {
      const rawParts = parties
        .map((p) => {
          const id = String(p?.clientId || "").trim();
          const live = id ? crmClientsById?.[id] : null;
          if (live) {
            return String(
              buildCanonicalClientQualification(live, crmClientsById)?.text ||
                "",
            ).trim();
          }
          return escapeHtml(String(p?.fullQualification || "").trim());
        })
        .filter((x) => String(x || "").trim() !== "");

      if (rawParts.length <= 1) return rawParts[0] || "";

      const stripFinalPunct = (s) =>
        String(s || "")
          .trim()
          .replace(/[\s\.,;]+$/g, "");
      const normalized = rawParts.map(stripFinalPunct);
      const joined = joinPartiesPt(normalized);
      return joined ? `${joined}.` : "";
    })();

    const allPartiesQualificationText = (() => {
      const parts = [partiesQualificationText, contractedQualificationText]
        .map((x) => String(x || "").trim())
        .filter((x) => x !== "");
      if (parts.length === 0) return "";
      if (parts.length === 1) return parts[0];
      return parts.join(" ");
    })();

    const mainParty =
      (Array.isArray(form?.parties) ? form.parties : [])?.[0] || {};
    const mainPartyId = String(mainParty?.clientId || "").trim();
    const mainLive = mainPartyId ? crmClientsById?.[mainPartyId] : null;
    const mainPhys = mainLive?.qualification?.physical || null;

    const officePhysical =
      officeQualificationClient?.qualification?.physical || {};
    const officeAddress = officeQualificationClient?.location?.address || {};
    const officeDisplayName = String(
      officePhysical?.socialName ||
        officePhysical?.name ||
        officeQualificationClient?.name ||
        "CONTRATADO",
    ).trim();
    const officeAddressText = [
      officeAddress?.street || "",
      officeAddress?.number ? `n. ${officeAddress.number}` : "",
      officeAddress?.complement || "",
      officeAddress?.neighborhood ? `bairro ${officeAddress.neighborhood}` : "",
      officeAddress?.city && officeAddress?.state
        ? `${officeAddress.city}/${officeAddress.state}`
        : officeAddress?.city || officeAddress?.state || "",
      officeAddress?.zipCode ? `CEP: ${officeAddress.zipCode}` : "",
    ]
      .filter(Boolean)
      .join(", ");

    const firstPartyQualificationText = (() => {
      const p = parties?.[0] || null;
      if (!p) return "";
      const id = String(p?.clientId || "").trim();

      const live = id ? crmClientsById?.[id] : null;
      if (live) {
        return String(
          buildCanonicalClientQualification(live, crmClientsById)?.text || "",
        ).trim();
      }
      return escapeHtml(String(p?.fullQualification || "").trim());
    })();

    const map = {
      "[[OFFICE_NAME]]": officeDisplayName,
      "[[OFFICE_NATIONALITY]]": String(
        officePhysical?.nationality ||
          officeQualificationClient?.nationality ||
          "brasileiro(a)",
      ),
      "[[OFFICE_CIVIL_STATUS]]": String(
        officePhysical?.civilStatus ||
          officeQualificationClient?.civilStatus ||
          "",
      ),
      "[[OFFICE_OAB]]": String(
        officePhysical?.professionalRegister ||
          officeQualificationClient?.oab ||
          "",
      ),
      "[[OFFICE_OAB_STATE]]": String(
        officePhysical?.registerUf ||
          officeQualificationClient?.oabState ||
          officeAddress?.state ||
          "",
      ),
      "[[OFFICE_CPF]]": String(
        officePhysical?.cpf || officeQualificationClient?.cpf || "",
      ),
      "[[OFFICE_RG]]": String(
        officePhysical?.rg || officeQualificationClient?.rg || "...",
      ),
      "[[OFFICE_RG_ISSUER]]": String(
        officePhysical?.orgaoEmissor ||
          officeQualificationClient?.rgIssuer ||
          "",
      ),
      "[[OFFICE_FULL_ADDRESS]]": officeAddressText,
      "[[OFFICE_ADDRESS]]": officeAddressText,
      "[[OFFICE_EMAIL]]": String(
        officeQualificationClient?.contact?.email ||
          officeQualificationClient?.email ||
          "",
      ),
      "[[CONTRATANTE_NOME]]": String(
        mainParty?.name || "CONTRATANTE",
      ).toUpperCase(),
      "[[CONTRATANTE_NATIONALITY]]": String(
        mainPhys?.nationality ||
          mainLive?.nationality ||
          mainParty?.nationality ||
          "brasileiro(a)",
      ),
      "[[CONTRATANTE_CIVIL_STATUS]]": String(
        mainPhys?.civilStatus ||
          mainLive?.civilStatus ||
          mainParty?.civilStatus ||
          "",
      ),
      "[[CONTRATANTE_PROFESSION]]": String(
        mainPhys?.profession ||
          mainLive?.profession ||
          mainParty?.profession ||
          "",
      ),

      "[[CONTRATANTE_CPF]]": String(mainParty?.cpf || "..."),
      "[[CONTRATANTE_ENDERECO]]": String(mainParty?.address || "..."),
      "[[G_AO]]": G.AO,
      "[[G_ao]]": G.ao,
      "[[G_DO]]": G.DO,
      "[[G_do]]": G.do,
      "[[G_NO]]": G.NO,
      "[[G_no]]": G.no,
      "[[G_PELO]]": G.PELO,
      "[[G_pelo]]": G.pelo,
      "[[G_CONTRATANTE]]": G.CONTRATANTE,
      "[[G_contratante]]": G.contratante,
      "[[G_PORTADOR]]": G.PORTADOR,
      "[[G_INSCRITO]]": G.INSCRITO,
      "[[G_DOMICILIADO]]": G.DOMICILIADO,
      "[[G_REPRESENTADO]]": G.REPRESENTADO,
      "[[G_INFORMADO]]": G.INFORMADO,
      "[[G_INTERESSADO]]": G.INTERESSADO,
      "[[G_CIENTE]]": G.CIENTE,

      "[[ROTULO_CONTRATANTE]]": `<strong>${partiesLabel}:</strong>`,
      "[[ROTULO_CONTRATADO]]": `<strong>${activePartners.length > 0 ? "PARTES CONTRATADAS:" : "PARTE CONTRATADA:"}</strong>`,
      "[[ROTULO_CONTRATANTES]]": `<strong>${partiesLabel}:</strong>`,
      "[[ROTULO_CONTRATADOS]]": `<strong>${activePartners.length > 0 ? "PARTES CONTRATADAS:" : "PARTE CONTRATADA:"}</strong>`,
      "[[NOME_CLIENTE]]": partiesNamesText,
      "[[CPF_CLIENTE]]": String(form.parties?.[0]?.cpf || ""),
      "[[EMAIL_CLIENTE]]": String(form.parties?.[0]?.email || ""),
      "[[ENDERECO_CLIENTE]]": String(form.parties?.[0]?.address || ""),
      "[[QUALIFICACAO_COMPLETA]]": partiesQualificationText,
      "[[QUALIFICACAO_CONTRATANTE]]":
        firstPartyQualificationText || partiesQualificationText,
      "[[QUALIFICACAO_CONTRATANTES]]": partiesQualificationText,
      "[[QUALIFICACAO_CONTRATADO]]": contractedQualificationText,
      "[[QUALIFICACAO_CONTRATADOS]]": contractedQualificationText,
      "[[QUALIFICACAO_ADVOGADO]]": contractedQualificationText,
      "[[QUALIFICACAO_COMPLETA_DE_TODOS]]": allPartiesQualificationText,
      "[[OBJETO_PRINCIPAL]]":
        String(form?.objetoPrincipal || "").trim() &&
        String(form?.objetoPrincipal || "").trim() !== "Descreva o objeto..."
          ? String(form.objetoPrincipal)
          : "PRESTAÇÃO DE SERVIÇOS JURÍDICOS (Objeto a ser definido na minuta específica).",
      "[[VALOR_HONORARIOS]]": new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(form?.valorHonorarios || 0)),
      "[[SERVICOS_CONTRATADOS]]": buildServiceNarrative(
        form?.selectedObjetos,
        form?.customObjetos,
      ),
      "[[VALOR_CAUSA]]": new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(Number(form?.valorCausa || 0)),
      "[[PERCENTUAL_EXITO]]":
        String(form?.percentualExito || 0).replace(".", ",") + "%",
      "[[RAMO_DIREITO]]": String(
        form?.isCustomRamo ? form?.customRamo : form?.ramoDireito || "",
      ).toUpperCase(),
      "[[POSICAO_CLIENTE]]": String(
        form?.isCustomPosicao
          ? form?.customPosicao
          : form?.posicaoCliente || "",
      ).toUpperCase(),
      "[[NOME_CONTRATADO]]": activePartners.length
        ? joinPt([
            officeDisplayName,
            ...activePartners.map((p) => p.name).filter(Boolean),
          ])
        : officeDisplayName,
      "[[NOME_DA_PARTE_CONTRARIA]]":
        String(form?.parteContrariaName || "").trim() || "...",
      "[[CONTEXTO_PROCESSUAL]]": buildContextoProcessual({
        parteContrariaName: form?.parteContrariaName,
        posicoes: [
          form?.isCustomPosicao ? form?.customPosicao : form?.posicaoCliente,
        ].filter(Boolean),
        ramos: [
          form?.isCustomRamo ? form?.customRamo : form?.ramoDireito,
        ].filter(Boolean),
        documentType: "contrato",
      }),
    };

    // htmlTrustedKeys: valores que já são HTML pré-montado e seguro (as
    // funções de qualificação acima escapam todo texto livre do usuário
    // antes de embrulhar em <strong>; ROTULO_* e CONTEXTO_PROCESSUAL idem).
    // Tudo que não estiver aqui é tratado como texto puro e escapado antes
    // de entrar no HTML — fecha o mesmo vetor de XSS já corrigido na
    // Procuração (Art. 341/342).
    const htmlTrustedKeys = new Set([
      "[[ROTULO_CONTRATANTE]]",
      "[[ROTULO_CONTRATADO]]",
      "[[ROTULO_CONTRATANTES]]",
      "[[ROTULO_CONTRATADOS]]",
      "[[QUALIFICACAO_COMPLETA]]",
      "[[QUALIFICACAO_CONTRATANTE]]",
      "[[QUALIFICACAO_CONTRATANTES]]",
      "[[QUALIFICACAO_CONTRATADO]]",
      "[[QUALIFICACAO_CONTRATADOS]]",
      "[[QUALIFICACAO_ADVOGADO]]",
      "[[QUALIFICACAO_COMPLETA_DE_TODOS]]",
      "[[CONTEXTO_PROCESSUAL]]",
    ]);
    Object.keys(map).forEach((k) => {
      const v = String(map[k] ?? "");
      out = out.split(k).join(htmlTrustedKeys.has(k) ? v : escapeHtml(v));
    });

    // Tolerância a placeholders com espaços acidentais, ex: "[[OBJETO_PRINCIPAL ]]"
    out = out
      .replace(
        /\[\[OBJETO_PRINCIPAL\s*\]\]/g,
        escapeHtml(String(map["[[OBJETO_PRINCIPAL]]"] ?? "")),
      )
      .replace(
        /\[\[VALOR_HONORARIOS\s*\]\]/g,
        escapeHtml(String(map["[[VALOR_HONORARIOS]]"] ?? "")),
      );

    return repairMojibakeText(out);
  };

  const handlePublishContract = (
    publishType = "simple",
    format = "html",
    cadernoId = "",
    cadernoTitle = "",
  ) => {
    const publicationParties = (
      Array.isArray(form?.parties) ? form.parties : []
    ).filter((party) => {
      const linkedId = String(
        party?.clientId || party?.qualificationData?.id || "",
      ).trim();
      const displayName = String(party?.name || "").trim();
      return linkedId || displayName;
    });

    if (publicationParties.length === 0) {
      setDialogState({
        open: true,
        title: "Publicação indisponível",
        message:
          "Importe ao menos uma parte vinculada ao CRM antes de publicar o contrato no sistema do cliente.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }

    const primaryClientId = String(
      publicationParties?.[0]?.clientId ||
        publicationParties?.[0]?.qualificationData?.id ||
        "",
    ).trim();
    if (!primaryClientId) {
      setDialogState({
        open: true,
        title: "Vínculo obrigatório",
        message:
          "A publicação exige que a parte principal esteja vinculada ao CRM por `clientId`, para que o documento apareça corretamente no Portal do Cliente e no prontuário.",
        mode: "info",
        onConfirm: null,
      });
      return;
    }

    const safePublishType = "simple";
    const safeFormat =
      String(format || "html")
        .trim()
        .toLowerCase() === "pdf"
        ? "pdf"
        : "html";
    const requiresConsent = false;
    const documentStatus = "PUBLISHED";
    const consentStatus = "not_required";
    const eventType = "DOCUMENT_PUBLISHED";

    setDialogState({
      open: true,
      title:
        safeFormat === "pdf"
          ? "Publicar PDF do Contrato"
          : "Salvar Contrato (HTML)",
      message:
        safeFormat === "pdf"
          ? "Esta ação irá publicar o contrato atual em PDF no Firebase Storage, registrar o documento oficial no Portal do Cliente e lançar o evento correspondente no prontuário. Deseja continuar?"
          : "Esta ação irá salvar o contrato em HTML no prontuário interno para edição futura. O documento NÃO será enviado ao portal do cliente. Deseja continuar?",
      mode: "confirm",
      onConfirm: async () => {
        // ── FRESH MASTER FETCH: Garantir que o template mais recente está no DOM ──
        // Busca o master da nuvem e atualiza templateData ANTES de capturar o DOM.
        // Previne que HTML/PDF seja gerado com conteúdo desatualizado (deploy, edição remota).
        try {
          if (appId) {
            const _freshMasterData = await getFreshContractMasterV8();
            if (_freshMasterSnap.exists()) {
              const _freshData = _freshMasterSnap.data() || {};
              const _freshStructure = Array.isArray(_freshData?.structure)
                ? repairMojibakeDeep(_freshData.structure)
                : null;
              if (_freshStructure && _freshStructure.length > 0) {
                masterStructureRef.current = _freshStructure;
                setTemplateData({ structure: _freshStructure });
                setDataSource("cloud_master");
                await new Promise((resolve) => {
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      setTimeout(resolve, 800);
                    });
                  });
                });
                console.log(
                  "[ContractEngine] Fresh master aplicado ao DOM antes da captura.",
                );
              }
            }
          }
        } catch (_freshErr) {
          console.warn(
            "[ContractEngine] Fresh master fetch falhou (non-blocking):",
            _freshErr?.message,
          );
        }

        const escapeHtml = (value) =>
          String(value || "")
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");

        const normalizeAbsoluteUrl = (raw) => {
          const value = String(raw || "").trim();
          if (!value) return "";
          try {
            return new URL(value, window.location.origin).href;
          } catch {
            return value;
          }
        };

        const buildPublicationHtml = (title) => {
          const paperSource = document.querySelector(".contract-print-area");
          const paperClone = paperSource ? paperSource.cloneNode(true) : null;

          if (
            paperClone &&
            !paperClone.querySelector("#contrato-legal-pronto")
          ) {
            const readyAnchor = document.createElement("div");
            readyAnchor.id = "contrato-legal-pronto";
            readyAnchor.setAttribute("data-status", "ready");
            readyAnchor.style.display = "none";
            paperClone.appendChild(readyAnchor);
          }

          if (paperClone) {
            paperClone.querySelectorAll("[src]").forEach((node) => {
              try {
                const current = node.getAttribute("src");
                if (current)
                  node.setAttribute("src", normalizeAbsoluteUrl(current));
              } catch {}
            });

            paperClone.querySelectorAll("[href]").forEach((node) => {
              try {
                const current = node.getAttribute("href");
                if (current && !String(current).startsWith("#"))
                  node.setAttribute("href", normalizeAbsoluteUrl(current));
              } catch {}
            });
          }

          const styleTags = Array.from(document.querySelectorAll("style"))
            .map((node) => `<style>${String(node?.textContent || "")}</style>`)
            .join("\n");

          const linkTags = Array.from(
            document.querySelectorAll('link[rel="stylesheet"]'),
          )
            .map((node) => {
              const href = normalizeAbsoluteUrl(node?.getAttribute("href"));
              return href
                ? `<link rel="stylesheet" href="${escapeHtml(href)}" />`
                : "";
            })
            .filter(Boolean)
            .join("\n");

          const bodyHtml =
            paperClone?.outerHTML ||
            `<div class="published-paper-fallback"><h1>${escapeHtml(title)}</h1><div>${sanitizeScreenHtml(parseText("<p>Snapshot visual indisponível no momento da publicação.</p>"))}</div></div>`;

          return `<!doctype html>
<html lang="pt-BR">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)}</title>
  ${linkTags}
  ${styleTags}
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap">
  <style>
    html, body { background: #f3f4f6; margin: 0; padding: 0; }
    body { font-family: 'Roboto', sans-serif; -webkit-font-smoothing: antialiased; color: #20243B; }
    .published-shell { max-width: 1120px; margin: 0 auto; padding: 24px; }
    .publication-banner { margin-bottom: 16px; border: 1px solid #dbe2ea; background: #fff; border-radius: 16px; padding: 16px 18px; }
    .publication-banner h1 { margin: 0 0 8px 0; font-size: 18px; font-weight: 800; }
    .publication-banner p { margin: 4px 0; font-size: 12px; line-height: 1.6; }
    .screen-paper { margin: 0 auto; }
    .no-print { display: none !important; }
    .quadro-resumo-icon { display: none !important; }
    .ghost-marker { display: none !important; }
    @media print {
      html, body { background: white !important; }
      .published-shell { max-width: none !important; margin: 0 !important; padding: 0 !important; }
      .publication-banner { display: none !important; }
      .contract-print-area, .print-only-view, .print-layout-table, .document-content { margin-top: 0 !important; }
      #guia-rapido-print, #guia-rapido { margin-top: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="published-shell">
    <div class="publication-banner">
      <h1>${escapeHtml(title)}</h1>
      <p>Documento publicado no sistema interno do Portal do Cliente em ${escapeHtml(new Date().toLocaleString("pt-BR"))} por ${escapeHtml(String(user?.displayName || user?.email || "Operador").trim() || "Operador")}.</p>
    </div>
    ${bodyHtml}
  </div>
</body>
</html>`;
        };

        const actorName =
          String(user?.displayName || user?.email || "Operador").trim() ||
          "Operador";
        const principalParties = publicationParties.map((party) => {
          const signature = buildSignatureLine(party);
          return {
            clientId: String(
              party?.clientId || party?.qualificationData?.id || "",
            ).trim(),
            principalName: String(party?.name || "").trim(),
            signerName: String(signature?.nameLine || party?.name || "").trim(),
            signerCpf: String(signature?.cpfLine || party?.cpf || "").trim(),
            signerRole: String(party?.role || "contratante").trim(),
            subtitle: String(signature?.subtitle || "").trim(),
            email: String(party?.email || "").trim(),
          };
        });

        const relatedClientIds = Array.from(
          new Set(
            principalParties
              .map((item) => String(item?.clientId || "").trim())
              .filter(Boolean),
          ),
        );
        const primaryParty = principalParties[0] || {};
        const primaryClientData =
          publicationParties[0]?.qualificationData ||
          publicationParties[0] ||
          {};
        const targetFolderId = readText(
          primaryClientData?.driveFolders?.processos ||
            primaryClientData?.drive?.processosFolderId ||
            primaryClientData?.driveFolders?.contratos ||
            primaryClientData?.drive?.contratosFolderId ||
            primaryClientData?.driveFolderId ||
            primaryClientData?.drive?.rootFolderId,
        );
        const title = buildFriendlyContractName();
        const currentVersion = await (async () => {
          const localCurrentVersion = Math.max(
            0,
            Number(
              editingPublicationMeta?.publicationVersion ||
                editingPublicationMeta?.version ||
                0,
            ) || 0,
          );
          let cloudCurrentVersion = 0;
          try {
            const seedId = String(
              editingPublicationMeta?.documentId || "",
            ).trim();
            const seedSeriesId = String(
              editingPublicationMeta?.publicationSeriesId || "",
            ).trim();
            if (seedId || seedSeriesId) {
              const loaded = await readLatestPublishedDocumentInSeries({
                documentId: seedId || seedSeriesId,
                publicationSeriesId: seedSeriesId,
              });
              cloudCurrentVersion = Math.max(
                0,
                Number(loaded?.data?.publicationVersion || 0) || 0,
              );
            }
          } catch (e) {
            void e;
          }
          return Math.max(localCurrentVersion, cloudCurrentVersion);
        })();
        const newVersion = currentVersion + 1;
        const fileBase = generatePublishedArtifactFileStem(
          "Contrato",
          publicationParties[0] || {},
          {
            mainPartyName: String(primaryParty?.principalName || "").trim(),
            mainPartyNames: principalParties
              .map((item) => String(item?.principalName || "").trim())
              .filter(Boolean),
            ramoDireito: String(form?.ramoDireito || "").trim(),
          },
          newVersion,
        );
        const publicationHtml = buildPublicationHtml(title);
        const editableContentData = {
          ...buildCloudAutosavePayload(),
          engine: "contract_engine",
          documentType: "CONTRACT",
          snapshotFormat: "dual-save-v1",
        };

        setIsPublishing(true);
        try {
          if (!appId) throw new Error("ProjectId do Firebase não encontrado.");

          const documentRef = await getNextContractDocumentRef();
          const publicationSeriesId =
            String(
              editingPublicationMeta?.publicationSeriesId || documentRef.id,
            ).trim() || documentRef.id;
          const previousPublicationId =
            String(editingPublicationMeta?.documentId || "").trim() || null;
          const htmlFileName = `${fileBase}.html`;
          const pdfFileName = `${fileBase}.pdf`;
          let fileName = htmlFileName;
          let fileUrl = "";
          let downloadUrl = "";
          let storagePath = "";
          let mimeType = "text/html";
          let extension = "html";
          let storageMode = "html_archive";
          let storageWarning = "";
          let storageEngine = {
            provider: "firestore",
            access: "internal",
            mode: "html_archive",
          };

          if (safeFormat === "pdf") {
            // ══ BARREIRA MFA/AUTH: Validar sessão antes do fetch ══
            if (!user) {
              throw new Error(
                "Sessão não iniciada. Faça login novamente antes de gerar o PDF.",
              );
            }
            try {
              await getToken(true);
            } catch (_mfaErr) {
              console.error(
                "[RDA PDF Auth] Token inválido ou MFA pendente:",
                _mfaErr,
              );
              throw new Error(
                "Sessão expirada ou 2FA pendente. Faça logout e login novamente antes de gerar o PDF.",
              );
            }

            let uploadResponse = null;
            let backendErrorText = "";
            let pdfBlob = null;
            try {
              uploadResponse = await fetchWithWarmup(
                `${PUBLISHING_API_BASE_URL}/drive/generate-pdf?t=${Date.now()}`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    html: publicationHtml,
                    fileName: pdfFileName,
                    parentId: targetFolderId || null,
                    makePublic: false,
                  }),
                },
                {
                  onWaking: () =>
                    showToast?.(
                      "⏳ Backend acordando... aguarde até 40s na primeira vez.",
                    ),
                  onRetry: (attempt, max) =>
                    showToast?.(
                      `🔄 Tentativa ${attempt + 1}/${max}... aguarde.`,
                    ),
                },
              );
              if (!uploadResponse?.ok) {
                backendErrorText = await uploadResponse.text().catch(() => "");
                throw new Error(
                  backendErrorText ||
                    uploadResponse?.statusText ||
                    "Falha ao gerar o PDF do contrato no motor Python.",
                );
              }
              pdfBlob = await uploadResponse.blob();
            } catch (uploadError) {
              const msg = String(uploadError?.message || uploadError || "");
              const isNetworkError =
                msg.includes("Failed to fetch") ||
                msg.includes("NetworkError") ||
                msg.includes("CORS") ||
                msg.includes("ERR_FAILED");
              if (isNetworkError) {
                throw new Error(
                  "O servidor de geração de PDF (backend) está fora do ar ou inacessível. Verifique se o serviço no Render está ativo em https://dashboard.render.com e tente novamente em alguns minutos.",
                );
              }
              throw new Error(msg || "Falha ao gerar o PDF do contrato.");
            }

            if (!pdfBlob || !pdfBlob.size) {
              throw new Error(
                "O motor Python retornou um PDF vazio para o contrato.",
              );
            }

            const primaryClientId =
              String(primaryParty?.clientId || "").trim() || "unlinked-client";
            const storageRef = ref(
              storage,
              `artifacts/${appId}/public/documents/contracts/${primaryClientId}/${pdfFileName}`,
            );
            await uploadBytes(storageRef, pdfBlob, {
              contentType: "application/pdf",
            });
            const storageDownloadUrl = await getDownloadURL(storageRef);

            fileName = pdfFileName;
            fileUrl = storageDownloadUrl;
            downloadUrl = storageDownloadUrl;
            storagePath = storageRef.fullPath;
            mimeType = "application/pdf";
            extension = "pdf";
            storageMode = "pdf_archive";
            storageEngine = {
              provider: "firebase_storage",
              access: "authenticated_url",
              mode: "pdf_archive",
              storagePath: storageRef.fullPath,
            };
          }

          const payload = {
            id: documentRef.id,
            type: "CONTRACT",
            status: documentStatus,
            module: "contracts",
            title,
            name: title,
            fileName,
            fileUrl,
            downloadUrl,
            storagePath,
            storageEngine,
            mimeType,
            extension,
            category: "Contrato",
            classification: { category: "Contrato" },
            clientVisible: safeFormat === "pdf",
            htmlContent: publicationHtml,
            contentData: editableContentData,
            publicationVersion: newVersion,
            publicationSeriesId,
            previousPublicationId,
            clientId: String(primaryParty?.clientId || "").trim(),
            relatedEmail: String(primaryParty?.email || "").trim() || null,
            relatedEntities: relatedClientIds,
            parties: principalParties,
            signers: principalParties.map((item) => ({
              clientId: String(item?.clientId || "").trim(),
              name: String(item?.signerName || "").trim(),
              cpf: String(item?.signerCpf || "").trim(),
              role: String(item?.signerRole || "").trim(),
              subtitle: String(item?.subtitle || "").trim(),
            })),
            context: {
              module: "contracts",
              ramoDireito: String(form?.ramoDireito || "").trim(),
              posicaoCliente: String(form?.posicaoCliente || "").trim(),
              objetoPrincipal: String(form?.objetoPrincipal || "").trim(),
              parteContrariaName: String(form?.parteContrariaName || "").trim(),
              relatedEntities: relatedClientIds,
              documentKind: "CONTRACT_PUBLICATION",
            },
            requiresConsent,
            consentStatus,
            publication: {
              source: "contract_engine",
              publishType: safePublishType,
              format: safeFormat,
              version: newVersion,
              publishedAtIso: new Date().toISOString(),
              publishedBy: actorName,
              storageMode,
              storageWarning,
            },
            cadernoId: cadernoId || null,
            cadernoTitle: cadernoTitle || null,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: email || null,
            updatedBy: email || null,
          };

          await publishContractDocument(documentRef, payload);

          await Promise.all(
            relatedClientIds.map((clientId) => {
              const signerText = principalParties
                .filter(
                  (item) =>
                    String(item?.clientId || "").trim() ===
                    String(clientId || "").trim(),
                )
                .map((item) =>
                  String(item?.signerName || "Signatário não informado").trim(),
                )
                .filter(Boolean)
                .join(", ");
              const noteHtml = requiresConsent
                ? `<p><strong>Consentimento solicitado:</strong> ${escapeHtml(title)}</p><p>Signatário(s): ${escapeHtml(signerText || "Não informado")}</p><p>Status: Consentimento pendente.</p>`
                : `<p><strong>Contrato publicado:</strong> ${escapeHtml(title)}</p><p>Signatário(s): ${escapeHtml(signerText || "Não informado")}</p><p>Formato: ${escapeHtml(safeFormat.toUpperCase())}</p>`;

              return addContractProntuarioNote(clientId, {
                category: "SYSTEM",
                type: eventType,
                payload: {
                  text: requiresConsent
                    ? `Consentimento solicitado para contrato: ${title}`
                    : `Contrato publicado: ${title}`,
                  html: noteHtml,
                  documentId: documentRef.id,
                  title,
                  fileName,
                  fileUrl,
                  publicationVersion: newVersion,
                  publicationSeriesId,
                  downloadUrl,
                  storagePath,
                  workspaceLabel: "Contrato",
                  module: "contracts",
                  requiresConsent,
                  consentStatus,
                  publishType: safePublishType,
                  format: safeFormat,
                  storageMode,
                },
                actor: { name: actorName, isBot: false },
                createdAt: serverTimestamp(),
              });
            }),
          );

          setEditingPublicationMeta({
            documentId: documentRef.id,
            publicationSeriesId,
            publicationVersion: newVersion,
          });

          setDialogState({
            open: true,
            title:
              safeFormat === "pdf"
                ? "PDF do contrato publicado"
                : "Contrato salvo (HTML)",
            message:
              safeFormat === "pdf"
                ? "✅ SUCESSO!\n\nO contrato foi publicado em PDF no Firebase Storage, vinculado ao acervo do cliente e o evento correspondente já foi registrado no prontuário."
                : "✅ SUCESSO!\n\nO contrato foi salvo em HTML no prontuário interno. O documento está disponível para edição futura, mas NÃO foi enviado ao portal do cliente.",
            mode: "info",
            onConfirm: null,
          });
        } catch (e) {
          setDialogState({
            open: true,
            title: "Erro ao publicar contrato",
            message: "Erro: " + (e?.message || String(e)),
            mode: "info",
            onConfirm: null,
          });
        } finally {
          setIsPublishing(false);
        }
      },
    });
  };

  // REMOVIDO: getClauseRefList (menu duplicado removido)

  // REMOVIDO: handleRefSelect (menu duplicado removido)

  // REMOVIDO: Menu de referências duplicado (mantido apenas RulesPanel)

  const renderHierarchicalList = (rawItems, listStyle, clauseNumber) => {
    const items = Array.isArray(rawItems) ? rawItems : [];

    const safeClauseNumber =
      typeof clauseNumber === "number" && clauseNumber > 0
        ? clauseNumber
        : null;
    const isAlpha = String(listStyle || "").toUpperCase() === "ALPHA";

    const letter = (n) => {
      const i = Number(n);
      if (!Number.isFinite(i) || i <= 0) return "";
      return String.fromCharCode(64 + i);
    };

    const formatIndex = (n) => (isAlpha ? letter(n) : String(n));

    const makePrefix = (parts) => {
      const p = parts.filter(Boolean).join(".");
      return p ? `${p}.` : "";
    };

    const getLevel = (raw) => {
      const s = String(raw || "").trim();
      if (s.startsWith(">>>")) return 2;
      if (s.startsWith(">>")) return 1;
      return 0;
    };

    const stripIndent = (raw) =>
      String(raw || "")
        .replace(/^\s*>{2,3}\s*/g, "")
        .trim();

    const tree = [];
    let current = null;
    let currentSub = null;

    for (const raw of items) {
      const level = getLevel(raw);
      const cleaned = stripIndent(raw);

      if (level === 0) {
        current = { html: cleaned, subs: [] };
        currentSub = null;
        tree.push(current);
        continue;
      }

      if (level === 1) {
        currentSub = { html: cleaned, subs: [] };
        current.subs.push(currentSub);
        continue;
      }

      if (!currentSub) {
        currentSub = { html: "", subs: [] };
        current.subs.push(currentSub);
      }

      currentSub.subs.push({ html: cleaned });
    }

    return (
      <ol className="clause-body list-none pl-0 space-y-2">
        {tree.map((node, idx) => {
          const i = idx + 1;
          const p1 = makePrefix([safeClauseNumber, formatIndex(i)]);

          return (
            <li key={idx}>
              <div className="clause-li-row">
                <span className="shrink-0 font-bold text-[11pt]">{p1}</span>
                <span className="min-w-0">
                  {node.html ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: parseText(node.html) }}
                    />
                  ) : null}
                </span>
              </div>
              {node.subs.length > 0 && (
                <ol className="list-none pl-0 mt-2 space-y-2">
                  {node.subs.map((s, sIdx) => {
                    const j = sIdx + 1;
                    const p2 = makePrefix([
                      safeClauseNumber,
                      formatIndex(i),
                      j,
                    ]);

                    return (
                      <li key={sIdx}>
                        <div className="clause-li-row">
                          <span className="shrink-0 font-bold text-[11pt]">
                            {p2}
                          </span>
                          <span className="min-w-0">
                            {s.html ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: parseText(s.html),
                                }}
                              />
                            ) : null}
                          </span>
                        </div>
                        {Array.isArray(s.subs) && s.subs.length > 0 && (
                          <ol className="list-none pl-0 mt-2 space-y-2">
                            {s.subs.map((t, tIdx) => {
                              const k = tIdx + 1;
                              const p3 = makePrefix([
                                safeClauseNumber,
                                formatIndex(i),
                                j,
                                k,
                              ]);
                              return (
                                <li key={tIdx}>
                                  <div className="clause-li-row">
                                    <span className="shrink-0 font-bold text-[11pt]">
                                      {p3}
                                    </span>
                                    <span
                                      className="min-w-0"
                                      dangerouslySetInnerHTML={{
                                        __html: parseText(t.html),
                                      }}
                                    />
                                  </div>
                                </li>
                              );
                            })}
                          </ol>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </li>
          );
        })}
      </ol>
    );
  };

  const handleReorder = (fromIndex, toIndex) => {
    const newStructure = [...templateData.structure];
    const [moved] = newStructure.splice(fromIndex, 1);
    newStructure.splice(toIndex, 0, moved);
    setTemplateData((prev) => ({ ...prev, structure: newStructure }));
    setIsModified(true);
  };

  const startEditing = (block) => {
    setEditingBlockId(block.id);
    setTempTitle(cleanClauseTitle(block.title));
    setActiveBlockData(
      JSON.parse(
        JSON.stringify({
          ...block,
          tags: Array.isArray(block.tags) ? block.tags : ["Universal"],
          condition: block.condition || "ALWAYS",
          requiredObjects: block.requiredObjects || [],
          minValue: block.minValue || 0,
          minParties: block.minParties || 1,
        }),
      ),
    );
  };

  const saveEditorContent = (newData) => {
    setTemplateData((prev) => ({
      ...prev,
      structure: prev.structure.map((b) => {
        if (b.id !== editingBlockId) return b;
        const merged = { ...b, ...newData, title: tempTitle };
        if (String(b?.type || "") === "list") {
          merged.listStyle = newData?.listStyle ?? b?.listStyle ?? "NUM";
        }
        return merged;
      }),
    }));
    setEditingBlockId(null);
    setIsModified(true);
  };

  const isPreambuloBlock = (block) => {
    return (
      block?.id === "preambulo" ||
      String(block?.title || "")
        .toUpperCase()
        .includes("PREÂMBULO")
    );
  };

  const isOptionLikeBlock = (block) => {
    const id = String(block?.id || "");
    const t = String(block?.title || "").toLowerCase();
    if (/_opt_/.test(id)) return true;
    if (t.includes("opção")) return true;
    if (
      t.includes("pessoa física") ||
      t.includes("pessoa jurid") ||
      t.includes("incapaz") ||
      t.includes("menor") ||
      t.includes("espólio") ||
      t.includes("condom") ||
      t.includes("animal") ||
      t.includes("privada de liberdade")
    )
      return true;
    return false;
  };

  const isNavigableClause = (block) => {
    if (!block) return false;
    if (String(block?.id || "").startsWith("cl_01_")) return false;
    if (isPreambuloBlock(block)) return false;
    if (block.isSubClause) return false;
    if (isOptionLikeBlock(block)) return false;
    return true;
  };

  const getClauseNumberForBlock = (blockId) => {
    if (String(blockId) === "__das_partes") return 1;
    const blocks = templateData?.structure || [];
    let n = 1;
    for (const b of blocks) {
      if (!b) continue;
      if (!isNavigableClause(b)) continue;
      if (String(b?.id || "") === "__das_partes") continue;
      n += 1;
      if (String(b.id) === String(blockId)) return n;
    }
    return null;
  };

  const getAnchorIdForBlock = (block) => {
    if (String(block?.id) === "__das_partes") return "clausula-1";
    const n = getClauseNumberForBlock(block?.id);
    if (!n) return null;
    return `clausula-${n}`;
  };

  const getQuickNavBlocks = () => {
    const blocks = templateData?.structure || [];
    const filteredBase = blocks
      .filter((b) => isNavigableClause(b))
      .filter((b) => !String(b?.id || "").startsWith("cl_01_"));
    const filtered =
      mode === "producao"
        ? filteredBase.filter((b) => validateBlock(b))
        : filteredBase;
    return filtered;
  };

  const getQuickNavMeta = (block) => {
    const t = String(block?.title || "");
    const cleaned = cleanClauseTitle(t) || "Seção";

    const normalizeQuickNavLabel = (raw) => {
      const s = String(raw || "").trim();
      if (!s) return "";
      const hasLower = /[a-zà-ÿ]/.test(s);
      const hasUpper = /[A-ZÀ-Ÿ]/.test(s);
      const shouldTitleCase = hasUpper && !hasLower;
      if (!shouldTitleCase) return s;

      const words = s.toLowerCase().split(/\s+/g);
      const small = new Set([
        "de",
        "do",
        "da",
        "dos",
        "das",
        "e",
        "ou",
        "a",
        "o",
        "as",
        "os",
        "em",
        "no",
        "na",
        "nos",
        "nas",
        "por",
        "para",
        "com",
        "sem",
        "ao",
        "aos",
        "à",
        "às",
      ]);
      return words
        .map((w, idx) => {
          const clean = w.replace(/[^a-zà-ÿ0-9]+/gi, "");
          if (clean && clean.length <= 4 && /^[a-z]+$/i.test(clean)) {
            if (
              ["lgpd", "oab", "cpf", "cnpj", "stj", "stf"].includes(
                clean.toLowerCase(),
              )
            )
              return w.toUpperCase();
          }
          if (idx !== 0 && small.has(clean)) return w;
          return w.charAt(0).toUpperCase() + w.slice(1);
        })
        .join(" ");
    };

    const meta = buildQuickNavMeta(block, () => cleaned);
    return { ...meta, label: normalizeQuickNavLabel(meta.label) };
  };

  const scrollToBlock = (id) => {
    const el = document.getElementById(`block-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const scrollToId = (domId) => {
    const el = document.getElementById(domId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const getCurrentObjects = () =>
    form.ramoDireito === "ALL"
      ? Object.values(TAXONOMY).flatMap((t) => t.objects)
      : TAXONOMY[form.ramoDireito]?.objects || [];

  // --- VALIDAÇÃO DE BLOCOS (POLO ATIVO E PASSIVO) ---
  const isBlockVisible = (block) => {
    // 1. Validação Básica
    if (!block) return false;
    if (!block.condition) return true;

    // 2. Validação de Condição
    const cond = String(block.condition || "")
      .trim()
      .toUpperCase();
    const role = String(form?.posicaoCliente || "")
      .trim()
      .toUpperCase();
    const roleTokens = role
      .split("/")
      .map((t) => String(t || "").trim())
      .filter(Boolean);

    if (!cond || cond === "ALWAYS") return true;

    const POLO_ATIVO = [
      "AUTOR",
      "REQUERENTE",
      "RECLAMANTE",
      "VÍTIMA / ASSISTENTE DE ACUSAÇÃO",
      "QUERELANTE",
    ];
    const POLO_PASSIVO = [
      "RÉU",
      "REU",
      "REQUERIDO",
      "RECLAMADA",
      "INVESTIGADO",
      "INDICIADO",
      "RÉU / ACUSADO",
      "REU / ACUSADO",
      "QUERELADO",
    ];

    if (cond === "AUTOR_ONLY") {
      if (POLO_ATIVO.includes(role)) return true;
      return roleTokens.some((t) => POLO_ATIVO.includes(t));
    }

    if (cond === "REU_ONLY") {
      if (POLO_PASSIVO.includes(role)) return true;
      return roleTokens.some((t) => POLO_PASSIVO.includes(t));
    }

    return false;
  };

  // --- VALIDAÇÃO COMPLETA ---
  const validateBlock = (block) => {
    // 1. Validação Básica
    if (!block) return false;

    // 2. Validação de Visibilidade
    if (!isBlockVisible(block)) return false;

    // 3. Validação de Ramo
    const activeContexts = Array.isArray(block.tags)
      ? block.tags
      : ["Universal"];
    const currentRamo = form.isCustomRamo ? form.customRamo : form.ramoDireito;
    if (
      form.ramoDireito !== "ALL" &&
      !activeContexts.includes("Universal") &&
      !activeContexts.includes(currentRamo)
    ) {
      return false;
    }

    // 4. Validação de Objetos (OR vs AND)
    const reqObjects = Array.isArray(block.requiredObjects)
      ? block.requiredObjects
      : [];
    if (reqObjects.length > 0) {
      if (block.matchRule === "ALL") {
        if (!reqObjects.every((req) => form.selectedObjetos?.includes(req)))
          return false;
      } else {
        if (!reqObjects.some((req) => form.selectedObjetos?.includes(req)))
          return false;
      }
    }

    // 4.5. Validação de Honorários (NOVO - OR vs AND)
    const reqHonorarios = Array.isArray(block.requiredHonorarios)
      ? block.requiredHonorarios
      : [];
    if (reqHonorarios.length > 0) {
      if (block.matchRule === "ALL") {
        if (
          !reqHonorarios.every((req) => form.selectedHonorarios?.includes(req))
        )
          return false;
      } else {
        if (
          !reqHonorarios.some((req) => form.selectedHonorarios?.includes(req))
        )
          return false;
      }
    }

    // 4.6. Exclusão de Honorários — evita duplicidade quando uma combinação
    // mais específica (ex.: item "misto") já cobre o mesmo cenário que um
    // item individual cobriria sozinho. Só exclui quando TODOS os tokens
    // listados estiverem selecionados (mesma semântica ALL do item que os
    // exclui), preservando a exibição individual em combinações parciais
    // (ex.: Fixo + Quota Litis, que não tem item combinado próprio).
    const exclHonorarios = Array.isArray(block.excludedWhenAllSelected)
      ? block.excludedWhenAllSelected
      : [];
    if (
      exclHonorarios.length > 0 &&
      exclHonorarios.every((req) => form.selectedHonorarios?.includes(req))
    ) {
      return false;
    }

    // 5. Validação de Valor
    if (block.minValue && form.valorCausa < block.minValue) {
      return false;
    }

    // 6. Validação de Partes
    if (block.minParties && form.parties.length < block.minParties) {
      return false;
    }

    // Se passou por todas as validações
    return true;
  };

  function isChildVisibleInHierarchy(children, childIndex) {
    const arr = Array.isArray(children) ? children : [];
    if (childIndex < 0 || childIndex >= arr.length) return false;
    const current = arr[childIndex] || {};
    if (!validateBlock(current)) return false;
    let currentLevel = Math.max(0, Math.floor(Number(current?.level || 0)));
    if (currentLevel <= 0) return true;
    for (let idx = childIndex - 1; idx >= 0; idx -= 1) {
      const candidate = arr[idx] || {};
      const candidateLevel = Math.max(
        0,
        Math.floor(Number(candidate?.level || 0)),
      );
      if (candidateLevel >= currentLevel) continue;
      if (!validateBlock(candidate)) return false;
      currentLevel = candidateLevel;
      if (currentLevel <= 0) return true;
    }
    return true;
  }

  if (dataSource === "loading")
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );

  // --- DEBUG BADGE (SOMENTE LEITURA) ---
  const hasAnyChildren =
    Array.isArray(templateData?.structure) &&
    templateData.structure.some(
      (b) => Array.isArray(b.children) && b.children.length > 0,
    );
  const DebugBadge = () => (
    <div className="absolute top-2 right-2 bg-black/80 text-white text-[9px] px-2 py-1 rounded font-mono no-print z-50">
      <span title="Fonte de dados">{dataSource}</span>
      {hasAnyChildren && (
        <span className="ml-2 text-green-400" title="Template contém children">
          ★
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-[calc(100vh-64px)] w-full font-sans bg-gray-100 overflow-hidden relative print:h-auto print:overflow-visible print:bg-white">
      {/* REMOVIDO: ReferencePicker duplicado (mantido apenas RulesPanel) */}
      <aside className="w-full lg:w-[360px] bg-white border-r border-gray-200 shadow-xl z-20 flex flex-col flex-shrink-0 h-full no-print">
        <div
          className="no-print shrink-0 flex flex-col"
          style={{ background: "#20243B" }}
        >
          <div className="flex items-baseline gap-2 px-4 py-3">
            <span className="text-white font-bold text-sm tracking-wide">
              Motor de Contratos
            </span>
          </div>
          <div className="h-0.5 w-full" style={{ background: "#C5A059" }} />
          <div className="flex overflow-x-auto px-2 py-1.5 gap-0.5 border-b border-white/10">
            {[
              ["client", "Partes"],
              ["partner", "Parceiros"],
              ["object", "Objeto"],
              ["finance", "Honorários"],
            ].map(([id, label], i) => (
              <button
                key={id}
                type="button"
                onClick={() => setOpenSection(openSection === id ? "" : id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium whitespace-nowrap border-b-2 transition-colors ${openSection === id ? "border-[#C5A059] text-[#C5A059] bg-white/10" : "border-transparent text-white/50 hover:text-white/80"}`}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold flex-shrink-0"
                  style={{
                    background:
                      openSection === id ? "#C5A059" : "rgba(255,255,255,0.2)",
                    color: "white",
                  }}
                >
                  {i + 1}
                </span>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1">
              <button
                onClick={() => setMode("producao")}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === "producao" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                type="button"
              >
                DOCUMENTO
              </button>
              <button
                onClick={() => setMode("auditoria")}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === "auditoria" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                type="button"
              >
                ESTRUTURA
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-2.5 py-2.5 bg-gray-50/80">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <ConfigSection
              id="client"
              title="Dados do Cliente"
              icon="fa-user"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const firstId = form?.parties?.[0]?.id || null;
                    setImportTargetPartyId(firstId);
                    setShowImportModal(true);
                  }}
                  className="flex-1 bg-indigo-50 border border-indigo-200 text-indigo-700 py-1.5 rounded-lg text-[10px] font-semibold flex items-center justify-center gap-1.5 hover:bg-indigo-100 transition-colors shadow-sm"
                  type="button"
                >
                  <Download className="w-3 h-3" /> IMPORTAR DO CRM
                </button>
                <button
                  onClick={() => {
                    addParty();
                    setIsModified(true);
                  }}
                  className="bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                  type="button"
                  title="Adicionar Parte"
                >
                  <Plus className="w-3 h-3" />
                </button>
              </div>

              <div className="space-y-2.5">
                {(Array.isArray(form?.parties) ? form.parties : []).map(
                  (party, idx) => (
                    <div
                      key={party.id}
                      className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-[0.04em]">
                          {formatContractRoleLabel(party?.role)} {idx + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setImportTargetPartyId(party.id);
                              setShowImportModal(true);
                            }}
                            className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                            type="button"
                          >
                            Importar CRM
                          </button>
                          <button
                            onClick={() => {
                              removeParty(party.id);
                              setIsModified(true);
                            }}
                            className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                            type="button"
                            disabled={
                              (Array.isArray(form?.parties) ? form.parties : [])
                                .length <= 1
                            }
                            title="Remover Parte"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2.5">
                        <div className="space-y-1 col-span-2">
                          <label className={labelBaseStyle}>Nome</label>
                          <input
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={party.name}
                            readOnly
                            disabled
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelBaseStyle}>CPF/CNPJ</label>
                          <input
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={party.cpf}
                            readOnly
                            disabled
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>Papel</label>
                          <select
                            className={
                              inputBaseStyle +
                              " bg-gray-50 text-[10px] font-normal text-gray-500 cursor-not-allowed"
                            }
                            value={String(
                              party.role || "CONTRATANTE",
                            ).toUpperCase()}
                            disabled
                          >
                            {CONTRACT_ROLE_OPTIONS.map((roleOption) => (
                              <option key={roleOption} value={roleOption}>
                                {roleOption}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </ConfigSection>

            <ConfigSection
              id="partner"
              openSection={openSection}
              setOpenSection={setOpenSection}
              title="Co-patrocínio"
              icon="fa-user-tie"
            >
              <div className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    Advogado Parceiro Principal
                  </div>
                  <button
                    onClick={() => {
                      setImportTargetPartnerIndex(0);
                      setShowPartnerImportModal(true);
                    }}
                    className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                    type="button"
                  >
                    Importar CRM
                  </button>
                </div>
                <label className="flex items-center gap-2 text-[11px] font-semibold text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(partnerData.active)}
                    onChange={(e) => {
                      updatePartner("active", e.target.checked);
                      setIsModified(true);
                    }}
                  />
                  Habilitar parceiro principal
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1 col-span-2">
                    <label className={labelBaseStyle}>Nome</label>
                    <input
                      className={inputBaseStyle}
                      value={partnerData.name}
                      onChange={(e) => {
                        updatePartner("name", e.target.value);
                        setIsModified(true);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className={labelBaseStyle}>OAB</label>
                    <input
                      className={inputBaseStyle}
                      value={partnerData.oab}
                      onChange={(e) => {
                        updatePartner("oab", e.target.value);
                        setIsModified(true);
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className={labelBaseStyle}>CPF</label>
                    <input
                      className={inputBaseStyle}
                      value={partnerData.cpf}
                      onChange={(e) => {
                        updatePartner("cpf", e.target.value);
                        setIsModified(true);
                      }}
                    />
                  </div>
                  <div className="space-y-1 col-span-2">
                    <label className={labelBaseStyle}>
                      Endereço profissional
                    </label>
                    <input
                      className={inputBaseStyle}
                      value={partnerData.address}
                      onChange={(e) => {
                        updatePartner("address", e.target.value);
                        setIsModified(true);
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                {(Array.isArray(partnerExtras) ? partnerExtras : []).map(
                  (p, i) => (
                    <div
                      key={i}
                      className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                          Parceiro Extra {i + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setImportTargetPartnerIndex(i + 1);
                              setShowPartnerImportModal(true);
                            }}
                            className="text-[10px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 hover:bg-indigo-100"
                            type="button"
                          >
                            Importar CRM
                          </button>
                          <button
                            onClick={() => {
                              setPartnerExtras((prev) => {
                                const arr = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                arr.splice(i, 1);
                                return arr;
                              });
                              setIsModified(true);
                            }}
                            className="text-[10px] font-bold text-red-600 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-red-50"
                            type="button"
                            title="Remover Parceiro"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-2.5">
                        <div className="space-y-1 col-span-2">
                          <label className={labelBaseStyle}>Nome</label>
                          <input
                            className={inputBaseStyle}
                            value={p?.name || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPartnerExtras((prev) => {
                                const arr = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                arr[i] = { ...(arr[i] || {}), name: v };
                                return arr;
                              });
                              setIsModified(true);
                            }}
                          />
                        </div>

                        <div className="space-y-1">
                          <label className={labelBaseStyle}>OAB</label>
                          <input
                            className={inputBaseStyle}
                            value={p?.oab || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPartnerExtras((prev) => {
                                const arr = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                arr[i] = { ...(arr[i] || {}), oab: v };
                                return arr;
                              });
                              setIsModified(true);
                            }}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelBaseStyle}>CPF</label>
                          <input
                            className={inputBaseStyle}
                            value={p?.cpf || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPartnerExtras((prev) => {
                                const arr = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                arr[i] = { ...(arr[i] || {}), cpf: v };
                                return arr;
                              });
                              setIsModified(true);
                            }}
                          />
                        </div>

                        <div className="space-y-1 col-span-2">
                          <label className={labelBaseStyle}>
                            Endereço profissional
                          </label>
                          <input
                            className={inputBaseStyle}
                            value={p?.address || ""}
                            onChange={(e) => {
                              const v = e.target.value;
                              setPartnerExtras((prev) => {
                                const arr = Array.isArray(prev)
                                  ? [...prev]
                                  : [];
                                arr[i] = { ...(arr[i] || {}), address: v };
                                return arr;
                              });
                              setIsModified(true);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>

              <button
                onClick={() => {
                  setPartnerData((p) => ({ ...p, active: true }));
                  setPartnerExtras((prev) => [
                    ...(Array.isArray(prev) ? prev : []),
                    { ...DEFAULT_PARTNER },
                  ]);
                  setIsModified(true);
                }}
                className="w-full bg-white border border-gray-200 text-gray-700 py-1.5 px-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5 hover:bg-gray-50 transition-colors shadow-sm"
                type="button"
              >
                <Plus className="w-3 h-3" /> Adicionar Parceiro Extra
              </button>
            </ConfigSection>

            <ConfigSection
              id="object"
              openSection={openSection}
              setOpenSection={setOpenSection}
              title="Objeto Jurídico"
              icon="fa-balance-scale"
            >
              <div className="space-y-1">
                <label className={labelBaseStyle}>Ramo</label>
                <select
                  className={inputBaseStyle}
                  value={form.isCustomRamo ? "CUSTOM" : form.ramoDireito}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((p) => ({
                      ...p,
                      isCustomRamo: val === "CUSTOM",
                      ramoDireito: val === "CUSTOM" ? p.ramoDireito : val,
                      selectedObjetos: [],
                    }));
                  }}
                >
                  <option value="ALL">TODAS AS ÁREAS (Master View)</option>
                  {Object.entries(TAXONOMY).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v.label}
                    </option>
                  ))}
                  <option value="CUSTOM">Outro...</option>
                </select>
              </div>

              {form.isCustomRamo && (
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Nome do Ramo</label>
                  <input
                    className={inputBaseStyle}
                    value={form.customRamo}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, customRamo: e.target.value }))
                    }
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className={labelBaseStyle}>Posição</label>
                <select
                  className={inputBaseStyle}
                  value={form.isCustomPosicao ? "CUSTOM" : form.posicaoCliente}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((p) => ({
                      ...p,
                      isCustomPosicao: val === "CUSTOM",
                      posicaoCliente: val === "CUSTOM" ? p.posicaoCliente : val,
                    }));
                  }}
                >
                  {(() => {
                    const ramoAtual = form.isCustomRamo
                      ? "ALL"
                      : form.ramoDireito;
                    const opcoes =
                      POSICOES_POR_RAMO[ramoAtual] || POSICOES_POR_RAMO["ALL"];
                    return opcoes.map((pos) => (
                      <option key={pos} value={pos}>
                        {pos}
                      </option>
                    ));
                  })()}
                  <option value="CUSTOM">Outra...</option>
                </select>
              </div>

              {form.isCustomPosicao && (
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Posição Custom</label>
                  <input
                    className={inputBaseStyle}
                    value={form.customPosicao}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, customPosicao: e.target.value }))
                    }
                    placeholder="Ex: Litisdenunciado"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className={labelBaseStyle}>Serviços Selecionados</label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white p-2 space-y-1">
                  {getCurrentObjects().map((obj) => (
                    <label
                      key={obj.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={form.selectedObjetos.includes(obj.id)}
                        onChange={() => toggleList("selectedObjetos", obj.id)}
                        className="rounded text-brand-blue focus:ring-0"
                      />
                      <span className="text-xs text-gray-600 leading-tight">
                        {obj.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className={labelBaseStyle}>
                  Objeto (descrição livre)
                </label>
                <textarea
                  className={inputBaseStyle + " resize-none"}
                  rows={3}
                  value={form.objetoPrincipal}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, objetoPrincipal: e.target.value }))
                  }
                />
              </div>

              <div className="space-y-1">
                <label className={labelBaseStyle}>Parte Contrária</label>
                <input
                  className={inputBaseStyle}
                  type="text"
                  placeholder="Nome da parte adversa..."
                  value={form.parteContrariaName}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      parteContrariaName: e.target.value,
                    }))
                  }
                />
              </div>
            </ConfigSection>

            <ConfigSection
              id="finance"
              openSection={openSection}
              setOpenSection={setOpenSection}
              title="Honorários & Valores"
              icon="fa-wallet"
            >
              <div className="space-y-1 mb-4">
                <label className={labelBaseStyle}>
                  Modalidade de Honorários
                </label>
                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg bg-white p-2 space-y-1">
                  {HONORARIOS_OPTIONS.map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={form.selectedHonorarios?.includes(opt.id)}
                        onChange={() =>
                          toggleList("selectedHonorarios", opt.id)
                        }
                        className="rounded text-brand-blue focus:ring-0"
                      />
                      <span className="text-xs text-gray-600 leading-tight">
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Causa (R$)</label>
                  <input
                    className={inputBaseStyle}
                    type="number"
                    value={form.valorCausa}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, valorCausa: e.target.value }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className={labelBaseStyle}>Êxito (%)</label>
                  <input
                    className={inputBaseStyle}
                    type="number"
                    value={form.percentualExito}
                    onChange={(e) =>
                      setForm((p) => ({
                        ...p,
                        percentualExito: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              {suggestedOABTotal > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 my-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-brand-blue">
                    <i className="fas fa-balance-scale"></i>
                    <span className="text-[10px] font-bold uppercase tracking-wider">
                      Piso OAB (Sugerido)
                    </span>
                  </div>
                  <span className="font-bold text-blue-800">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(suggestedOABTotal)}
                  </span>
                </div>
              )}

              <div className="space-y-1">
                <label className={labelBaseStyle}>Honorários Iniciais</label>
                <input
                  className={inputBaseStyle + " font-bold"}
                  type="number"
                  value={form.valorHonorarios}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, valorHonorarios: e.target.value }))
                  }
                />
              </div>

              <div className="grid grid-cols-1 gap-2 pt-2">
                {Number(form.valorHonorarios || 0) > 0 && (
                  <button
                    onClick={() => setShowProvisionModal(true)}
                    className="w-full px-2.5 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl text-[12px] font-bold shadow-md transition-colors flex items-center justify-center gap-1.5 whitespace-nowrap leading-none"
                    type="button"
                  >
                    <Coins className="w-3 h-3" /> FATURAR
                  </button>
                )}
              </div>
              <div className="mt-6 border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold mb-4 flex items-center gap-2">
                  <span>💰</span> Faturamento
                </h3>
                <BillingGenerator
                  client={
                    crmClientsById?.[
                      String(form.parties?.[0]?.clientId || "").trim()
                    ] ||
                    form.parties?.[0] ||
                    null
                  }
                />
              </div>
            </ConfigSection>

            <ConfigSection
              id="vincular"
              title="Vincular a Processo"
              icon="fa-link"
              openSection={openSection}
              setOpenSection={setOpenSection}
            >
              {form?.parties?.[0]?.clientId ||
              form?.parties?.[0]?.qualificationData?.id ? (
                <div className="space-y-1">
                  <CadernoSelector
                    clientId={String(
                      form?.parties?.[0]?.clientId ||
                        form?.parties?.[0]?.qualificationData?.id ||
                        "",
                    ).trim()}
                    clientName={String(
                      form?.parties?.[0]?.name ||
                        form?.parties?.[0]?.qualificationData?.name ||
                        "",
                    ).trim()}
                    value={preFlightState.cadernoId}
                    onChange={(id, title) =>
                      setPreFlightState((prev) => ({
                        ...prev,
                        cadernoId: id,
                        cadernoTitle: title,
                      }))
                    }
                  />
                  <p className="text-[9px] text-gray-400 mt-0.5">
                    O contrato será vinculado ao caderno selecionado e aparecerá
                    no portal do cliente.
                  </p>
                </div>
              ) : (
                <p className="text-[10px] text-gray-400 italic">
                  Importe um cliente do CRM para vincular a um processo.
                </p>
              )}
            </ConfigSection>
          </div>
        </div>

        <div className="relative z-[90] p-3 border-t border-gray-200 bg-white shrink-0 space-y-2">
          <div className="grid grid-cols-1 gap-2 no-print">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                onClick={() => openPreFlightForContract("simple", "html")}
                className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-violet-200 bg-white text-violet-700 hover:bg-violet-50 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title="Salva o contrato em HTML no prontuário interno para edição futura (não envia ao cliente)"
                type="button"
                aria-disabled={isPublishing}
              >
                <i className="fas fa-save"></i>{" "}
                {isPublishing ? "Salvando..." : "Salvar HTML"}
              </button>

              <button
                onClick={() => openPreFlightForContract("simple", "pdf")}
                className="px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                title="Publica o contrato em PDF no acervo do cliente e registra no prontuário"
                type="button"
                aria-disabled={isPublishing}
              >
                <i className="fas fa-file-pdf"></i>{" "}
                {isPublishing ? "Publicando..." : "Publicar PDF"}
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-stretch no-print">
              <div className="relative z-[110]">
                <button
                  ref={advancedMenuAnchorRef}
                  onClick={() => setShowAdvancedActions((prev) => !prev)}
                  className="w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap border border-gray-200 bg-gray-50 text-gray-700 hover:bg-gray-100 transition-colors flex items-center justify-center gap-1.5 leading-none shadow-sm"
                  type="button"
                  aria-expanded={showAdvancedActions}
                >
                  <i className="fas fa-cog"></i> Avançado
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${showAdvancedActions ? "rotate-180" : ""}`}
                  />
                </button>

                {showAdvancedActions
                  ? createPortal(
                      <div
                        ref={advancedMenuRef}
                        className="fixed w-64 bg-white border border-gray-200 shadow-2xl rounded-lg overflow-hidden z-[1200] flex flex-col"
                        style={{
                          top: advancedMenuPosition.top,
                          left: advancedMenuPosition.left,
                          transform: "translateY(-100%)",
                        }}
                      >
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleResetDraft();
                          }}
                          className={`text-left px-4 py-3 text-xs font-bold w-full transition-colors ${isModified ? "text-orange-700 bg-orange-50 hover:bg-orange-100" : "text-gray-700 hover:bg-gray-50"}`}
                          title="Limpar Draft (com confirmação)"
                          type="button"
                        >
                          Limpar Draft
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleDownloadBackup();
                          }}
                          className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                          title="Baixar uma cópia de segurança do contrato para o seu computador"
                          type="button"
                        >
                          Backup Seguro (JSON)
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleRestoreBackup();
                          }}
                          className="text-left px-4 py-3 hover:bg-amber-50 text-xs font-bold text-amber-700 w-full transition-colors"
                          title="Restaurar contrato a partir de um arquivo JSON salvo"
                          type="button"
                        >
                          Restaurar Backup
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleRelinkFromCrm();
                          }}
                          className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                          title="Recarrega do CRM os vínculos atuais e sobrescreve snapshots antigos do contrato"
                          type="button"
                        >
                          Revincular do CRM
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            void handleForceSyncCloud();
                          }}
                          className="text-left px-4 py-3 hover:bg-green-50 text-xs font-bold text-green-700 w-full transition-colors border-b border-green-100"
                          title="Limpa o cache local e força o download do template atualizado da nuvem"
                          type="button"
                        >
                          ☁️ Sincronizar com a Nuvem
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            void handleDownloadCloudMaster();
                          }}
                          className="text-left px-4 py-3 hover:bg-sky-50 text-xs font-bold text-sky-700 w-full transition-colors"
                          title="Baixar o mestre atualmente salvo na nuvem como JSON, sem publicar nada"
                          type="button"
                        >
                          Baixar Mestre da Nuvem
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleSaveAsMaster();
                          }}
                          className="text-left px-4 py-3 hover:bg-rose-50 text-xs font-bold text-rose-700 w-full transition-colors"
                          title="Sobrescreve o banco de dados com a versão atual da tela"
                          type="button"
                          aria-disabled={isSaving}
                        >
                          Publicar Mestre
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleImportMD();
                          }}
                          className="text-left px-4 py-3 hover:bg-emerald-50 text-xs font-bold text-emerald-700 w-full transition-colors"
                          type="button"
                        >
                          Upload da Revisão (.MD)
                        </button>
                        <button
                          onClick={() => {
                            setShowAdvancedActions(false);
                            handleExportMD();
                          }}
                          className="text-left px-4 py-3 hover:bg-indigo-50 text-xs font-bold text-indigo-700 w-full transition-colors"
                          type="button"
                        >
                          Baixar para Revisão (.MD)
                        </button>
                      </div>,
                      document.body,
                    )
                  : null}
              </div>

              <button
                onClick={() => {
                  const previousTitle = document.title;
                  const nextTitle = buildFriendlyContractName();
                  const restoreTitle = () => {
                    document.title = previousTitle;
                    window.removeEventListener("afterprint", restoreTitle);
                  };

                  document.title = nextTitle;
                  browserEnv.addEventListener("afterprint", restoreTitle);
                  window.print();
                  window.setTimeout(restoreTitle, 1000);
                }}
                disabled={hydratingDeepClients}
                className={`w-full px-2.5 py-1.5 rounded-lg font-semibold text-[11px] whitespace-nowrap shadow-sm flex justify-center gap-1.5 transition-colors items-center no-print border leading-none ${hydratingDeepClients ? "bg-gray-200 text-gray-500 border-gray-300 cursor-not-allowed" : "bg-[#20243B] text-white border-[#20243B] hover:brightness-95"}`}
                title={
                  hydratingDeepClients
                    ? "Carregando dados completos do cliente (Tutor/Representante)..."
                    : undefined
                }
                type="button"
              >
                <i className="fas fa-print"></i>
                PDF / Imprimir
              </button>
            </div>

            <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.04em] text-gray-500 border-t border-gray-100 pt-1.5">
              <span>Sincronização automática</span>
              <span
                className={`px-2 py-1 rounded-full border whitespace-nowrap ${cloudSyncState === "saving" ? "border-sky-200 bg-sky-50 text-sky-700" : cloudSyncState === "saved" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : cloudSyncState === "error" ? "border-red-200 bg-red-50 text-red-700" : "border-gray-200 bg-gray-50 text-gray-600"}`}
              >
                {cloudSyncState === "saving"
                  ? "Salvando..."
                  : cloudSyncState === "saved"
                    ? `Nuvem OK${cloudSyncMeta?.updatedAt ? ` · ${new Date(cloudSyncMeta.updatedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : ""}`
                    : cloudSyncState === "error"
                      ? "Falha no auto-save"
                      : "Aguardando alterações"}
              </span>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex-1 overflow-auto relative p-4 md:p-8 bg-white min-w-0 min-h-0 print:p-0 print:overflow-visible print:bg-white">
        <div className="contract-print-area">
          <div className="screen-only-view p-8 bg-gray-100 flex justify-center">
            <div className="screen-paper">
              <div className="text-center mb-8 relative">
                <DebugBadge />
                <h1 className="contract-title">
                  CONTRATO DE PRESTAÇÃO DE
                  <br />
                  SERVIÇOS ADVOCATÍCIOS E HONORÁRIOS
                </h1>
              </div>

              <div className="document-content space-y-6">
                <div id="guia-rapido" className="quadro-resumo-section mb-8">
                  <h2 className="text-center font-bold text-sm mb-4 uppercase relative">
                    <span
                      className="ghost-marker"
                      style={{ top: "-15px", left: 0 }}
                      aria-hidden="true"
                    >
                      RDA_TGT_GUIARAPIDO
                    </span>
                    Guia Rápido do Documento
                  </h2>
                  <div className="quadro-resumo-grid">
                    {getQuickNavBlocks().map((b, idx) => {
                      const aid = getAnchorIdForBlock(b);
                      if (!aid) return null;
                      const meta = getQuickNavMeta(b);
                      const safeId = String(b?.id || "").replace(
                        /[^a-zA-Z0-9_\-]/g,
                        "_",
                      );
                      return (
                        <a
                          key={b.id}
                          href={`#block-${b.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            scrollToBlock(b.id);
                          }}
                          className="quadro-resumo-card group relative"
                        >
                          <span
                            className="ghost-marker"
                            style={{ top: 0, left: 0 }}
                            aria-hidden="true"
                          >{`RDA_TL_BTN_${safeId}`}</span>
                          <span
                            className="ghost-marker"
                            style={{ bottom: 0, right: 0 }}
                            aria-hidden="true"
                          >{`RDA_BR_BTN_${safeId}`}</span>
                          <span
                            className="quadro-resumo-icon"
                            aria-hidden="true"
                          >
                            <i className={meta.icon} aria-hidden="true" />
                          </span>
                          <div className="quadro-resumo-content flex-1">
                            <h3 className="quadro-resumo-title-group">
                              <span className="quadro-resumo-clause-number">
                                Cláusula {idx + 1}ª
                              </span>
                              <span
                                className="quadro-resumo-label"
                                title={meta.label}
                              >
                                {meta.label}
                              </span>
                            </h3>
                          </div>
                          {meta.badge ? (
                            <span className="quadro-resumo-badge">
                              {meta.badge}
                            </span>
                          ) : null}
                        </a>
                      );
                    })}
                  </div>
                </div>

                {renderPreamble()}

                {(() => {
                  let clauseNumber = 0;

                  return templateData?.structure?.map((block) => {
                    const isMatch = validateBlock(block);
                    if (mode === "producao" && !isMatch) return null;

                    const isPreambulo =
                      block?.id === "preambulo" ||
                      String(block?.title || "")
                        .toUpperCase()
                        .includes("PREÂMBULO");
                    if (isPreambulo) return null;
                    if (String(block?.id || "").startsWith("cl_01_"))
                      return null;
                    if (isOptionLikeBlock(block)) return null;

                    clauseNumber += 1;

                    return (
                      <div
                        key={block.id}
                        className="clause-block mb-2"
                        style={{
                          "--indent-level": `${(block.level || 0) * 24}px`,
                          borderLeft: "none",
                          paddingLeft: block.level ? "1rem" : "0.5rem",
                        }}
                      >
                        {renderBlock(block, clauseNumber)}
                      </div>
                    );
                  });
                })()}

                <div className="mt-12 mb-8 signature-spacer-top avoid-page-break-inside">
                  <p className="text-justify mb-4">
                    E, por estarem assim justos e perfeitamente acordados, as
                    partes assinam eletronicamente o presente instrumento, com a
                    certificação de integridade, autoria e data, para que
                    produza todos os seus jurídicos e legais efeitos.
                  </p>
                  {renderContractExecutionClosing()}
                  {renderContractSignatures()}
                </div>
                <div
                  id="contrato-legal-pronto"
                  data-status="ready"
                  style={{ display: "none" }}
                ></div>
              </div>
            </div>
          </div>

          <div className="print-only-view">
            <div className="hybrid-visual-header-fixed">
              <DocumentHeader />
            </div>
            <div className="hybrid-visual-footer-fixed">
              <DocumentFooter />
            </div>

            <table className="print-layout-table">
              <thead className="hybrid-table-header-group">
                <tr>
                  <td>
                    <div className="spacer-header-ghost">&nbsp;</div>
                  </td>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td>
                    <div className="document-content pt-4 pb-4">
                      <div className="text-center mb-8">
                        <h1 className="contract-title">
                          CONTRATO DE PRESTAÇÃO DE
                          <br />
                          SERVIÇOS ADVOCATÍCIOS E HONORÁRIOS
                        </h1>
                      </div>

                      <div
                        id="guia-rapido-print"
                        className="quadro-resumo-section mb-8"
                      >
                        <h2 className="text-center font-bold text-sm mb-4 uppercase">
                          Guia Rápido do Documento
                        </h2>
                        <div className="quadro-resumo-grid">
                          {getQuickNavBlocks().map((b, idx) => {
                            const aid = getAnchorIdForBlock(b);
                            if (!aid) return null;
                            const meta = getQuickNavMeta(b);
                            const safeId = String(b?.id || "").replace(
                              /[^a-zA-Z0-9_\-]/g,
                              "_",
                            );
                            return (
                              <a
                                key={b.id}
                                href={`#${aid}`}
                                className="quadro-resumo-card group relative"
                              >
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_TGT_TOP_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_TL_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ bottom: 0, right: 0 }}
                                >
                                  {`RDA_BR_${safeId}`}
                                </span>
                                <span
                                  className="ghost-marker"
                                  style={{ top: 0, left: 0 }}
                                >
                                  {`RDA_BTN_${safeId}`}
                                </span>
                                <span
                                  className="quadro-resumo-icon"
                                  aria-hidden="true"
                                >
                                  <i className={meta.icon} aria-hidden="true" />
                                </span>
                                <div className="quadro-resumo-content">
                                  <h3 className="quadro-resumo-title-group">
                                    <span className="quadro-resumo-clause-number">
                                      Cláusula {idx + 1}ª
                                    </span>
                                    <span
                                      className="quadro-resumo-label"
                                      title={meta.label}
                                    >
                                      {meta.label}
                                    </span>
                                  </h3>
                                </div>
                                {meta.badge ? (
                                  <span className="quadro-resumo-badge">
                                    {meta.badge}
                                  </span>
                                ) : null}
                              </a>
                            );
                          })}
                        </div>
                      </div>

                      {renderPreamble()}

                      {(() => {
                        let clauseNumber = 0;

                        return templateData?.structure?.map((block) => {
                          const isMatch = validateBlock(block);
                          if (mode === "producao" && !isMatch) return null;

                          const isPreambulo =
                            block?.id === "preambulo" ||
                            String(block?.title || "")
                              .toUpperCase()
                              .includes("PREÂMBULO");
                          if (isPreambulo) return null;
                          if (String(block?.id || "").startsWith("cl_01_"))
                            return null;
                          if (isOptionLikeBlock(block)) return null;

                          clauseNumber += 1;

                          return (
                            <div
                              key={block.id}
                              id={`block-${block.id}`}
                              className="clause-block mb-2"
                              style={{
                                "--indent-level": `${(block.level || 0) * 24}px`,
                                borderLeft: "none",
                                paddingLeft: block.level ? "1rem" : "0.5rem",
                              }}
                            >
                              {renderBlock(block, clauseNumber)}
                            </div>
                          );
                        });
                      })()}

                      <div className="mt-12 mb-8 signature-spacer-top avoid-page-break-inside">
                        <p className="text-justify mb-4">
                          E, por estarem assim justos e perfeitamente acordados,
                          as partes assinam eletronicamente o presente
                          instrumento, com a certificação de integridade,
                          autoria e data, para que produza todos os seus
                          jurídicos e legais efeitos.
                        </p>
                        {renderContractExecutionClosing()}
                        {renderContractSignatures()}
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot className="hybrid-table-footer-group">
                <tr>
                  <td>
                    <div className="spacer-footer-ghost">&nbsp;</div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      <DocumentMap
        structure={templateData?.structure || []}
        activeId={editingBlockId}
        onScrollTo={scrollToBlock}
        onReorder={handleReorder}
        onDelete={deleteClause}
        mode={mode}
        isVisible={validateBlock}
      />

      {showImportModal && (
        <ClientImportModal
          onClose={() => setShowImportModal(false)}
          onSelect={(picked) => handleClientImport(picked, importTargetPartyId)}
        />
      )}

      {showPartnerImportModal && (
        <PartnerImportModal
          onClose={() => setShowPartnerImportModal(false)}
          onSelect={(picked) =>
            handlePartnerImport(picked, importTargetPartnerIndex)
          }
        />
      )}

      {showProvisionModal && (
        <ProvisionModal
          onClose={() => setShowProvisionModal(false)}
          onConfirm={handleProvisioning}
          initialValue={form.valorHonorarios}
          clientName={form.parties?.[0]?.name}
        />
      )}
      {showDeliveryModal && (
        <DeliveryModal
          onClose={() => setShowDeliveryModal(false)}
          clientName={form.parties?.[0]?.name}
          clientPhone={form.parties?.[0]?.phone}
          contractName={`Contrato de Honorários - ${form.parties?.[0]?.name ? toTitleCasePt(form.parties[0].name) : "Minuta"}`}
        />
      )}

      <PreFlightCheckModal
        isOpen={preFlightState.open}
        onClose={closePreFlightForContract}
        onConfirm={confirmPreFlightForContract}
        documentType="contract"
        data={form}
        publishIntent={preFlightState.publishType}
        extraContent={
          <CadernoSelector
            clientId={String(
              form?.parties?.[0]?.clientId ||
                form?.parties?.[0]?.qualificationData?.id ||
                "",
            ).trim()}
            clientName={String(
              form?.parties?.[0]?.name ||
                form?.parties?.[0]?.qualificationData?.name ||
                "",
            ).trim()}
            value={preFlightState.cadernoId}
            onChange={(id, title) =>
              setPreFlightState((prev) => ({
                ...prev,
                cadernoId: id,
                cadernoTitle: title,
              }))
            }
          />
        }
      />

      {dialogState.open ? (
        <div className="fixed inset-0 z-[700] flex items-center justify-center p-4 no-print">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              const fn = dialogState.onCancel;
              setDialogState({
                open: false,
                title: "",
                message: "",
                mode: "confirm",
                onConfirm: null,
                onCancel: null,
              });
              if (typeof fn === "function") fn();
            }}
          />
          <div className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-gray-200 p-5">
            <div className="text-sm font-bold text-[#20243B]">
              {dialogState.title || "Confirmar"}
            </div>
            <div className="text-xs text-gray-600 mt-2 whitespace-pre-wrap">
              {dialogState.message}
            </div>
            <div className="mt-5">
              {dialogState.mode === "confirm" ? (
                <SaveCancelBar
                  onCancel={() => {
                    const fn = dialogState.onCancel;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  onSave={() => {
                    const fn = dialogState.onConfirm;
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    });
                    if (typeof fn === "function") fn();
                  }}
                  cancelLabel="Cancelar"
                  saveLabel="Confirmar"
                />
              ) : (
                <SaveCancelBar
                  onCancel={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  onSave={() =>
                    setDialogState({
                      open: false,
                      title: "",
                      message: "",
                      mode: "confirm",
                      onConfirm: null,
                      onCancel: null,
                    })
                  }
                  cancelLabel=""
                  saveLabel="OK"
                  disabledCancel={true}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
